/**
 * Post-processing: contact occlusion, restrained bloom, velocity motion blur, filmic
 * rolloff and the colour grade.
 *
 * OWNER: piece:postfx
 *
 * ## Why hand-rolled passes and not EffectComposer
 *
 * `EffectComposer` + `RenderPass` + N `ShaderPass`es means N *full-resolution* blits
 * ping-ponging between two RGBA16F targets. At 1080p one blit is ~8 MB of write plus
 * ~8 MB of read; five of them is 80 MB of bandwidth per frame before a single useful
 * texture fetch, which on integrated graphics is most of a 2 ms budget spent on moving
 * pixels rather than on looking at them. The addon passes also each own their own
 * target: `UnrealBloomPass` alone allocates five mip pairs and runs eleven draws,
 * `GTAOPass`/`SSAOPass` run at full resolution and re-render the scene for normals.
 *
 * So the chain here is:
 *
 *   1. scene -> HDR target (RGBA16F, MSAA, depth texture attached)
 *   2. contact occlusion at **half** res, from depth alone (no normal prepass, no second
 *      scene draw)
 *   3. wide occlusion at **quarter** res, and its own wider denoise
 *   4. contact denoise at half res, which also lifts the wide term onto its grid
 *   5. bright pass at quarter res, then two separable blurs at 1/4 and two at 1/8
 *   6. **one** full-resolution pass that does everything else at once
 *
 * Everything display-facing — motion blur, AO application, bloom add, exposure, tone
 * curve, grade, vignette, chromatic fringing, dither, sRGB — happens in that single
 * composite. There is exactly one full-res read/write in the whole chain besides the
 * scene draw itself, which is what makes the budget reachable.
 *
 * ## Contact occlusion: why the resolve is the whole problem
 *
 * Round 2 shipped an occlusion buffer that was arithmetically fine and visually absent:
 * mid-field trunks met the dirt with no darkening at all, and a boulder at 3 m got a
 * half-metre diffuse smudge instead of a line. Measured on the raw buffer, the darkest
 * 5% of the pixels around that boulder sat at 0.71 — a 16% darkening spread over 80 px,
 * which is grime, not contact. Three things did that, and all three are in the *resolve*
 * rather than in the estimate:
 *
 *   1. **The denoise diluted by 7.6x.** Nine taps with a total weight of 7.6 against a
 *      centre weight of 1 turns a two-texel black band into a twelve-texel grey one with
 *      an eighth of the contrast. Its bilateral term compared depths in *absolute metres*
 *      (`exp(-|dz| * 3)`), so at 20 m a trunk 0.3 m in front of the ground still got 40%
 *      weight — it averaged straight across the silhouette it was supposed to protect.
 *   2. **The upsample was hardware bilinear.** Half-res AO stretched over full-res depth
 *      with no depth test bleeds occlusion computed on the trunk onto the ground and vice
 *      versa, and adds another two pixels of blur to a feature that was already too soft.
 *   3. **The estimator was unnormalised under its own screen-space clamp.** The sample
 *      ring is clamped to `aoMaxRadiusPx`, which near the camera cuts the world radius
 *      from 1.0 m to about 0.3 m — but the falloff and the intensity normalisation still
 *      used 1.0 m, so two thirds of the disc's contribution was simply missing exactly
 *      where the camera is closest and contact matters most.
 *
 * So the occlusion here is rebuilt around the resolve:
 *
 *   - **Two rings, not one, and not the same estimator.** The **contact ring**
 *     (`aoContactRadius`) is a short horizon march — a few directions, a few steps each,
 *     keep the highest horizon — because near a rock's base only about a quarter of the
 *     directions are blocked, and a scattered-point estimate of that swings between
 *     nothing and everything depending on where the taps fell. Measured, the point
 *     version reached 0.51 occlusion raw and delivered 0.32 after the resolve; the
 *     horizon version reaches 0.97 raw and delivers 0.87, on the same tap budget, because
 *     samples along a ray are correlated and a denoise cannot tell a one-texel spike from
 *     noise. The two terms travel in R and G of one 8-bit buffer, so the second costs
 *     taps and no bandwidth.
 *   - **Radius from the clamp, not from the tuning.** Both rings derive their falloff
 *     radius and their normalisation from the screen radius they actually sampled, so the
 *     estimate is unbiased whether or not the clamp bit.
 *   - **Stratified sampling with a finer hash.** Interleaved gradient noise rotates the
 *     spiral and the march; a 4x4 ordered pattern, decorrelated from it, jitters each tap
 *     *within its own annulus* (and each march *within its own step*). Sixteen pixels
 *     therefore cover sixteen distinct (angle, radius) strata, so a 3x3 denoise sees ~9N
 *     effective samples instead of N. That is what pays for the denoise being small
 *     enough to keep a contact line.
 *   - **A relative-depth 3x3 denoise** with the contact channel weighted hard toward its
 *     own centre, replacing the 5x5-ish absolute-metre one.
 *   - **A joint bilateral upsample** in the composite. Each AO texel carries its own view
 *     depth packed into B/A (sqrt-encoded, ~4 mm resolution at 5 m), so the full-res pixel
 *     can weight the four candidates against its own depth for four fetches and no extra
 *     depth sampling. Where the four disagree the upsample collapses to nearest, which is
 *     precisely the behaviour that keeps occlusion on the correct side of a silhouette.
 *   - **A resolvability guard.** Occlusion fades out where the depth gradient says the
 *     surface is not resolvable at half res — foliage seen edge-on. Without it a stronger
 *     AO turns a fir canopy into speckle.
 *
 * ## Wide occlusion: why half the model was doing nothing
 *
 * The wave-3 review measured the wide ring at a **median 0.4% darkening, exceeding 15% on
 * 1.9% of ground pixels**, and called it what it was: with lighting doing the heavy
 * lifting under this direction, half the occlusion model was inert. Two independent
 * causes, and neither of them was the estimator's arithmetic:
 *
 *   1. **It was never wide.** A 1 m world radius clamped to 34 half-res px is 0.26 m at
 *      3 m from the camera — tighter than the contact ring it was meant to complement.
 *      Nothing it exists to find is that size: a tree well is 2–4 m, a bench cut's crease
 *      is a 3–6 m concavity.
 *   2. **Two relief masks were calibrated against numbers this world does not produce.**
 *      Measured off the scene buffer (DEBUG_VIEW 6, added for this), the forest floor
 *      reads 0.020 median scene luminance and sunlit dirt 0.067–0.18. `aoDarkRelief` was
 *      ramping over [0.004, 0.022] — sitting on top of the forest floor's own
 *      distribution, holding its darkest fifth at 17% of strength — and the sunlit relief
 *      ramped over [0.55, 2.2], which nothing in the game ever enters.
 *
 * So the wide ring is now its own pass at **quarter** resolution, marching horizons over
 * 6 m with the near 14% of its radius handed to the contact ring so the two do not count
 * the same occluder twice. See `AO_WIDE_FRAG`. Measured on the same forest frame after:
 * **median 9.8%, above 15% on 34% of ground pixels** — against 0.4% and 1.9% before. On
 * the open jump line, where a wide term *should* mostly find nothing and a wrong one
 * would read as grime, median 1.6% and above 15% on 5.7%: the troughs between lips, the
 * base of every berm and the treeline get it, and flat dirt does not.
 *
 * What it still cannot do is sky occlusion under a closed canopy: the canopy over a
 * ground pixel is 15–25 m up and far outside any screen-space march that pixel can
 * afford. That belongs to whatever lights the scene, not to a depth buffer.
 *
 * ## Why a correct occlusion buffer still produced no picture — the ordering
 *
 * The round above rebuilt the estimate and shipped it, and the buffer was genuinely
 * right: `probeAo` on a forest frame read a median 9.8% wide occlusion, p90 26.7%,
 * p99 44.7%. On the screen the same A/B, restricted to static ground with no foliage in
 * it, moved the image by a **mean 1.2 and p90 2.2 code values against a region mean of
 * 23**. Half the occlusion model was still, in the only sense that matters, inert — and
 * for a reason that has nothing to do with canopy reach, which is where the previous round
 * sent the blame.
 *
 * The occlusion multiply ran *before* the display transform's additive terms. Straight
 * after it, `PostTuning.flare` added a flat 0.022 x luma([0.42, 0.52, 0.76]) = 0.0114 of
 * scene luminance to every pixel in the frame. The forest floor's own scene luminance is
 * 0.020 (DEBUG_VIEW 6). So the veil was 57% of the surface it sat on, and a multiply of
 * 0.83 followed by an add of 0.0114 is a multiply of 0.89 — **36% of every occlusion the
 * wide ring computed was refilled in the next instruction.** The ACES toe and the sRGB
 * encode then compressed the remainder into a part of the transfer function where the
 * forest interior lives inside display codes 9–30, and 11% of a linear value that small is
 * one or two codes.
 *
 * Three things follow, and all three are in this file:
 *
 *   1. **Occlusion is applied after the veil, not before it.** It is also the physically
 *      coherent order: the veil stands in for the sky's contribution to the black point,
 *      occlusion *is* sky visibility, and a pixel that cannot see the sky should not be
 *      handed the sky's veil. Same instruction count, and the p90 darkening lands on the
 *      image roughly 1.6x larger before anything else changes.
 *   2. **The wide ring gets a shaping transfer** in front of its gain, so flat ground can
 *      stay clean while a hollow reads. A single linear strength had to pay for "a tree
 *      well reads" and "an open hillside stays clean" out of one number, and the second
 *      constraint was setting it. (That transfer was first an exponent above 1; the round
 *      after this one measured where the top of the distribution actually is and replaced
 *      it — see `aoWideShape`.)
 *   3. **The masks read the luminance they were calibrated against.** `aoSunMask` and
 *      `aoDarkRelief` are smoothsteps over "scene luminance as DEBUG_VIEW 6 reports it",
 *      which is exposure-scaled; the shader was feeding them the pre-exposure value and
 *      would have silently mis-fired the moment anyone touched `Tuning.render.exposure`.
 *
 * And one instrument, because none of this was visible from any view this file had:
 * **DEBUG_VIEW 7 puts the same pixel through the identical display transform twice, with
 * the occlusion and without, and shows the difference in display code values.** Every
 * other AO view here reports the buffer, and the buffer was never what was broken.
 *
 * ## Why a correct occlusion buffer, correctly applied, still drew nothing on the ground
 *
 * The round above fixed the ordering and shipped an exponent of 1.35 in front of a gain of
 * 1.45, on the argument that an exponent above 1 buys separation: it hands the darkening to
 * the top of the distribution and leaves flat ground alone. The argument is sound and the
 * conclusion was wrong, because nobody had asked **what is at the top of this distribution**.
 * Measured with DEBUG_VIEW 7 split by region on the forest frame:
 *
 *     region   wide deficit        darkening delivered, display codes
 *     ground   median 0.063        median 1.3     p95  9.1
 *     canopy   median 0.24         median 2.9     p95 18.5
 *
 * The top of the distribution is **fir foliage seen edge-on at quarter resolution**, which
 * is the one part of the estimate that is noise. So the exponent spent the model's whole
 * authority on canopy speckle and left the ground — every hollow, every bench crease, every
 * tree base, the entire subject of the brief — moving the image by one code value in thirty,
 * on 34% of ground pixels below one code. "Half the occlusion model is inert" was still true
 * after the round that was supposed to fix it, and the buffer was still not the reason.
 *
 * `aoWideShape` replaces the exponent with floor / gain / saturate, which is the shape the
 * measured distribution actually wants: nothing below the estimator's own residual on a
 * plane, a hard gain through the middle where the structure is, and a ceiling so the noisy
 * top cannot run away with the frame.
 *
 * ## Why *that* transfer then turned the forest floor into a black-point shift
 *
 * `1 - exp(-16 * d1)` reaches 90% of its ceiling at a deficit of 0.16, and the forest
 * floor's *median* deficit is 0.165. Measured at the stocked-stand station with DEBUG_VIEW
 * 9 (the wide ring's authority after the masks, ceiling 76 codes):
 *
 *     region        p25   p50   p75   p95        local contrast
 *     ground         58    69    73    75            0.6 - 1.6
 *     canopy         39    69    75    76            5.5
 *
 * Three quarters of the ground inside 17 codes of a 76-code ceiling is not occlusion, it is
 * a black point. And the one region with real variation in it is the canopy, whose variation
 * is *quarter-res aliasing* — 4x the local contrast of the ground on the same frame. So the
 * model was spending its authority twice over on the wrong thing: saturated where the
 * structure is, and noisy where there is none.
 *
 * Both halves are fixed at their own layer, because they are two different faults:
 *
 *   1. **The estimate stops being fed foliage.** The wide ring no longer marches the
 *      full-resolution depth buffer. It marches a quarter-res *median* depth prefilter
 *      (`AO_COHERENCE_FRAG`) that also reports, per texel, how far apart the near and far
 *      surfaces in that texel are. A fir seen through its own needles has a huge spread and
 *      a meaningless median; ground, trunks, banks and rock have a small spread. That one
 *      number both fades the term on foliage *receivers* and discounts foliage *occluders*,
 *      for no extra taps, at the resolution the error is actually at. Measured, it takes the
 *      canopy's local contrast in DEBUG_VIEW 9 from 5.5 code values to 2.4 and moves the
 *      bare forest floor's own deficit by 0.02 — the authority stopped going into aliasing
 *      rather than the term getting weaker.
 *   2. **The transfer is fitted to the resolvable part of the distribution.** The exponential
 *      is replaced by a soft clip, `x / sqrt(1 + x^2)` on `x = (d - floor) / knee`, whose
 *      knee is set at the forest floor's own p5 deficit rather than well below its median.
 *      That is the difference between a transfer that saturates inside the population and one
 *      that spans it: see `aoWideShape`.
 *
 * ## R3: does the transfer still span the ground, and where IS the forest
 *
 * The carried gap says the wide ring "saturates its own transfer and lands as a near-constant
 * multiply on the forest floor: p25 64 / median 72 / p95 76 out of a 76 ceiling". Re-measured
 * this round on the shipped build, and two things came back.
 *
 * **The transfer spans the population.** DEBUG_VIEW 9 over a bare-floor rectangle, ceiling
 * 128 (aoStrength 0.50): **p25 0 / median 11 / p75 36 / p95 87 / p99 95**. Local contrast on
 * the ground 0.56 code values against the canopy's — the aliasing the previous round was
 * spending the model's authority on — now below it. Nothing is inside 17 codes of the
 * ceiling; three quarters of the distribution is in the bottom third of the range, which is
 * a term that is doing more where there is more to find. The soft clip and the quarter-res
 * coherence prefilter (`aoWideShape`, `AO_COHERENCE_FRAG`) closed that gap; this round
 * confirms it rather than re-cutting it.
 *
 * **The station every one of those numbers was taken at is no longer forest.** (-828, 275)
 * is hard-coded in `floorcam`, `todforest` and `todmask` as "a fully stocked stand,
 * ENV_TREE 0.74". Asked today, `__scatter.env` there returns **tree 0, forest 0, height 706 m
 * against a treeline of 686** — it is open dirt above the treeline, and the beauty frame
 * (`captures/w4post-r3-base/forestIn_base_v0.png`) is an alpine slope with a view to the far
 * range. The vegetation and terrain pieces are live this wave and the timber moved. Scanning
 * `__scatter.env` over a 6 km box (`captures/w4post-find.mjs`) puts the nearest real stand at
 * **(-2850, 150)**, tree 1.00 / forest 1.00 / height 496 m, and that is where the numbers
 * above were taken. Anything in this file that says "forest floor" and predates this round
 * was measured on open ground; treat those numbers as describing the open case.
 *
 * ## What this file takes from `render/shading/relief.ts` — two of the five operations,
 * ## the shared light block, and the CPU mirror. And why the other three are refused.
 *
 * Stated as a checklist, because the integrator's audit going into this wave read "post uses
 * zero of the library's five operations, importing only the bookkeeping call and the sun
 * colour", and the answer has to be checkable against the imports at the top of this file
 * rather than argued:
 *
 *     1 normal perturbation   NO   — argued below
 *     2 cover blending        NO   — argued below
 *     3 contact occlusion     YES  — `rlfContactAt`, in COMPOSITE_FRAG's `wheelContact`
 *     4 lit sprites           NO   — argued below
 *     5 one LOD policy        YES  — `rlfLodManual` + `rlfAlbedoAmp` in AO_FRAG and
 *                                    AO_WIDE_FRAG; `reliefNyquist` / `reliefFootprint`
 *                                    (the CPU mirror) in `solveCulls`
 *     shared light block      YES  — `rlfEnv` in COMPOSITE_FRAG's `aoTintLit`, and
 *                                    `reliefUniforms()` on every material in the chain
 *     installReliefSystem     YES  — called once at module scope
 *
 *
 * The library's thesis is that everything in this game is PAINTED rather than SHAPED and
 * nothing modulates the scene's lighting. At this layer that is the same sentence as the
 * paragraph above — a constant multiply on the forest floor is paint; an occlusion that
 * spans its own population is lighting. What the library has that a full-screen composite
 * can actually consume is three things, and all three are now consumed:
 *
 *   - **The LOD policy (operation 5).** Both occlusion rings used to share one invented
 *     42-78 m window. They are 20x apart in feature size, so one window cannot be right for
 *     both: it fades the contact ring (a 0.30 m feature) 20 m *after* it stops being
 *     resolvable, and it kills the wide ring (a 6 m feature) at 78 m, which is roughly where
 *     the "past 100 m the mountain is a flat wash with no gully or rib carrying any shade"
 *     complaint starts. Each ring now fades on `rlfAlbedoAmp( rlfLodManual(...), 1/feature )`
 *     — the library's own Nyquist policy at its own feature frequency — and the CPU-side
 *     early-out that has to agree with it is computed from the library's CPU mirror
 *     (`reliefNyquist` / `reliefFootprint`), which is exactly what that mirror exists for.
 *     Solved at 1600x900 / fov 60: the contact ring is full strength to 20 m and gone by
 *     66; the wide ring holds full strength to 202 m, still has 54% of itself at 300, and
 *     is faded out by the 360 m cost cull (`aoCullM`) well before its own Nyquist zero at
 *     653. Against one shared window that killed both at 78.
 *   - **The shared light block (`rlfEnv`).** The occlusion tints were three authored
 *     constants fitted at 07:54 — the same fault class as the mask windows the round before
 *     this one referred to the light rig. Occlusion is loss of *sky*, so what an occluded
 *     pixel is multiplied toward is the chromaticity of the ground bounce relative to the
 *     ambient it lost, and both of those are in the block every other piece shades against.
 *     The authored value (how dark) stays; the hue now tracks the sun. See `uAoTint`.
 *     Held to `aoTintLight` 0.30 rather than 1, and the doc there says why: measured across
 *     06:36 / 07:54 / 12:00 the ground-bounce-to-ambient chromaticity ratio moves about 3%,
 *     so this is mostly a small constant correction toward the rig's own answer and it would
 *     be overselling it to call it a time-of-day fix.
 *   - **Contact occlusion (operation 3).** `rlfContactAt` at each tyre's contact patch —
 *     see `aoWheelRadius`. The contact ring in this file is a horizon march at half res over
 *     a 0.30 m radius and a tyre is 60 mm wide, so there is nothing there for it to march
 *     over; the library's closed form is the only thing that can draw it, which is precisely
 *     the case relief.ts's own header lists ("a stone, a tussock, a wheel"). Measured by A/B
 *     in one page session, it takes the ground inside the tyres' own radius down 11% and
 *     leaves the sky and the far hillside within 0.4 of a code value.
 *
 * ## WHAT THIS CHAIN COSTS — measured, at 1080p, against BAR's 2 ms
 *
 * The round before this one wrote "EXT_disjoint_timer_query does not exist in the harness's
 * headless ANGLE/SwiftShader, so `__gripPost.gpuMs()` returns -1 there". **That was wrong on
 * both halves and it is the reason this round exists.** The renderer under the harness's own
 * launch args is `ANGLE (Apple, ANGLE Metal Renderer: Apple M4 Max)`, not SwiftShader; the
 * extension is present; `timer.available` is true and the queries resolve, 894 for 894, with
 * zero disjoints.
 *
 * ### But the query still cannot price this file, and that is a measurement, not an excuse
 *
 * Two runs of the same five variants on the same build, from the critic's own probe:
 *
 *     variant                        run A medianGpuMs   run B medianGpuMs
 *     shipped                             30.8                9.9
 *     motion blur OFF                     24.5               31.7
 *     motion blur 0.021                   19.9               30.3
 *     motion blur 0.045 (shipped)         18.7               18.3
 *     + wide ring OFF                     15.6               31.7
 *
 * Run A is monotone in the work removed. Run B is noise, and it *reverses the ordering*. A
 * query that resolves every sample and cannot rank a 4x fill-rate change is not measuring
 * what sits between `beginQuery` and `endQuery`: on ANGLE's Metal backend TIME_ELAPSED comes
 * off command-buffer timestamps, so it sees the whole submission and everything sharing the
 * GPU with it — and nine other agents are running headless Chromium on this machine.
 *
 * ### The instrument that does work: a slope, not a difference
 *
 * `captures/w4post-bench.mjs`, and `PostFX.setRepeat` is the hook it needs.
 *
 *   1. Pin the frame — camera parked, both brakes held, clock fixed, tier locked.
 *   2. Run the chain n = 1, 3, 6, 10 times on that identical frame and regress wall clock
 *      on n. **The slope is the chain.** The world's draw, the MSAA resolve, the swap and
 *      every driver constant are constant in n and cancel out of a slope; they do not cancel
 *      out of a difference, which is why every absolute number above is unusable.
 *   3. Sync with `readPixels`, not `finish()`. In Chrome the GL service lives in another
 *      process and `finish()` returns on its ack — timed that way, eight full-res composites
 *      at 1080p came back as 0.5 ms. A one-pixel readback is a real round trip.
 *   4. Sweep every variant end to end three times and reduce by **minimum**: a second page
 *      hitting the GPU can only add time, so the minimum is the estimator that survives a
 *      shared machine. One session came back with a 4.5 ms quiet floor and the next with
 *      16-26 ms on the same build.
 *
 * Reported with R^2, and a row under 0.99 is thrown away rather than quoted.
 *
 * ### The numbers. 1920x1080, OPEN-DIRT station, camera parked, tier pinned
 *
 * **Read the W5 section below before quoting any of these.** The station is open alpine and
 * the whole cost model fitted here is wrong about which term dominates. Kept because the
 * method is right, the smear-length result survives re-measurement, and a round that quietly
 * deleted the numbers it superseded would be hiding the size of the correction.
 *
 * Only rows with R^2 >= 0.97 are quoted; contended passes are discarded, not averaged in.
 * The two clean sessions are labelled because the world changed between them (nine agents),
 * so **deltas are only valid within a session**.
 *
 *   SESSION A — build with the 4-tap ladder, R^2 0.9941 to 0.9999
 *
 *     what                                    chain ms     delta
 *     grade + tone curve + vignette (tier 0)     0.082
 *     + bloom                          (tier 1)  0.149      +0.067
 *     + contact ring and its resolve             0.252      +0.103
 *     + wide ring (coherence, march, denoise)    0.518      +0.266
 *
 *     smear length, all at tier 3, same session:
 *     blurMaxFrac    0      0.021   0.045   0.72 (per-tap cap off)
 *     chain ms       0.515  0.522   0.518   0.513
 *
 *   SESSION B — the shipped build, 8-tap ladder, R^2 0.9762 to 0.9998
 *
 *     shipped                       0.657 / 0.629 / 0.646   (three measurements)
 *     same, with a 4-tap ladder     0.634                   -> 8 taps costs +0.009
 *     same, motion blur off         0.608                   -> all of MB costs  0.035
 *     grade only (tier 0)           0.069
 *
 * **0.64 ms**, against the 2 ms docs/BAR.md allows — and 11% of the frame's own GPU time at
 * the same station (base 4.5-5.6 ms).
 *
 * Three things follow that were previously guesses:
 *
 *   - **The wide ring is half the chain** — 0.266 of 0.518 ms — which is the number to come
 *     back to first if a budget ever bites, and it is spent on the term the brief is about.
 *   - **Motion blur is 5% of the chain and its LENGTH is free.** A 16x change in smear
 *     length moves 0.005 ms, inside the noise, because the ladder is `2 * MB_TAPS + 1`
 *     samples *however long the streak is*. "This round roughly doubled the motion-blur
 *     smear, a fill-rate change at 1080p" is a reasonable-sounding model of this shader that
 *     the shader does not have. The thing that IS a fill-rate change is the tap count, and
 *     doubling that costs 0.009 ms — which is what paid for this round's heavier blur.
 *   - **The degradation ladder is priced**: tier 1 is 29% of tier 3, tier 0 is 16%.
 *     **This last line is the one the W5 section overturns — at canopy tier 1 is 62-67%.**
 *
 * ### W5: EVERY NUMBER ABOVE WAS TAKEN AT OPEN DIRT, AND THE CONCLUSION REVERSES AT CANOPY
 *
 * The station all of it was measured at — `forestIn`, (-828, 275) — is the one the section
 * above already flags as "no longer forest": `__scatter.env` returns tree 0 / forest 0,
 * above the treeline. Both AO rings early-out on sky and on the far fade, so open alpine is
 * the CHEAP end of this chain and the whole cost model was fitted where it does least. The
 * fix is not a better coordinate, because the timber moves every wave; `captures/w5post-bench.mjs`
 * SCANS for the densest stand and reports what it found, and prints the frame's draw calls
 * and triangle count so an empty frustum is visible rather than inferred. (Its first run
 * took the scan box's own edge, got 0.7 ms frames against wave 4's 4.5-5.6 ms base, and that
 * guard is why it did not get quoted.)
 *
 * Re-measured at **(-3000, 2000): tree 1.00, forest 1.00, clutter 1.00, 118 draws, 8.57 M
 * triangles**, same slope instrument, five interleaved passes reduced by minimum. Tier 3
 * measured TWICE independently in the same session, which is the error bar:
 *
 *     0.92 Mpix (1280x720)    0.349 / 0.316   mean 0.333   spread 10%
 *     2.07 Mpix (1920x1080)   0.651 / 0.711   mean 0.681   spread  9%
 *     3.69 Mpix (2560x1440)   1.225 / 1.101   mean 1.163   spread 11%
 *
 *     regression on megapixels:  0.057 ms fixed + 0.300 ms per megapixel,  R^2 0.99999
 *
 * **At 1080p, 92% of the chain is FILL.** The paragraph this replaces concluded the opposite
 * — "dominated by a per-pass constant, roughly 0.10 ms fixed plus 0.07 ms per megapixel" —
 * from a 720p/1080p pair on open dirt, where most of the fill was being skipped. Three
 * resolutions on canopy give a straight line through the origin's neighbourhood with an R^2
 * of five nines. The per-megapixel term is the one that transfers to another GPU, and it is
 * not a correction to the story, it is the whole story.
 *
 * ### The tier ladder is a THIRD, not two thirds — and the two methods agree
 *
 * `postfx.fillPasses` counts what the chain writes, in units of one full-resolution pass,
 * from inside `Quad.draw`. It is exact, costs nothing, and is the same number on every
 * device. Beside it, the timing at 1080p canopy:
 *
 *     tier   fill (exact)   of tier 3   measured ms   of tier 3   R^2
 *      3        1.906         100%         0.681        100%      0.94 / 0.96
 *      2        1.875          98%         (0.52)         —       0.55  rejected
 *      1        1.188          62%         0.457         67%      0.998
 *      0        1.000          52%         (0.14)         —       0.69  rejected
 *
 * The two clean rows agree to five points, by two methods that share nothing. So:
 *
 *   - **Tier 1 costs 62-67% of tier 3.** Not the 29% this file claimed, and not the 38% the
 *     re-measurement that carried the gap found. The ladder saves about a THIRD.
 *   - **Tier 2 is tier 3.** It differs by one pair of 1/8-res bloom blurs: 1.6% of the fill.
 *     It is a look step, not a cost step, and calling it a degradation tier oversells it.
 *   - **The reason is exact and is not going to move.** The composite is 1.000 of 1.906 —
 *     **52.5% of the chain's fill — and it runs at every tier including tier 0.** The ladder's
 *     entire authority is the other 47.5%. No retuning of the tiers can beat that bound.
 *   - The wide ring is 10% of the fill and 29% of the time (0.481 ms at 1080p with it off),
 *     so it is the one part of the chain that costs more per pixel than average — it marches.
 *     Wave 4's "the wide ring is half the chain" was open-dirt arithmetic; at canopy the
 *     composite is.
 *   - Motion blur remains inside the noise: `mbOff` 0.783 and a 16x smear (`ctl_mbAbsurd`)
 *     0.751 against tier 3's 0.681, all within the 10% repeatability. Wave 4's conclusion
 *     that the LENGTH of the smear is free survives at canopy; the tap count is the cost.
 *
 * ### W5R3: re-verified, and what this round added to the bill
 *
 * No pass was added, removed or resized this round, so the timings above stand and are not
 * re-quoted as if they were re-measured. What WAS re-measured is the exact half — fill —
 * because it costs nothing and it is the number that transfers off this GPU. At a freshly
 * scanned canopy station (-3600, 3000; tree 1.00 / forest 1.00 / clutter 1.00; 8.18 M
 * triangles, 93 draws) at **1920x1080**, straight out of the shipping path:
 *
 *     tier 3  1.9063     tier 2  1.8750     tier 1  1.1875     tier 0  1.0000
 *
 * Identical to R2's ladder to four figures, which is the point of a structural metric.
 *
 * **The one thing this round DID add to the frame is the instrument, and it is a hitch, not
 * a slope.** `postfx.frameAuditMs`: 5.4 ms at 1600x900, 7.6 ms at 1920x1080, once every
 * `FRAME_PERIOD_S`. It adds nothing to fill and nothing to the per-frame chain — see the
 * doc on `FRAME_AUDIT_MS_MAX` for why that is stated as a dropped frame every four seconds
 * rather than as a percentage.
 *
 * ### The integrated GPU: NOT MEASURED. What the ladder assumes instead.
 *
 * BAR's floor is 60 fps at 1080p **on integrated graphics**. There is no integrated GPU
 * reachable from this machine — the harness's renderer is `ANGLE (Apple, ANGLE Metal
 * Renderer: Apple M4 Max)` and the only other device Chromium will give us here is
 * SwiftShader, which is a CPU. **No number in this file has ever been taken on the hardware
 * BAR names, and quoting a published spec ratio would be inventing one.** What can be said
 * without inventing anything is the break-even, because the transferable term is now
 * measured and the structural term is exact:
 *
 *     at 1080p, chain = 0.057 ms fixed + F x 0.623 ms, where F is how many times lower the
 *     target's fill throughput is FOR THIS SHADER than an M4 Max's
 *
 *     tier 3  breaks BAR's 2 ms at  F > 3.1
 *     tier 1  breaks BAR's 2 ms at  F > 5.0
 *     tier 0  breaks BAR's 2 ms at  F > 5.9      <- this is the FALLBACK
 *
 * Three things that follow, and the third is the one a future round should act on:
 *
 *   1. **The margin is three-fold, not order-of-magnitude.** Anyone who has looked at an
 *      integrated part's fill rate next to an M4 Max's should treat "tier 3 fits in 2 ms on
 *      integrated" as unlikely rather than as established. This file is not going to put a
 *      number on F it did not measure, but it is not going to imply the question is settled
 *      either, which is what the paragraph this replaces did.
 *   2. **The tier ladder assumes the composite is affordable everywhere, and that assumption
 *      has never been tested.** At tier 0 the composite IS the chain, so the fallback and the
 *      shipping path are within 1.9x of each other in fill. A degradation ladder whose worst
 *      case is half its best case is not a mitigation for a 3x hardware gap.
 *   3. **The lever that is missing is render scale, not another tier.** Fill is 92% of the
 *      cost and scales with buffer AREA, so putting the model above through a scale s:
 *
 *          s = 1.00  0.679 ms  100%      tier 0 by fill, modelled:  0.384 ms   57%
 *          s = 0.75  0.407 ms   60%
 *          s = 0.67  0.336 ms   49%
 *
 *      A 0.75x render scale buys about what collapsing all the way to tier 0 buys, and it
 *      keeps the occlusion, the bloom and the blur; at 0.67x it beats tier 0 outright, still
 *      with the whole look on. That is the shape of the answer if F turns out to be 3 or
 *      more. It is a change to how the chain is SIZED rather than to what is in it, it
 *      interacts with the scene draw and with the UI's own sharpness, and it is not a call to
 *      make unilaterally inside this file. Named here so the next round starts from it
 *      instead of re-deriving it.
 *
 * Operations 1 (normal perturbation), 2 (cover blending) and 4 (lit sprites) are surface
 * operations: they take a shading normal, a cover layer or a camera-facing quad and give
 * Operations 1 (normal perturbation), 2 (cover blending) and 4 (lit sprites) are surface
 * operations: they take a shading normal, a cover layer or a camera-facing quad and give
 * back form. This system's input is a framebuffer and a depth buffer — after every surface
 * in the frame has been shaded and resolved — so there is no surface to perturb, no cover to
 * blend and no sprite to light. Adopting them here would mean inventing a surface, which is
 * the "painted" failure the library exists to stop, one layer further down the pipe.
 *
 * The open coordination problem, stated here so the next round does not have to rediscover
 * it: the wave-3 coherence pass found **three** systems darkening the ground under the bike
 * — `bikeModel`'s contact blobs, the sky's analytic rider ellipsoid (which measured 1.6 m
 * off to the side), and this file's contact ring — and read the combined result as a
 * sticker. The tyre term above is a fourth, deliberately at a *different scale* to the other
 * three so it adds the thing none of them draws rather than a fourth copy of what they all
 * draw. That is a mitigation, not a fix. BAR puts contact occlusion on this piece and this
 * piece is the right owner for all of it; the other two should come out. That is a call for
 * whoever routes the wave, not one to make unilaterally inside one file.
 *
 * ## The clock
 *
 * Both masks were then absolute windows in scene luminance, fitted to the histogram of one
 * frame at 07:54 — the only clock time any scenario in the harness had ever rendered. See
 * `aoAmbientRefK` for what that cost and what replaced it, and `todvista` / `todforest` /
 * `todmask` in `tools/capture/scenarios.mjs` for the captures that were missing.
 *
 * ## The grade, and the point of view behind it
 *
 * **W5: THE AUTHOR OF THIS GRADE IS NOW `src/render/palette.ts`, NOT THIS FILE.** Every
 * constant from `curvePreScale` to `flareColor` is read from its `Grade` block; the
 * argument for each one stays here, because this is where the implementation is and the
 * arguments were earned here, but the numbers are the authority's. `postfx.gradeDrift`
 * re-checks all twenty on every frame and `postfx.neutralAxisChroma` measures what they
 * do to a neutral, so the copy cannot silently diverge and the look cannot be
 * un-authored by a probe that forgets to restore.
 *
 * What that actually changed, measured rather than asserted (`captures/w5post-grade.mjs`,
 * one page session, one pinned frame, the old grade written back into `PostTuning` at
 * runtime so the A/B is on one build):
 *
 *   - **The black point was blue and unmasked, and this file's own comment predicted it.**
 *     `flare` is the only term in the chain with no luminance mask, so its colour reaches
 *     every pixel before anything else runs. The comment on `flare` below says, in these
 *     words, that weighting it hard to blue "is not a cool shadow, it is a blue cast on
 *     the darkest quarter of every frame" — and then shipped `[0.42, 0.52, 0.76]`, OKLab
 *     C 0.0150 at hue 264, against an authority ceiling of 0.010. The correct argument
 *     was attached to the wrong number for three rounds because nobody measured the
 *     constant against the sentence. `[0.58, 0.62, 0.74]` at 0.024 is C 0.0070, hue 270.
 *   - **The restraint was being spent twice.** `satChromatic` 0.88 pulled 12% out of the
 *     chromatic content — the dirt, the timber, the trail — while the cool near-neutral
 *     majority of the frame kept all of its own. Upstream, every world albedo is already
 *     capped at `PaletteLimits.worldChromaCeiling`. 0.94.
 *
 * **What was NOT adopted, and why, because a silent omission is how the last round got
 * "you imported it and did not use it".** `palette.gradeExposure(env)` moves exposure —
 * and only exposure — with time of day and cloud. It is not applied here. Two reasons,
 * both arithmetic rather than taste. (1) At the harness's own default clock (07:54,
 * cloudCover 0.32) it returns 0.886, so adopting it would darken **every frame every
 * other agent captures this wave by 11%**, mid-wave, including the ones being used to
 * calibrate terrain shading against absolute scene luminance. (2) The exposure this chain
 * multiplies by is `renderer.toneMappingExposure`, which is `main.ts`'s
 * `Tuning.render.exposure` and is also what three's own ACES path uses when the chain
 * falls back — applying a second factor inside the composite would make the shipping path
 * and the fallback disagree by that factor on exactly the machines that take the fallback.
 * It belongs to whoever owns `Tuning.render.exposure`, applied once, in one place.
 *
 * The direction is stylized-grounded (docs/BAR.md). Simplified materials read as
 * *authored* rather than as unfinished when the value structure is deliberate, so this
 * grade is opinionated and constant — the same transform at 07:00, at noon and at
 * 19:00. It never adapts to the scene, because an auto-exposure or a time-of-day-keyed
 * grade is precisely what makes a game read as "a viewport" rather than as a look.
 *
 *   - **Filmic per-channel curve.** The game shipped on three's ACES, and terrain,
 *     vegetation and atmosphere are all calibrated against its response — swapping in
 *     a curve with a different midtone answer moves the whole world by most of a stop
 *     and silently breaks four other people's tuning. So the curve here is ACES's own
 *     RRT+ODT rational fit, applied **per channel with the ACES colour-space matrices
 *     removed**. Identical response on a neutral, so nobody's exposure moves; but
 *     without the inset/outset matrices it loses ACES's notorious hue skew, which is
 *     what drags warm dirt toward yellow and saturated greens toward acid — exactly
 *     the two hues this palette is built out of. Per channel is also what desaturates
 *     highlights the way film does, so a bright sky rolls off into cloud structure
 *     instead of a flat clipped field. A gamma just under 1 afterwards opens the toe
 *     back up: ACES crushes the tree line, and shadow detail is not optional here.
 *   - **Cool shadows, warm key** as a split tone, restrained: ~6% either side. Enough
 *     that the shadowed side of a ridge reads sky-lit and the sunlit side reads sun-lit,
 *     not enough to become a teal-and-orange trailer.
 *   - **Restraint scaled by chroma.** Neutral pixels keep their saturation; already
 *     saturated ones get pulled back. That is what stops the ochre dirt going candy
 *     without draining the evergreens.
 *   - **Neutral-preserving channel mixer** that leans blue slightly toward green
 *     (evergreen, teal shade) and cleans green out of red (dirt reads as clay, not mud).
 *     Rows sum to 1, so the white point does not move.
 *   - **A cool veiling flare in scene-referred linear.** Film has a toe, not a hard zero,
 *     and a toe is a thing you do *before* the transfer function, not after it. Every
 *     operation after the curve here is multiplicative for the same reason: an additive
 *     term in display space cannot lift a black without flattening what sits just above
 *     it, and that is what collapsed 3/4 of a canopy frame into seven code values in the
 *     round before this one. See `PostTuning.flare` and `PostTuning.contrast`.
 *
 * ## What this piece publishes to the self-audit bus (`src/core/audit.ts`)
 *
 *     postfx.neutralAxisChroma  build  the colour the grade puts on a scene NEUTRAL, worst
 *                                      level. Budget: the palette's neutralAxisChromaMax.
 *     postfx.blackPointChroma   build  the veiling flare's own chroma — the one grade term
 *                                      with no luminance mask, so it reaches every pixel.
 *     postfx.gradeDrift         run    every grade constant against the palette authority,
 *                                      every frame. Budget 0.
 *     postfx.fillPasses         run    what the chain writes, in full-resolution passes.
 *                                      The cost quantity that transfers off this GPU.
 *     postfx.renderTier         run    the tier the frame ACTUALLY rendered at, against 3.
 *     postfx.governorTierLoss   run    tiers the GOVERNOR took and did not give back.
 *                                      Budget 0. The one number that separates "a probe
 *                                      pinned a tier" from "a controller removed the look".
 *     ---- W5R3: the five that READ A PIXEL. See the block above `INV_FRAME_BLACK`. ----
 *     postfx.frameBlackPoint    run    p1 display luma of the finished frame, against the
 *                                      palette's authored band [0.030, 0.070].
 *     postfx.frameWhitePoint    run    p99 over the UNCLIPPED pixels, against 0.94.
 *     postfx.frameClipped       run    share of the frame at or past display 0.98, vs 0.005.
 *     postfx.frameToneOccupancy run    how much of the frame's OWN tonal range carries
 *                                      pixels. The "is this capture evidence at all" guard.
 *     postfx.frameAuditMs       run    this instrument's own bill.
 *
 * `governorTierLoss` and `renderTier` are the reason the degradation section below matters
 * more than it looks. The frame-time governor steps the tier down on its own, nothing
 * reported it, and every screenshot taken after a step-down is a different chain from the
 * one being graded.
 *
 * ## W5R3: THE PIECE THAT OWNS EVERY PIXEL HAD NO INVARIANT THAT READ ONE
 *
 * The carried gap, verbatim in effect: all six invariants above certify the chain's
 * CONFIGURATION analytically — `this.tier`, a fill counter, two constant tables, and a
 * SYNTHETIC neutral pushed through a CPU mirror — so all six report green on captures
 * whose frames could not support the claim. `terrainMaterial.ts` already reads the
 * finished frame (`nearClassSeparationL`), so the capability was in the codebase and this
 * piece was not using it.
 *
 * `PostFX.measureFrame` reads the canvas at the end of `render` — the same bytes the
 * harness screenshots — and publishes the five lines above. The instrument was validated
 * before any number from it was quoted; that is in `captures/w5post-r3-tools/`.
 *
 * ### What it found on the first frame it read, and it is not small
 *
 * On `vista`, `todvista`, `camtimber` and `trailride` — four independent captures within
 * one hour — the shipping build broke two of the palette's own frame targets:
 *
 *     postfx.frameClipped      0.0138  vs 0.005    2.8x over
 *     postfx.frameWhitePoint   0.9725  vs 0.94     (p99 over the unclipped pixels)
 *     postfx.frameBlackPoint   p1 0.082 vs band 0.030-0.070   on the late/low-sun frames
 *
 * ### AND THEN THE WORLD MOVED UNDER IT, WHICH IS ITSELF THE POINT
 *
 * Re-run ninety minutes later on the same probe, same code, the same station clipped
 * **zero**: the exposure sweep below went from 11.8% of the frame above scene luminance
 * 1.0 to 1.6%, and nothing above 2.0 at all. Nine agents edit this tree and the sky, the
 * spawn and the trail all moved between the two runs. So the honest statement is split in
 * two, and only one half is a property of this file:
 *
 *   - **The transfer's thresholds are a property of `Grade` and do not move**: the authored
 *     grade reaches sRGB luma 0.94 at scene 1.12, 0.98 at 1.65 and 1.00 at 3.73. Anything
 *     the sky hands this chain above 1.65 is inside the last 2% of the range.
 *   - **Whether a given frame is up there is a property of the world on the day.** It was,
 *     four times, this morning. It was not, ninety minutes later.
 *
 * That is exactly the wave-4 lesson about a station going stale, and the reason it is
 * survivable here is that the invariant is now IN the shipping path: it is re-measured on
 * every capture any agent takes, rather than being a number in a report that ages.
 *
 * ### Attribution, one page session, one pinned frame — it is NOT the sun and NOT the chain
 *
 * `captures/w5post-r3-tools/whodunit.mjs`. Sweeping `toneMappingExposure` by k turns
 * `frameClipped` into the tail of the scene's own luminance distribution, because a pixel
 * clips when its post-exposure scene luminance passes ~2: clipped(k) = P(L > 2/k).
 *
 *     k          4      2      1        0.5    0.25   0.125   0.0625
 *     L >       0.5    1.0    2.0       4.0    8.0    16.0    32.0
 *     clip AM   0.217  0.118  0.0134    0      0      0       0     <- the hot run
 *     clip PM   0.116  0.0165 0         0      0      0       0     <- 90 min later
 *
 * **Nothing in the frame is above scene luminance 4, in either run.** So the clipped region
 * is not a sun disc — a sun disc is hundreds — it is the circumsolar sky, sitting in a band
 * the transfer cannot hold. And it is not this chain: on the same frame, bloom off takes
 * 0.0124 to 0.0105 and **tier 0 — grade, curve and vignette only — still clips 0.0105**, so
 * 85% of it survives removing everything the ladder can remove.
 *
 * It is the authored transfer, and the arithmetic is exact. The contrast operator is
 * `pivot * pow(c/pivot, 1.12)` with **no shoulder**, so display-linear passes 1.0 at scene
 * 2.1 and the sRGB clamp is the only ceiling. Pushing a neutral through the whole
 * transform (`Grade`'s own constants):
 *
 *     sRGB luma 0.94 at scene 1.12      <- GradeTargets.whitePointP99Max lands HERE
 *     sRGB luma 0.98 at scene 1.65
 *     sRGB luma 1.00 at scene 3.73
 *     with contrast 1.00 instead: 0.98 arrives at scene 3.56, a factor of 2.2 later
 *
 * So 1.2 stops of sky arrive in five code values with the red channel pinned at 254 (zero
 * pixels are fully white — measured — which is why this reads as a hot sky rather than as
 * a hole, and why nothing had caught it).
 *
 * **This round did not fix it, on purpose.** Every constant in that chain — `contrast`,
 * `contrastPivot`, `highTint`, `hiDesat*` — is `src/render/palette.ts`'s `Grade`, adopted
 * in R2, and `postfx.gradeDrift` has a budget of 0 against it. Adding a shoulder in the
 * shader instead would be a new authored number smuggled into the implementation, and it
 * would silently break `palette.applyGrade`, which is the authority's mirror of THIS file.
 * The exposure the frame is graded at is `Tuning.render.exposure`, which is not this
 * piece's either. So the finding is routed with its numbers rather than patched here — see
 * the round report. What this file now has that it did not have is the invariant that
 * makes it impossible to ship past silently again.
 *
 * ## Degradation, and the four-wave defect that lived in it
 *
 * Four tiers, picked from capability at boot and then adapted from measured frame time.
 * Tier 0 is grade + tone curve + vignette only — a device that cannot afford the chain
 * still gets the look, just without the trimmings. If even a render target cannot be
 * created the system releases `renderHook` and the game draws itself with three's own
 * ACES path, which is exactly the pre-existing behaviour.
 *
 * ### W5R2: the governor was not fed a measurement, and it cost 99.1% of the gate run
 *
 * `adapt(dt * 1000)`. `dt` is the timestep the frame was ASKED TO ADVANCE BY; the frame
 * PERIOD is how long the machine took. Under `requestAnimationFrame` they coincide,
 * because `GameLoop.frame` passes the measured wall interval straight through — which is
 * why nobody caught it for four waves. Under the capture harness they do not:
 * `__grip.stepFrames(n, dt)` calls `stepManual` with a CONSTANT dt, 1/30 on `fullrun`. So
 * the controller was handed 33.3 ms on every frame against a `degradeMs` of 20.5, on
 * hardware rendering that frame in 1.6 ms, and it did the only thing it could:
 *
 *   - tier 0 in ~3.6 s, every run, regardless of hardware;
 *   - **99.1% of the gate run's 5154 frames** rendered with no occlusion, no bloom, no
 *     motion blur and no tyre contact — the authored look absent from the frames this
 *     project grades the ride on;
 *   - and it SURVIVED the harness's per-scenario `__gripPost.reset()`, because
 *     `tierCeiling` only ever decreased and `unlockTier` did not restore it, so a collapse
 *     in scenario one was still in force in scenario nine of the same page session.
 *
 * Three changes, and the second is the one that matters most:
 *
 *   1. **A measured clock.** `FrameClock` — a rolling median of the wall interval between
 *      frames, with samples over `frameRejectMs` discarded as not-a-frame (a throttled
 *      background tab, a screenshot readback, a driver flush). Median rather than the EMA
 *      it replaces because docs/AGENTS.md is explicit that in this environment one frame
 *      in ten blocks for tens of ms: the gate run's own distribution is median 1.6, p95
 *      3.9, p99 25.3, max 92.1 ms, and a mean of that is a statement about the driver's
 *      queue rather than about the frame.
 *   2. **Every step down is a hypothesis, and it is tested.** Frame period cannot separate
 *      "the GPU is saturated by this chain" from "the display is 30 Hz and the machine is
 *      idle" from "another system in this frame is slow", and the signal that could —
 *      TIME_ELAPSED_EXT — is documented above ranking a 4x fill change backwards on this
 *      machine. So the controller states the hypothesis ("frames are over budget BECAUSE
 *      of this chain"), takes the step, and checks the prediction it implies: the frame got
 *      measurably faster. If it did not, the chain was not the bottleneck, the step bought
 *      nothing, it was paid for with the entire authored look — so the tier goes back and
 *      the controller backs off, doubling. This is the branch whose absence caused the
 *      defect above, and it is the reason the failure mode of a blunt signal is now
 *      conservative: **this piece will not shed its own quality for a saving it can prove
 *      is not there.**
 *   3. **The ceiling is probation, not a ratchet.** Sustained fast frames give a tier back;
 *      `unlockTier` restores the DEVICE ceiling, so the harness's boundary reset means what
 *      its doc says and the collapse can no longer leak between scenarios.
 *
 * The degradation ladder is **3 -> 1 -> 0**, skipping tier 2: `fillPasses` measures tier 2
 * at 98% of tier 3, so it is a look step and not a cost step (see `nextTierDown`). Measured
 * again this round at a freshly scanned canopy station (-3600, 2100; tree 1.00 / forest
 * 1.00 / clutter 1.00; 8.1 M triangles) at 1920x1080, straight out of the shipping path:
 * tier 3 **1.9063** passes, tier 1 **1.1875**, tier 0 **1.0000**, bypass 1.0000 (the world
 * draw alone). So the ladder's first step now removes **37.7% of the chain's fill in one
 * move** instead of 1.6%.
 *
 * What that is worth in milliseconds, and why the governor is nearly inert here, is on
 * `PostTuning.tierVerifyGain`. Read it before concluding the controller does nothing.
 *
 * ### Validated by falsification, not by assertion — `captures/w5post-tools/gov.mjs`
 *
 * Six trials, each driving the controller into a different branch with a frame cost the
 * probe controls (a busy-wait in the page's own frame loop, so the frame genuinely takes
 * that long — the clock is measuring, not being told):
 *
 *     A  30 Hz requested dt, fast real frames    tier 3 held for 30 s        (was: tier 0 in 3.6 s)
 *     B  30 ms/frame, INDEPENDENT of tier        steps down, REJECTS, restores tier 3
 *     C  9 ms per tier (fill-bound emulation)    steps down and KEEPS it, settles at tier 2
 *     D  load removed                            ceiling climbs 2 -> 3
 *     E  pinned tier 0, then reset()             tier 3
 *     F  governor median vs an independent
 *        median of the same 120-frame window     8.0 vs 8.0 ms, 0.0% error
 *
 * B and C are the pair that matters: the same over-budget frame period, opposite correct
 * answers, and the controller gets both right. A metric that could only ever say "yes,
 * degrade" — which is what the old one was, since it was fed a constant — cannot.
 *
 * ## Validating the frame instrument, because five waves say do not skip this
 *
 * Two independent implementations and five deliberate defects.
 *
 * **Agreement.** `captures/w5post-r3-tools/xcheck.mjs` first PROVES the canvas is static —
 * three `frameStats()` in a row with nothing stepped, byte-identical, and unchanged after
 * the screenshot — so what follows compares two instruments and not two frames. Then
 * `frame.py` reduces the PNG the screenshot wrote, in Python, from the file rather than
 * from the GL buffer:
 *
 *     in-engine   p1 0.0431  p50 0.4078  p99 0.9725  clip 0.01145  occ 0.8125  floor 9/12/16
 *     offline     p1 0.0431  p50 0.4000  p99 0.9725  clip 0.01147  occ 0.8438  floor 9/12/16
 *
 * p1, p99 and the per-channel floor are identical to the code value; `clipped` differs by
 * 7 pixels in 360,000. `p50` differs by two codes and occupancy by one bin of 32, and both
 * are explained rather than waved at: the median sits on the sky's flat plateau, so which
 * ROWS are sampled moves it (`readPixels` counts y up, the PNG counts y down, and at
 * stride 2 the two grids are disjoint — measured on the same file, that alone is one code),
 * and the PNG additionally contains the DOM HUD, which the canvas readback does not.
 *
 * Note what this cross-check cannot be: this instrument computes no spatial quantity, only
 * histograms, so it is immune to the y-up/y-down registration error that
 * `terrainshading`'s attribution pass has to guard against. That is stated because "my
 * cross-check passed" means nothing without saying what it could have caught.
 *
 * **Falsification** (`captures/w5post-r3-tools/probe.mjs`, one pinned frame, five grades
 * mutated at runtime so the comparison is inside one page session):
 *
 *     variant                          p1      clip     occ     what must move
 *     shipping                         0.0392  0.0124   0.781   —
 *     flare 0        (crushed black)   0.0039  0.0121   0.813   p1 leaves the band DOWN   OK
 *     flare 0.19     (milky black)     0.2863  0.0197   0.844   p1 leaves the band UP     OK
 *     flareColor pre-adoption blue     0.0275  0.0120   0.813   floor C 0.0101 -> 0.0172  OK
 *     exposure x3    (blown)           0.0784  0.1870   0.750   clip 15x                  OK
 *     contrast 60    (a step function) 0.0000  0.2589   0.156   OCCUPANCY 5x below budget OK
 *
 * The last row is the one the gap is about. `contrast 60` turns the transfer into a step
 * about the pivot, i.e. it draws **a frame that is two flat fields** without touching the
 * world — and occupancy is the only one of the five numbers that collapses, from 0.78 to
 * 0.156 against a floor of 0.35, while p1 and clip move in ways a *correctly* graded frame
 * could also produce. That is what "this metric can see this defect and the others cannot"
 * looks like when it is demonstrated rather than asserted.
 */

import * as THREE from 'three';
import { registerSystem, renderHook, systemTimings, type SystemContext } from '../core/systems';
import {
  installReliefSystem, reliefUniforms, reliefGLSL,
  reliefFootprint, reliefNyquist,
} from './shading/relief';
import { Grade, GradeTargets, linearToOklab } from './palette';
import { declareInvariant } from '../core/audit';

// ---------------------------------------------------------------------------
// Tuning. Local to this module by repo convention (tuning.ts is physics-owned).
// ---------------------------------------------------------------------------

export const PostTuning = {
  // ===============================================================================
  // THE GRADE — every constant below this banner and above `--- ambient occlusion ---`
  // is READ FROM `src/render/palette.ts`'s `Grade` block. This file is the
  // implementation; the palette is the author.
  //
  // They are copied into a mutable object rather than referenced through `Grade.x` for
  // exactly one reason: the probe path (`__gripPost.tuning`, `captures/w4post-probe.mjs`)
  // sweeps a dozen candidate grades on one identical frame inside a single page session,
  // and that is how every grade number in this file was converged. `Grade` is `as const`
  // and must stay that way. `gradeDrift` below is the invariant that makes the copy safe:
  // it re-checks every one of these against the authority on every frame, so a probe that
  // forgets to restore, or an edit here that diverges from the palette, is a line at the
  // top of SUMMARY.md rather than a look nobody can trace to an owner.
  //
  // WHAT THE ADOPTION ACTUALLY MOVED. The palette's finding is narrow and it is not the
  // split tone: "the black point is blue, and it is unmasked". `flare` is added to every
  // pixel before the tone curve, so its colour is the one term in the chain with no
  // luminance mask at all, and this file shipped `0.022 * [0.42, 0.52, 0.76]` — OKLab
  // C 0.0150 at hue 264, on the darkest quarter of every frame. This file's own comment
  // on `flare` predicted it in those words and shipped two thirds of the way there
  // anyway. The authority's `0.024 * [0.58, 0.62, 0.74]` is C 0.0070 at hue 270: same
  // direction, half the cast, and the masked split tone above it is then free to do the
  // work it was always supposed to do alone. Second: `satChromatic` 0.88 -> 0.94, because
  // the chroma ceiling upstream (`PaletteLimits.worldChromaCeiling`) already spends that
  // restraint once, and spending it twice quiets the warm content while leaving the cool
  // majority of the frame untouched, which IS "cold and high-key" arithmetically.
  //
  // Measured through this file's own chain, not the palette's model — see
  // `gradeNeutral()` and the `postfx.neutralAxisChroma` invariant.
  // ===============================================================================

  // --- tone curve ---------------------------------------------------------------
  /** Scene-referred pre-scale. 1/0.6 is what three's ACES path applies, and matching it
   *  is what keeps every other piece's exposure calibration valid. */
  curvePreScale: Grade.curvePreScale,
  /**
   * Display-space gamma after the curve. Below 1 opens the toe.
   *
   * This was 0.93, which was a second compensation for the black crush in the contrast
   * operator (see `contrast`). With the crush gone and a real toe in front of the curve,
   * it has nothing left to do, and 1.0 is worth holding on to: at 1.0 the curve's response
   * on a neutral is *exactly* three's ACES path, which is what terrain, vegetation and
   * atmosphere are calibrated against. Measured, mid grey (scene 0.30) lands on code 166
   * against the untouched render's 165, and the median of a canopy frame within 2 codes.
   * Left as a knob rather than deleted because it is the right lever if a future round
   * wants the toe opened further, and it costs one pow.
   */
  toeGamma: Grade.toeGamma,

  /**
   * Highlight desaturation, applied in scene-referred linear before the curve. Only
   * genuinely bright things — the sun, sunlit snow, sky next to the sun — so that they
   * roll off into structure instead of into a white sheet.
   */
  hiDesatStart: Grade.hiDesatStart,
  hiDesatEnd: Grade.hiDesatEnd,
  hiDesatAmount: Grade.hiDesatAmount,

  // --- grade --------------------------------------------------------------------
  /**
   * Contrast, applied as a **power about the pivot**, not as a slope about it.
   *
   * The obvious `(c - pivot) * k + pivot` subtracts `pivot * (k - 1)` from every pixel —
   * 0.028 of display at these settings — and then the clamp at zero eats it. Measured on
   * the HDR buffer of a canopy frame that put 44% of the frame at code 0 on its own, and
   * it is what the old display-space shadow lift was really compensating for: the lift
   * put a flat 0.030 pedestal back on top of a range that had already been destroyed,
   * which is how 3/4 of a frame ended up inside seven code values.
   *
   * `pivot * pow(c / pivot, k)` has the same slope at the pivot and reaches white within
   * 0.002 of where the linear form does, so the midtone and highlight response other
   * pieces are calibrated against is unchanged — but it is multiplicative, so black stays
   * black and every shadow value keeps its ratio to its neighbour.
   *
   * It is also why this can now be 1.12 rather than 1.07: with the shadow end no longer
   * being subtracted away, contrast buys punch instead of buying a black hole, and the
   * lifted toe in front of the curve wants a slightly firmer S to sit against.
   */
  contrast: Grade.contrast,
  contrastPivot: Grade.contrastPivot,
  /** Saturation applied to near-neutral pixels... */
  satNeutral: Grade.satNeutral,
  /**
   * ...and to already-chromatic ones. The gap is the restraint: saturation is allowed
   * to come from light, not from albedo shouting.
   *
   * 0.88 -> 0.94 on adopting the authority. The intent is unchanged and the palette
   * agrees with it; the argument is that it is now enforced ONCE. Every world albedo is
   * already capped at `PaletteLimits.worldChromaCeiling` 0.075 upstream of here, so a
   * second 12% pull at the display end takes it out of the warm content twice over while
   * the frame's cool majority — sky, haze, rock, shadow — sits near neutral and keeps all
   * of its own. That asymmetry is what "high-key" measures.
   */
  satChromatic: Grade.satChromatic,

  /** Multiplier applied to shadows. Cool: sky-lit, not black. Masked tightly — a wide
   *  shadow mask puts a blue cast over the whole forest, which is not the same thing. */
  shadowTint: [...Grade.shadowTint] as [number, number, number],
  shadowTintAmount: Grade.shadowTintAmount,
  shadowMaskEnd: Grade.shadowMaskEnd,
  /** Multiplier applied to highlights. Warm: sun-lit. */
  highTint: [...Grade.highTint] as [number, number, number],
  highTintAmount: Grade.highTintAmount,
  highMaskStart: Grade.highMaskStart,

  /**
   * Neutral-preserving channel mixer — each row sums to 1, so the white point does not
   * move. Red is cleaned of green and blue so dirt reads as clay rather than as mud;
   * green takes a little red and gives up a little blue, which deepens the firs toward
   * evergreen instead of the pale cyan a naive cool grade turns them into.
   */
  mixR: [...Grade.mixR] as [number, number, number],
  mixG: [...Grade.mixG] as [number, number, number],
  mixB: [...Grade.mixB] as [number, number, number],

  /**
   * Veiling flare — the toe, and the only thing in the chain that sets the black point.
   *
   * **Scene-referred, added after exposure and before the tone curve.** That is the whole
   * point: a constant added *after* the curve is a pedestal, which lifts black and
   * flattens everything just above it by the same amount, so shadow separation is spent
   * paying for the lift. Added *before* the curve it behaves the way real veiling glare
   * does — the curve's toe is convex here, so the slope through the ACES fit at 0.008 is
   * about four times what it is at 0, and shadows land on a steeper part of the transfer
   * function than they started on. Measured on a canopy frame: the display-space version
   * spanned 3–5 code values between the 5th and 50th scene-luminance percentile, this one
   * spans 21, which is what "shadows keep separation" means numerically.
   *
   * Sized against the ACES fit's own zero crossing, which is the thing that actually sets
   * the black point: the rational fit's numerator goes negative below a pre-scaled 0.0033
   * and the clamp takes it to exactly 0, so a scene value under ~0.002 is not dark, it is
   * *gone*. A flare of 0.008 sat right on that crossing and the red channel never cleared
   * it — measured black point 0/0/3, which is worse than no toe at all. 0.022 puts a
   * scene-black pixel on 3/5/11 and the darkest 5% of a canopy frame on 14/22/29: a film
   * black with a sky-lit cast, not a milky one and not a hole.
   *
   * The colour is only mildly blue on purpose. Weighted hard to blue the black goes to
   * 2/6/20, which is not a cool shadow, it is a blue cast on the darkest quarter of every
   * frame — the failure the split-tone below is deliberately restrained to avoid.
   *
   * **And it shipped two thirds of the way there anyway.** `[0.42, 0.52, 0.76]` measures
   * OKLab C 0.0150 at hue 264 — the palette authority's `blackPointChromaMax` is 0.010 —
   * and because this is the one term with no luminance mask, that chroma is on every
   * pixel in the frame before anything else runs. The paragraph above is the correct
   * argument attached to the wrong number, which is a more interesting failure than a
   * missing argument: nobody ever measured the constant against the sentence. The
   * authority's `0.024 * [0.58, 0.62, 0.74]` is C 0.0070 at hue 270 — the same hue, half
   * the chroma, and a slightly larger amount so the black point itself does not drop.
   * `postfx.neutralAxisChroma` is now the invariant that would have caught it.
   *
   * **The occlusion multiply now runs after this add, not before it.** That is not a
   * detail of instruction order, it is what decides whether the occlusion model exists on
   * the screen: at 0.0114 of scene luminance against a 0.020 forest floor, a veil applied
   * *after* the multiply refilled 36% of every occlusion in the frame. See the "ordering"
   * section at the top of this file for the measurement, and note the consequence here —
   * this number can be tuned for the black point alone now, because raising it no longer
   * costs the occlusion any contrast. It only costs the *material* contrast of whatever
   * sits within a stop of it, which is the trade it always should have been.
   */
  flare: Grade.flare,
  flareColor: [...Grade.flareColor] as [number, number, number],

  // --- ambient occlusion: the wide ring -----------------------------------------
  /**
   * World-space radius of the **wide** ring, m — the large-scale sky-occlusion term.
   *
   * This was 1.0 m, and at 1.0 m the ring is not wide, it is a second contact ring with
   * a worse estimator. Measured on the resolved buffer of a forest frame: median wide
   * occlusion 0.004, and above 0.15 on 1.9% of ground pixels. Half the occlusion model
   * was doing nothing, under a direction whose whole thesis is that lighting carries the
   * image. Two things caused that and both are geometric:
   *
   *   1. **Nothing this term is supposed to find is 1 m across.** A tree well is 2–4 m,
   *      the crease where a cut bench meets the hillside is a 3–6 m concavity, a canopy
   *      edge is tens of metres. A 1 m disc placed on a forest floor sees flat ground in
   *      every direction and correctly answers "unoccluded".
   *   2. **The screen clamp made it narrower still.** `aoMaxRadiusPx` was 34 half-res px,
   *      which at 5 m from the camera is a 0.44 m world radius and at 3 m is 0.26 m —
   *      *tighter than the contact ring it was supposed to complement*, exactly where a
   *      rider is looking.
   *
   * 6 m is sized off the features: it spans a tree well, reaches across a bench cut, and
   * still dies inside the width of a clearing so an open hillside does not go grey.
   */
  aoRadius: 6.0,
  /**
   * Wide-ring intensity, in horizon-estimator units (this ring is no longer a
   * scattered-point SAO estimate — see `AO_WIDE_FRAG`). Each direction returns a
   * visibility deficit in [0,1] and the directions are averaged, so 1.0 would take a
   * right-angle interior corner — half the hemisphere blocked — to about 0.5 occlusion.
   *
   * Deliberately modest where the contact ring is deliberately over-driven. A wide term
   * at contact strength is grime over the whole frame; what this one has to do is give
   * the mountain a large-scale value structure, which is a 10–25% multiplier over a broad
   * area, not a line.
   */
  aoIntensity: 1.55,
  /**
   * Angular bias for the wide horizon search, as a sine. ~9 degrees.
   *
   * Half the contact ring's, and it can be: this ring marches at quarter resolution over
   * metres rather than centimetres, so the normal error it is guarding against is a much
   * smaller fraction of the elevations it is measuring. A bias sized for the contact ring
   * would cost a 6 m concavity a third of its signal.
   */
  aoWideBias: 0.16,
  /**
   * Screen-space clamp on the wide ring, **quarter-res** px.
   *
   * This is the number that decides whether the term is wide *in the near field*, which is
   * most of the frame and all of where the rider is looking. At 48 it was not: the clamp
   * bites below 26 m, so ground 4 m away got a 1.1 m effective radius — the same 1 m ring
   * that was measured inert, reached by a different route. 84 quarter-res px is 336 full
   * res, and gives 1.9 m at 4 m, 2.9 m at 6 m and the full 6 m beyond 17 m.
   *
   * It is a large fraction of the frame and that is the correct answer for a term whose
   * job is large-scale structure; the cost is bounded by the quarter-res grid it marches
   * on, not by the reach. Both the falloff radius and the normalisation derive from
   * whatever this clamp leaves, so biting it tightens the term rather than weakening it.
   */
  aoMaxRadiusPx: 84,
  aoMinRadiusPx: 3.0,
  /**
   * Resolvability guard for the wide ring, as local depth gradient over view depth.
   *
   * Looser than the contact ring's, because it reads quarter-res depth: the same surface
   * produces roughly twice the per-texel depth step, so the half-res thresholds would
   * fade the wide term out over most of the forest floor — every texel there has a grass
   * blade or a twig in it.
   */
  aoWidePlanar: [0.11, 0.42] as [number, number],
  /**
   * Scale on the depth prefilter's `(max - min) / median` before it is clamped to 0..1.
   *
   * The raw ratio is what it says: a texel standing on one surface, however steep, reports
   * a few per cent; a texel with needles at 4 m and sky behind them reports a number bigger
   * than 1. 3.0 puts the useful part of that range — the boundary between "a steep bank"
   * and "several surfaces" — in the middle of the 0..1 the two fades below act on, which is
   * the only thing this constant does.
   */
  aoCoherenceK: 3.0,
  /**
   * Where the wide ring stops believing its own **receiver**, as a smoothstep on the
   * clamped spread above. The single knob this round's upstream fix is behind.
   *
   * Measured by A/B in one page session at the stocked stand (`captures/w4post-probe.mjs`,
   * which sweeps `PostTuning` live so a dozen candidates are shot on the identical frame):
   *
   *     gate                    canopy deficit p50   canopy local contrast   bare floor p50
   *     off                            0.200                 7.65                0.090
   *     [0, 0.35]                      0.008                 1.07                0.090
   *     [0, 0.60]                      0.016                 1.42                0.101
   *     [0, 0.35] + occluder gate      0.000                 0.49                0.051
   *
   * The gate removes the canopy's whole contribution and takes its local contrast down by
   * 7x, and costs the bare forest floor — the surface this round is about — nothing at all.
   * The fourth row is why there is no occluder gate: discounting foliage as an *occluder*
   * costs the floor 45% of its deficit, because sparse canopy inside 6 m genuinely does
   * occlude the ground under it even when its depth samples alias. Aliasing is a reason to
   * distrust the receiver, not a reason to disbelieve the occluder.
   *
   * [0.22, 0.62] rather than [0, 0.35] because a hard start at zero puts the fade on the
   * few per cent of spread that even a clean steep hillside produces.
   */
  aoCoherenceFade: [0.22, 0.62] as [number, number],
  /**
   * Where the wide ring hands its near field to the contact ring — **in metres**, at the
   * half-power point of the band-pass.
   *
   * This was a fraction (0.14) of the wide ring's own *clamped* radius, and a fraction is
   * the wrong unit for it. The contact ring's reach is a world constant (0.30 m), so the
   * distance at which the two terms should meet is a world constant too; tying it to a
   * radius that swings between 1.9 m and 6 m with view distance means the meeting point
   * swings with it. Worked through: at 4 m from the camera the clamp leaves 1.9 m, so the
   * handover sat at 27 cm and the pair met correctly — but past 17 m the clamp stops
   * biting, the radius is the full 6 m, and the handover moved out to **84 cm while the
   * contact ring still stopped at 30**. Everything between those two numbers — a stump's
   * root flare, a 60 cm trail-side rock, the lip of a tread cut — fell in a band that
   * neither ring was looking at, in the mid ground, which on a trail is most of what you
   * see.
   *
   * 0.30 m is not a taste value: it is the contact ring's radius, so the wide ring reaches
   * half strength exactly where the contact ring's own inverse-square falloff has dropped
   * to half. Capped at half the wide radius in the shader so a heavily clamped near-field
   * ring cannot hand away its whole reach.
   */
  aoWideNearM: 0.30,
  /** Wide-ring denoise: relative-depth tolerance, the weight of the outer cross relative
   *  to the inner 3x3, and how far out that cross reaches in quarter-res texels. The wide
   *  term is low-frequency by construction, so unlike the contact channel it *wants* the
   *  reach — 2 quarter-res texels is 8 full-res px, which is what takes the six-direction
   *  march's residual rotational structure off it. */
  aoWideBlurTol: 70,
  aoWideBlurFar: 0.55,
  aoWideBlurSpread: 2.0,
  /**
   * Joint bilateral tolerance for lifting the wide buffer onto the half-res grid — the
   * same form as `aoUpsampleTol` and deliberately a third as strict.
   *
   * The contact channel wants 200 because a contact line half a texel out of place beats
   * one that has crossed a silhouette; for a term whose whole content is a soft gradient,
   * the failure modes swap over. At 200 the four quarter-res candidates disagree over any
   * 1% depth step and the upsample collapses to nearest, which puts visible quarter-res
   * stairs into what is supposed to be a gradient. 60 keeps the gradient and still throws
   * out a candidate that belongs to the trunk in front of this pixel.
   */
  aoWideUpsampleTol: 60,
  /** How fast a collapsed bilinear weight reads as "unoccluded" in that upsample. The
   *  weights sum to 1 when all four candidates sit on this pixel's own surface, so 4.0
   *  means a quarter of the weight surviving is still full confidence and a trunk-depth
   *  candidate set (weight ~0.03) delivers almost none of its occlusion to the ground or
   *  sky behind it. */
  aoWideUpsampleTrust: 4.0,
  /**
   * The **ceiling** on how much the wide ring may darken a pixel, after `aoWideShape`.
   *
   * It used to be a gain (1.45) in front of an exponent above 1 (`aoWideCurve` 1.35), and
   * that pair is what this round replaced. The reason is in the shape of the distribution
   * it was applied to, measured on the forest frame with DEBUG_VIEW 7 restricted to the
   * near-field ground and, separately, to the canopy:
   *
   *   region        wide deficit          darkening delivered, display codes
   *   ground        median 0.063, p5 0.286    median **1.3**, p95 9.1
   *   canopy        median 0.24,  p5 0.55     median 2.9,  p95 **18.5**
   *
   * An exponent above 1 spends the model's authority on the top of the distribution, and
   * the top of this distribution is **foliage seen edge-on at quarter resolution**, not
   * tree wells. So the round that added it bought a 50% darkening on canopy speckle and
   * left the ground — every hollow, every bench crease, every tree base, the entire
   * subject of the brief — moving the image by one code value out of thirty. That is the
   * "half the occlusion model is inert" finding, arriving by a route the previous round
   * did not check because it read the *buffer* and the buffer was fine.
   *
   * The replacement transfer (`aoWideShape`) subtracts a floor, gains the middle hard and
   * saturates, so this number means what it says: the most the wide ring can take off any
   * pixel. 0.30 is sized off the one place where *everything* saturates — a fully stocked
   * stand, `floorcam`'s station, where nearly every pixel is genuinely occluded within the
   * ring's 6 m and the term stops being structure and becomes a flat multiply on the whole
   * interior. Measured there, the wide ring costs the forest floor 21% of its display value
   * at the default clock; the frame keeps its deadfall, its trunk separation and a p1 of
   * 4.8 code values, so it is a darker space rather than a crushed one, but this is the
   * number to come back to if a critic reads that frame as unreadable rather than as deep
   * shade.
   */
  aoStrength: 0.50,
  /**
   * The wide ring's transfer: **[floor, knee]** on its visibility deficit.
   *
   *   x   = max(deficit - floor, 0) / knee
   *   occ = aoStrength * x * inversesqrt(1 + x*x)
   *
   * A soft clip, not an exponential, and the difference is the whole of this round.
   *
   * `1 - exp(-16 * d1)` was fitted to a measurement of the *deficit* distribution and never
   * checked against the distribution it produced. It reaches 90% of its ceiling at a deficit
   * of 0.16 — and the forest floor's median deficit, measured on the resolved buffer at the
   * stocked-stand station, is 0.165. Read across the ground's own quantiles (deficit from
   * DEBUG_VIEW 5, authority from DEBUG_VIEW 9 over the same rectangles):
   *
   *     bare-floor quantile   p95    p75    p50    p25    p5     p1
   *     wide deficit          0.01   0.04   0.11   0.20   0.40   0.55
   *     old transfer          0.00   0.31   0.75   0.94   1.00   1.00   <- fraction of ceiling
   *     this transfer         0.00   0.10   0.36   0.62   0.86   0.92
   *
   * The old row is a black point with a rounding error on top of it: three quarters of the
   * forest floor inside a fifth of the model's range, and everything from its median down
   * to its deepest pixel inside 25% of the range. The new one spans the population.
   * Measured on the shipped `todmask` capture, DEBUG_VIEW 9 over a bare-floor rectangle,
   * all six clock times: **p25 12 / median 46 / p75 79 / p95 110 of a 128 ceiling**, against
   * the p25 64 / median 72 / p95 76 of a 76 ceiling this round was sent back for. Still
   * within a code value of itself at every hour, so the previous round's time-of-day fix is
   * intact.
   *
   * `aoStrength` goes 0.30 -> 0.50 alongside, so that the *deep* end of the distribution
   * keeps the weight the old transfer was giving the whole of it. Median linear darkening on
   * the forest floor lands at 18% against the old 23% — slightly less total ink, spent
   * across a range instead of on a pedestal.
   *
   *   - **floor 0.02** — unchanged, and it is not a taste value: the open jump line measures
   *     a median deficit of 0.016, which is the estimator's own residual on a plane.
   *   - **knee 0.225** — the forest floor's own p5 deficit above the floor (0.40 - 0.02 =
   *     0.38) scaled so that `x / sqrt(1 + x^2)` lands it at 0.86 of the ceiling. That is
   *     what "spend the authority on the resolvable part of the distribution" means as an
   *     arithmetic instruction: put the knee at the deep end of the population you can
   *     resolve, not below its median.
   *   - **the shoulder still saturates**, because the top of the distribution is still the
   *     least trustworthy part of it: deficits of 0.5 and 0.8 land at 0.91 and 0.96 of the
   *     ceiling rather than running away with the frame. It is a softer shoulder than the
   *     exponential's because the prefilter upstream (`AO_COHERENCE_FRAG`) has already taken
   *     most of the aliasing out of that tail, so it no longer has to be clamped flat.
   */
  aoWideShape: [0.02, 0.225] as [number, number],
  /**
   * What fully occluded pixels are multiplied toward — the authored **depth**, in a hue
   * that now comes from the light rig rather than from 07:54.
   *
   * The three numbers below still set how dark and how cool a fully occluded pixel goes.
   * What changed is that the shader no longer uses them raw: it keeps their luminance and
   * their departure from neutral, and rotates the hue by the ratio of the ground-bounce
   * chromaticity to the ambient chromaticity taken from `relief.ts`'s shared light block
   * (see `uAoTint` in COMPOSITE_FRAG). Wide occlusion is loss of *sky*, so what is left on
   * an occluded pixel is bounce, and the colour of bounce is a property of the hour.
   *
   * This is the same fault class the round before this one fixed for the mask windows: a
   * constant fitted at one clock time, in a file whose brief is "a grade with an actual
   * point of view, applied consistently across every time of day". At the 07:54 default the
   * rotation is within a couple of percent of these numbers by construction — `aoTintLight`
   * is what decides how far it is allowed to travel from them.
   */
  aoTint: [0.38, 0.44, 0.56] as [number, number, number],
  /**
   * How much of the occlusion tint's hue is taken from the live light block, 0..1.
   *
   * 0 is the old behaviour exactly. 1 would let a strongly coloured dawn sky swing the
   * shadow hue as far as the rig says, which overshoots — the authored tint is not only
   * physics, it is also the grade's point of view. 0.6 is enough that a cold blue morning
   * and a warm evening put visibly different colour in the tree wells, and not enough for
   * the occlusion to stop looking like the same film at both hours.
   */
  aoTintLight: 0.30,
  /** Direct sunlight is not ambient — spare bright pixels this much of the occlusion. */
  aoHighlightRelief: 0.55,

  // --- the two light references, and why the masks are no longer absolute -------
  /**
   * ## The clock problem
   *
   * `aoSunMask` and `aoDarkRelief` decide how much of the geometric occlusion reaches the
   * image. Both were absolute windows in scene-referred luminance, read off the histogram
   * of **one frame at 07:54** — and the sun moves. The mountain's own light swings by
   * more than two stops across a playable day (measured, DEBUG_VIEW 8, forest ground
   * median): 0.014 at 06:24, 0.022 at 07:54, 0.075 at 12:00, 0.027 at 17:36. Against a
   * window that does not move, that means:
   *
   *   - at a low sun the darkest fifth of the forest floor slides into the dark guard and
   *     hands back part of the occlusion the round had just spent on it;
   *   - at noon sunlit ground crosses the top of the sun window and gives up 55% of the
   *     wide term to `aoHighlightRelief`, so the same hollow, unmoved, reads differently
   *     because of the hour;
   *   - and dawn ground that genuinely *is* in direct sun sits below the bottom of the
   *     window, so the relief that exists to protect sunlit surfaces never fires at all —
   *     the mask was, at 06:24, exactly backwards.
   *
   * docs/BAR.md asks for "a grade with an actual point of view, applied consistently
   * across every time of day". A mask fitted to one clock time cannot be that.
   *
   * ## What replaces it
   *
   * Both windows are now expressed against **the light rig**, not against an absolute
   * number, and are recomputed each frame from the shared relief block (`relief.ts` —
   * sun colour x intensity and the sky probe's own irradiance, the same values every other
   * piece shades against):
   *
   *   ambRef = exposure * aoAmbientRefK * luma(sky irradiance)
   *   sunAdd = exposure * aoSunRefK     * luma(key) * sunVis
   *
   * `ambRef` is what ground lit *only* by the sky reads, and `ambRef + sunAdd` is what the
   * same ground reads with the sun on it. Every window below is a position between those
   * two. This is emphatically **not** an auto-exposure: it never looks at a pixel and it
   * never looks at the frame. Point the camera at black timber or at a snowfield and
   * nothing moves; only the sun moving moves it. The grade itself is still the same
   * constant transform at every hour, which is the thing BAR is protecting.
   *
   * The two constants are the fit to the numbers the previous round measured at the
   * default clock, so at 07:54 the windows are within a percent of the values that were
   * shipped and calibrated against — this round changes the *behaviour at other hours*,
   * not the reference frame.
   */
  /**
   * Scene luminance of ambient-lit ground per unit of the relief block's sky term.
   *
   * Fitted, not guessed: the forest floor reads 0.020 (DEBUG_VIEW 6/8) at the 07:54
   * default, where `relief.ts`'s block carries a sky luma of 0.1479 — so 0.1352. Both
   * constants are in **the relief block's own units**, which already carry that library's
   * `keyScale` and `ambientScale`; do not cross-check them against `sky.ts`'s raw
   * `sun.intensity`, which is 3.1x larger. Physically this is the ground's albedo over pi
   * times its mean sky visibility, and it is a property of the material, not of the hour.
   */
  aoAmbientRefK: 0.1352,
  /** ...and the addition direct sun makes, per unit of the block's key luma. Fitted: near
   *  sunlit dirt reads 0.067 against the same 0.020 floor at 07:54, where the block's key
   *  luma is 0.7711, so the sun is worth 0.047 -> 0.0610 per unit. */
  aoSunRefK: 0.0610,
  /**
   * Where a pixel starts and finishes counting as **sunlit**, in units of `sunAdd` above
   * `ambRef`. 0 is ambient-lit ground, 1 is the same ground with the full sun on it.
   *
   * [0.32, 2.34] reproduces the old absolute [0.035, 0.13] at the 07:54 default to within
   * a percent, so nothing about the reference frame moves. What it buys is that the same
   * two positions travel with the sun: at 06:24 the window becomes [0.017, 0.059] and
   * dawn ground in direct sun is finally recognised as sunlit instead of being treated as
   * shade, and at noon it opens to [0.039, 0.147] so a lit hollow keeps a third more of
   * its occlusion than the fixed window left it.
   *
   * The top sits above 1 on purpose: fully sunlit near dirt keeps roughly a third of the
   * relief and only genuinely blazing surfaces — far sunlit hillside, snow — keep all of
   * it. That was true of the old window too; it is now true at every hour.
   */
  aoSunMask: [0.32, 2.34] as [number, number],

  // --- contact ring -------------------------------------------------------------
  /**
   * The tight ring, and the reason anything in this world sits *on* the ground.
   *
   * A single 1 m ring cannot draw contact: by the time its screen radius is clamped it is
   * 80 full-res px wide, its nearest tap is 7 px out, and the denoise it needs to survive
   * 12 taps over that area is exactly the denoise that erases the first 7 px. So this
   * second ring is deliberately small in *screen* space — never more than
   * `aoContactMaxPx` half-res pixels — which means it is densely sampled at every
   * distance, survives a 1-texel denoise intact, and its gradient lands where a contact
   * shadow actually lives: the few centimetres either side of the join.
   *
   * 0.30 m is sized off this world's scatter: a fir trunk is ~0.5 m across, a trail-side
   * boulder 0.6–1.2 m. A contact shadow that reaches a third of the way across the object
   * reads as weight; one that reaches a whole object's width reads as dirt on the lens.
   */
  aoContactRadius: 0.30,
  /**
   * Deliberately over-driven, and it has to be to read as contact.
   *
   * The horizon estimator's honest answer at a rock's base is about 0.5 — half the
   * hemisphere is blocked — and measured, 3.0 put the darkest 0.1% of the resolved buffer
   * at 0.48 occlusion, which multiplies shadowed dirt by 0.83 and reads as a smudge. At
   * 5.0 the innermost band saturates while the r^2 falloff still supplies the gradient
   * outside it, so what lands on the image is a hard dark core with a soft edge — a
   * contact line rather than a gradient that happens to be darkest in the right place.
   * Over-driving a *tight* term is safe in a way that over-driving a wide one is not:
   * the wide ring at this strength is grime over the whole frame, this one is 0.3% of it.
   */
  aoContactIntensity: 5.0,
  aoContactMinPx: 2.0,
  aoContactMaxPx: 16.0,
  aoContactStrength: 1.0,
  /**
   * Contact darkening survives direct sun far better than a general ambient term does —
   * the whole point of a contact shadow is that it is the one place the sky *and* the
   * bounce are both blocked — so this relief is a quarter of the wide ring's.
   */
  aoContactHighlightRelief: 0.22,
  /** Darker and less blue than the wide tint: the millimetres under a rock get neither
   *  sky nor much bounce, and a blue contact line reads as a decal. */
  aoContactTint: [0.30, 0.32, 0.37] as [number, number, number],

  // --- the tyre contacts, analytic ----------------------------------------------
  /**
   * Radius and strength of the analytic disc occlusion at each tyre's contact patch —
   * `rlfContactAt` from `render/shading/relief.ts`, operation 3 of the shared library.
   *
   * Why this is not the contact ring's job even though the contact ring exists. That ring
   * is a horizon march at **half resolution** over a 0.30 m world radius, and a tyre is
   * 60 mm wide: at 3 m from the camera the entire contact patch spans two or three half-res
   * texels, so the estimator has almost nothing to march over and its own 3x3 denoise
   * removes what it finds. It draws the contact under a boulder beautifully and cannot draw
   * the one under a tyre at all. relief.ts's own header names this case exactly — "a stone,
   * a tussock, a wheel... anything sitting on the ground that the shadow cascade is too
   * coarse to resolve" — and the function is a closed form, so it costs ~10 ALU and no taps.
   *
   * **0.26 m is a tyre-scale radius, not a bike-scale one, and that is deliberate.** The
   * wave-3 coherence pass found three systems already darkening the ground under the bike
   * (`bikeModel`'s contact blobs, the sky's analytic rider ellipsoid, this file's contact
   * ring) and the combined result still reading as a sticker. All three are body-sized. A
   * fourth body-sized blob would make that finding worse; what none of them draws is the
   * tyre's own footprint, which is the thing the eye actually uses to decide whether a
   * wheel is *on* the dirt. See this round's report — this system is the right owner for
   * all of it and the other two should come out, but that is a coordination call, not one
   * to make unilaterally inside one file.
   *
   * Set `aoWheelStrength` to 0 to remove the term entirely.
   */
  aoWheelRadius: 0.26,
  aoWheelStrength: 0.50,
  /** How fast each wheel's term follows contact/no-contact, per second. A hard cut pops a
   *  visible ring on and off every time the bike gets light over a roller. */
  aoWheelBlend: 9.0,

  /**
   * Resolvability guard, as local depth gradient over view depth.
   *
   * Half-res depth cannot represent a fir seen through its own needles: every texel is a
   * different surface, so the estimator returns noise and the denoise cannot tell noise
   * from contact. Below `[0]` the surface is flat enough to trust; above `[1]` the
   * occlusion is faded out entirely. Ground next to a trunk stays well under the floor,
   * because the normal reconstruction already takes the *smaller* depth step on each
   * axis and this reads the same two steps.
   */
  aoPlanarFade: [0.055, 0.20] as [number, number],
  /**
   * Angular bias for the horizon search, as a sine. ~17 degrees.
   *
   * Much larger than the metric bias the wide ring uses, and it has to be. On ground seen
   * at a grazing angle the sample vector lies almost in the tangent plane, so `dot(v, n)`
   * is a small residual of two large numbers and a normal rebuilt from four depth taps is
   * off by more than that residual — measured, a 6-degree bias left the whole near-field
   * ground striped with the march's own jitter pattern, because the estimator was
   * resolving its own normal error. 17 degrees costs a vertical occluder 30% of its
   * signal, which is a constant the intensity absorbs, and costs a grazing surface all of
   * its noise, which nothing else could remove.
   */
  aoHorizonBias: 0.30,

  /**
   * Denoise: relative-depth tolerance, contact centre weight, diagonal tap weight.
   *
   * The centre weight is what a denoise costs a feature one texel wide, and the old
   * kernel charged 7.6x for it. Here the wide channel pays 6.2/1 — it is a soft term and
   * wants the smoothing — while the contact channel pays 9.7/4.5, a bit over 2x, because
   * the horizon estimator it is filtering is already smooth in the per-pixel rotation and
   * only needs the residual march jitter taken off. Measured on a boulder base, raw
   * contact occlusion peaks at 0.98 and survives the resolve at 0.87.
   */
  aoBlurTol: 90,
  aoBlurCentre: 4.5,
  aoBlurDiag: 0.30,
  /**
   * Joint bilateral upsample tolerance, in reciprocal relative-depth units. A candidate
   * texel whose own view depth differs from this pixel's by a fraction `e` of the pixel's
   * depth keeps `1/(1 + e * this)` of its bilinear weight, so at 200 a 1% depth gap — a
   * different surface at any distance — is down to a third, and a 5% gap to a fourteenth.
   * Where all four candidates disagree the filter degenerates to nearest, which is the
   * right failure: a hard edge in the wrong place by half a texel beats a soft one that
   * has crossed the silhouette.
   */
  aoUpsampleTol: 200,
  /**
   * Floor below which the occlusion fades out — now **as a fraction of `ambRef`**, the
   * scene luminance of ground lit by the sky alone this frame.
   *
   * The absolute form of this went [0.015, 0.10], then [0.004, 0.022], then
   * [0.0008, 0.005], each round measuring the forest floor again and moving the window
   * down to get out from under it. The third one was right at 07:54 and wrong at 06:24 for
   * the same reason the first two were wrong at 07:54: the floor is not a constant, it is
   * whatever "unlit" means under the light that is actually up. At 06:24 the sky is 0.54x
   * what it is at the default, so a fixed window sits half a stop higher on the frame's own
   * distribution than it was ever meant to and starts eating occlusion off the darkest
   * ground in the game.
   *
   * [0.04, 0.25] of ambient-lit ground is 4.6 to 2 stops below it, and reproduces
   * [0.0008, 0.005] at the default clock. What it guards against is unchanged: multiplying
   * an ambient term into a pixel that has no ambient on it. What changes is that the guard
   * now means the same thing at every hour.
   */
  aoDarkRelief: [0.04, 0.25] as [number, number],

  /**
   * The two rings' feature sizes, metres — and, through `relief.ts`'s LOD policy, the only
   * thing that now decides how far each of them reaches.
   *
   * There used to be one distance window here, `aoFadeStart 42 / aoFadeEnd 78`, shared by
   * both rings. That is `relief.ts` rule 5 broken in the one way the rule names: "do not
   * invent your own distance ramp; if yours differs from everyone else's the mountain will
   * visibly change materials as you ride down it". Worse, one ramp cannot be right for two
   * terms whose features are 20x apart in size:
   *
   *   - a 0.30 m contact shadow stops being resolvable at half res around 65 m, so a window
   *     that only *starts* fading at 42 and finishes at 78 was still asking the estimator
   *     for a feature it could no longer see — which is noise, denoised into grime;
   *   - a 6 m concavity is perfectly resolvable at 200 m and still readable at 500, and the
   *     same window deleted it at 78. That is most of the distance band the standing
   *     critique describes as "one flat wash where no gully, rib or spur carries any shade".
   *
   * So each ring now fades on `rlfAlbedoAmp( rlfLodManual( z, z / pxu ), 1 / feature )` —
   * the library's Nyquist policy evaluated at that ring's own feature frequency and that
   * pass's own resolution — and the CPU-side cull that has to agree with it is solved from
   * the library's CPU mirror in `ringCull()`. Measured at 1600x900, fov 60: contact full to
   * 20 m and gone by 65; wide full to 202 m and gone by 651.
   *
   * These are deliberately *the same numbers* as `aoContactRadius` and `aoRadius`. They are
   * separate fields only because "the size of the thing this ring is looking for" and "how
   * far the ring reaches on screen" are separate ideas, and a future round may want to
   * resolve a 6 m ring's 2 m sub-structure.
   */
  aoContactFeatureM: 0.30,
  aoWideFeatureM: 6.0,
  /**
   * Absolute backstop on either ring's reach, metres.
   *
   * The LOD policy above is a Nyquist argument and it is happy to run the wide ring to 650 m.
   * That is correct for the picture and it is thirty taps per quarter-res texel over a frame
   * that may be mostly distant hillside. This caps the marched region; past it the ring
   * early-outs to "unoccluded", which at that distance costs the image nothing that aerial
   * perspective is not already doing.
   *
   * The shader fades to it over the last 28% rather than stopping at it, so the budget
   * cannot put a ring of "occlusion ends here" across a distant hillside. 360 m is 4.6x the
   * reach the old shared window allowed and covers the whole 100-300 m band the standing
   * mid-distance critique is about; the cost of the extra marched area is bounded by the
   * quarter-res grid — see the note on cost in the header.
   */
  aoCullM: 360,

  // --- bloom --------------------------------------------------------------------
  /** Scene-referred threshold. The clear-sky horizon sits near 1.6, so this catches
   *  the sun, specular glints and sky seen *through* canopy, and not the sky itself. */
  bloomThreshold: 1.45,
  bloomKnee: 0.55,
  /** Deliberately small. Sun through trees, not a glow over everything. */
  bloomIntensity: 0.055,
  /** Weight of the wide (1/8) level relative to the tight (1/4) one. */
  bloomWide: 0.75,

  // --- motion blur --------------------------------------------------------------
  /**
   * Shutter fraction. 0.5 is a 180-degree shutter; this is 252 degrees.
   *
   * Deliberately past physical, and the measurement that decided it is the one below about
   * the gate. The MAVRIX chase reference dissolves its whole near foreground while the rider
   * stays sharp, and a 180-degree shutter at the speeds this game reaches does not get
   * close. Shot on one pinned 50 km/h frame (`captures/w4post-r3-mb5`), 0.50 -> 0.70 with
   * the gate below is the step where the near ground stops reading as "slightly soft" and
   * starts reading as moving; 0.85 with a wider gate is the step after that, and it is where
   * the tap ladder starts showing as discrete copies on thin grass. So: 0.70.
   */
  shutter: 0.70,
  /**
   * Speed, m/s, at which blur starts and at which it is fully in.
   *
   * `blurSpeedFull` was 20 m/s = 72 km/h, and **nothing in this game goes that fast under
   * its own power**: `descend`, the quickest scenario in the harness, peaks at 55.1 km/h.
   * So the gate was holding the smear at `smoothstep(6, 20, 13.9) = 0.60` at the fastest
   * frame anyone would ever see, and the round before this one answered "the blur is lighter
   * than the reference" by raising the CEILING — which is the one parameter that provably
   * could not move that frame. Measured: 0.045 -> 0.085 on an identical 50 km/h frame is
   * indistinguishable in the corner crops (`captures/w4post-r3-mb4`), because the clamp was
   * never what was binding.
   *
   * 14 m/s = 50 km/h is full smear at a speed the game actually reaches. The bottom of the
   * ramp does not move: 6 m/s is 21.6 km/h and a trail bike at 20 km/h is still not a thing
   * you want smeared. What changes is the middle — 30 km/h goes from 7% of the budget to
   * 20% — and that is the band the reference shots are actually in.
   */
  blurSpeedIn: 6.0,
  blurSpeedFull: 14.0,
  /**
   * Hard ceiling on smear length as a fraction of frame height, **at the edge of the
   * frame**. With `blurCentreRetain` below, this is an edge number, not a whole-frame one.
   *
   * ## What this knob is NOT, which cost the round before this one its whole change
   *
   * It went 0.0072 -> 0.021 -> 0.045 across three rounds, each time on the argument that
   * the MAVRIX chase reference smears its periphery into streaks and ours does not. The
   * argument is right and the knob was wrong: this is a CLAMP, and a clamp only does
   * something on pixels that were going to exceed it. Measured on one pinned 50 km/h frame
   * with everything else held (`captures/w4post-r3-mb4`), 0.045 -> 0.085 is
   * indistinguishable in the bottom-left corner crop — the near ground at 50 km/h was
   * already inside the old ceiling, because the speed gate had two fifths of the smear
   * switched off. See `blurSpeedFull`, which is what was actually binding and is what moved
   * this round.
   *
   * 0.085 is 76 px at 900 and 92 px at 1080, and with `shutter` at 0.70 and the gate full
   * at 50 km/h it now genuinely binds — near ground at the frame edge above about 60 km/h.
   * Paired with a centre retain of 0.055 the middle of the frame gets 4.2 px, which is
   * where the subject has been for four rounds; everything that moved, moved outside
   * r = 0.16. Read `captures/w4post-r3-mb5/` rather than taking that on trust.
   */
  blurMaxFrac: 0.085,
  /**
   * Second ceiling on the same smear: **pixels per tap**, so a longer streak cannot be
   * asked of a shorter tap ladder.
   *
   * `blurMaxFrac` is a look decision and `TIER_SPEC[t].mb` is a cost decision, and they
   * multiply into an artefact neither of them can see on its own. The ladder is
   * `2 * MB_TAPS + 1` samples spread over the smear, so lengthening the smear without
   * touching the taps widens the gap between them — and past roughly 5 px, jittered or not,
   * a high-contrast edge stops reading as a streak and starts reading as discrete ghosts of
   * itself. That is visible in this round's own sweep: the `gate12sh085` candidate in
   * `captures/w4post-r3-mb5/` shows the grass tufts as repeated copies, and it is the reason
   * that candidate was not taken.
   *
   * Tier 3 now has 8 taps (17 samples, an 85 px allowance, which does not bind at 76),
   * tier 2 has 4 and tier 1 has 3. The tap count went up because this round could finally
   * price it: 4 -> 8 taps measured **+0.009 ms** at 1080p on the slope instrument, against a
   * 0.64 ms chain and a 2 ms budget. See `mbTaps`.
   *
   * Set high to disable.
   */
  blurMaxPxPerTap: 5.0,
  /**
   * Override on the tier's motion-blur tap ladder. -1 takes `TIER_SPEC[t].mb`.
   *
   * A separate knob from `blurMaxFrac` because the two are separate costs, and conflating
   * them is what produced the claim this round had to disprove ("this round roughly doubled
   * the motion-blur smear, a fill-rate change at 1080p"). The composite takes
   * `2 * MB_TAPS + 1` samples along the smear **whatever its length is**, so lengthening
   * the streak changes where the taps land and not how many there are. Measured at 1080p by
   * the slope instrument (`captures/w4post-bench.mjs`), on the identical frame:
   *
   *     blurMaxFrac   0     0.021   0.045   0.72 + no per-tap cap
   *     chain ms      0.515 0.522   0.518   0.513
   *
   * — i.e. a **16x** change in smear length costs nothing measurable, because the tap count
   * did not move. The number that does cost is this one, and it is now priceable on its own.
   */
  mbTaps: -1,
  /**
   * Blur retained in the middle of the frame, where the trail you are about to ride is.
   * The edges take the full amount, which is where speed is legible anyway.
   *
   * Held at 0.055 through three ceiling changes on purpose: it is the only thing protecting
   * the subject, and BAR's line on this piece is "motion blur that reads as speed and does
   * not smear the trail into mud". At the shipped 0.085 edge that is 4.2 px in the middle of
   * a 900-line frame — the tread under the wheel and the next feature up the trail stay
   * readable in every frame of `captures/w4post-r3-mb5/`, which is the check that matters.
   */
  blurCentreRetain: 0.055,
  /**
   * Where the radial ramp from centre to edge starts and finishes, as a fraction of the
   * aspect-corrected radius (1.02 at the corner of a 16:9 frame).
   *
   * Widened from [0.10, 0.62] so the sharp island in the middle is a real island — a third
   * of the frame width rather than a fifth — and the ramp to full smear lands just inside
   * the corners rather than 40% of the way out. A pow above 1 on the ramp keeps the
   * transition off the subject: at the reference's framing the rider sits at r ~ 0.2.
   */
  blurRadialRamp: [0.16, 0.86] as [number, number],
  blurRadialShape: 1.5,
  /**
   * How far the streak is **rotated** onto the frame's outward axis at full speed, 0..1.
   *
   * This replaces `blurRadial`, which added `rel * k` to the velocity. Same intent — the
   * MAVRIX chase reference's blur is unmistakably outward and reprojection's own focus of
   * expansion wanders off-frame-centre through a corner — but the additive form pays for it
   * with smear on things that are not moving. Measured at 50 km/h on one pinned frame
   * (`captures/w4post-r3-mb3`), at 0.024 and 0.030 the CLOUDS in the top-left corner come
   * back streaked; the sky has no velocity and had no business being blurred.
   *
   * A rotation cannot do that: `mix(vel, outward * |vel|, k)` is identically zero wherever
   * `vel` is, so the sky and the far ridge stay sharp at k = 1, while a fast near-ground
   * pixel keeps its own smear length and gets it pointed outward. That decouples "how
   * radial" from "how much", which is what let the edge ceiling go up this round without
   * the periphery filling with speed lines.
   *
   * 0.45 reads as clearly outward against the reference without overriding a genuine
   * sideways flow through a berm. 0 is pure reprojection.
   */
  blurRadialBlend: 0.45,

  // --- trimmings ----------------------------------------------------------------
  vignette: 0.16,
  vignetteAtSpeed: 0.10,
  /** Radial chromatic fringing, at speed only. Fractions of frame width at the corner. */
  chromatic: 0.0016,

  /** Multisampling on the HDR scene target, or -1 to take the tier's own value. Exists
   *  so the sample count can be priced independently of the rest of the chain. */
  msaaOverride: -1,

  // --- the tier governor ----------------------------------------------------------
  //
  // Every constant here is about the CONTROLLER, not about the picture. Read the
  // "Degradation" section of the header before moving any of them: this block shipped a
  // defect for four waves that removed the authored look from 99.1% of the frames the
  // project grades itself on, and the constants below are the shape of the fix.

  /** Measured median frame period above which the chain may step down a tier, ms. */
  degradeMs: 20.5,
  /** ...and below which it may step back up. */
  upgradeMs: 12.0,
  /**
   * How long the median has to stay over `degradeMs` before a step down, ms.
   *
   * 3000, up from 1200. A step down costs the authored look and — measured on this GPU —
   * buys at most 0.3 ms, so the asymmetry between a false positive and a slow reaction is
   * enormous and the hold time should reflect it. Three seconds is a machine that is
   * genuinely over budget, not a scene load, a shader compile or a GC pause.
   */
  degradeHoldMs: 3000,
  /** How long the median has to stay under `upgradeMs` before a step up, ms. */
  upgradeHoldMs: 6000,
  /**
   * Frame periods above this are DISCARDED rather than clamped, ms.
   *
   * A sample this long is not a frame: it is a background tab throttled to 1 Hz, a
   * screenshot readback in the capture harness, a devtools breakpoint, or the driver
   * flushing a queue. Feeding any of them to a quality controller is how a page that was
   * merely in the background comes back with the look turned off. 250 ms is 4 fps — below
   * that, which tier the post chain is on has stopped being the question.
   */
  frameRejectMs: 250,
  /**
   * Frames in the governor's rolling window. 120 at 60 fps is ~2 s of history.
   *
   * Measured on this box with nine agents on it, a 64-frame window's median swung between
   * 1.8 and 24.7 ms at an unchanged load, because the frame distribution here has a heavy
   * tail (trial E of `captures/w5post-tools/gov.mjs`: p05 1.0, median 6.7, p95 36.3 ms).
   * A median is robust to a tail, but not when a third of the window IS the tail. 120
   * frames is the shortest window whose median was stable enough to act on.
   */
  governorWindow: 120,
  /** Samples required before the median means anything and the controller may act. */
  governorMinSamples: 40,
  /**
   * After a step down, how long to wait before judging whether it helped, ms.
   *
   * Must be longer than the window (`governorWindow` frames) so the median being judged
   * contains no pre-step frames. 2500 ms is ~150 frames at 60 Hz against a 120-frame
   * window.
   */
  tierVerifyMs: 2500,
  /**
   * How much of the frame a step down has to give back to be kept — as a fraction, and
   * as an absolute floor in ms. The step is kept only if it clears BOTH.
   *
   * THIS IS THE CONSTANT THAT STOPS THE DEFECT COMING BACK IN ANOTHER FORM. The measured
   * ladder (header) is weak: the whole of it is 47.5% of a chain that costs 0.68 ms at
   * 1080p canopy on an M4 Max, because the composite runs at every tier including 0. So a
   * governor that steps down and does not check has no way to tell "the chain was the
   * bottleneck" from "the frame is slow for a reason that is not this piece" — and in the
   * second case it has traded the whole authored look for nothing. It checks.
   *
   * SAY THE CONSEQUENCE OUT LOUD RATHER THAN LET SOMEONE DISCOVER IT: measured at 1080p
   * canopy on this GPU, tier 3 is 0.681 ms and tier 1 is 0.457 ms, so the whole first step
   * of the ladder is worth **0.224 ms** — under `tierVerifyMinMs`. **On hardware this fast
   * the governor is therefore inert by construction: it will notice an over-budget frame,
   * try a step, measure that the step did not buy 0.25 ms, and put it back.** That is the
   * correct answer here and it is the honest reading of the ladder's own numbers — the
   * chain is 11% of this machine's frame and no rearrangement of it fixes a frame that is
   * slow for another reason. The governor only has real work to do where the chain is a
   * large share of the frame, i.e. where BAR's F (see the integrated-GPU section) is 3 or
   * more, and that is hardware nobody in this loop can reach.
   */
  tierVerifyGain: 0.05,
  tierVerifyMinMs: 0.25,
  /**
   * How long a failed step down suppresses the next one, ms, and its ceiling. Doubles on
   * each consecutive failure so a machine whose slowness is elsewhere stops re-testing
   * within a minute instead of flapping the look on and off for the whole session.
   */
  tierBackoffMs: 20000,
  tierBackoffMaxMs: 120000,
};

const REF_DT = 1 / 60;

// ---------------------------------------------------------------------------
// THE GRADE'S OWN INSTRUMENT, and the invariants it feeds.
//
// The project's defining risk is that a metric is structurally unable to see the defect
// it is watching for. So this block states, up front, what defect each of these three
// can see and how that was demonstrated rather than asserted. `captures/w5post-instr.mjs`
// runs the demonstration; its output is quoted in the round's report.
//
//   postfx.neutralAxisChroma  — pushes a scene NEUTRAL through this file's own display
//     transform and measures the OKLab chroma it comes out with. A grade with a point of
//     view is supposed to put colour on a neutral (cool shadows, warm key); what it must
//     not do is put ONE colour on every level, which is a cast. Fed the constants this
//     file shipped before the palette adoption it returns C 0.0153 at hue 267 on scene
//     0.004, over the authority's 0.010 ceiling; fed the adopted ones it returns 0.0077.
//     That is the same number the palette derived independently, from its own model, and
//     the two agreeing is the cross-check — one is a mirror of the shader, the other is a
//     mirror of the parameter block.
//
//   postfx.gradeDrift — every grade constant against `Grade`, every frame. It exists
//     because this file's tuning object is deliberately live-mutable (that is how the
//     grade was converged) and the harness shares one page across every scenario. The
//     wave-4 failure where `aodebug` left DEBUG_VIEW=1 on and a whole --all run
//     screenshotted the raw occlusion buffer is the same shape: a probe mutates, a later
//     scenario inherits it, and the frames look authored. Demonstrated by mutating one
//     constant by 1e-3 and watching the invariant fire.
//
//   postfx.renderTier — the tier the frame actually rendered at, against the tier the
//     look is authored for. The frame-time governor steps this down on its own, and every
//     picture a critic grades after that is a different chain. Nothing anywhere reported
//     it. Demonstrated by pinning tier 1 and watching it fire at ratio 3.
// ---------------------------------------------------------------------------

/**
 * The CPU mirror of `COMPOSITE_FRAG`'s `displayTransform`, plus the veiling flare that
 * runs immediately before it.
 *
 * It reads `PostTuning`, NOT `Grade` — the point is to measure the shader that ships,
 * including any live mutation, rather than the authority it is supposed to equal. The
 * palette has its own mirror (`applyGrade`) which reads `Grade`; the two are deliberately
 * separate implementations of one transform, so agreement between them is evidence and
 * not tautology.
 *
 * Order is load bearing and is copied line for line from the shader: flare, highlight
 * desaturation, tone curve, contrast-as-a-power about the pivot, split tone masked on
 * DISPLAY luminance, chroma-dependent saturation, channel mixer. The palette's own header
 * records getting this order wrong once — masks read off scene luminance instead of
 * display — and how much it moved the answer.
 *
 * Returns display-linear, the value just before `srgb()`.
 */
function gradeNeutral(scene: number): { L: number; a: number; b: number; C: number; h: number } {
  const T = PostTuning;
  const LR = 0.2126, LG = 0.7152, LB = 0.0722;
  let r = scene + T.flareColor[0] * T.flare;
  let g = scene + T.flareColor[1] * T.flare;
  let b = scene + T.flareColor[2] * T.flare;

  const hl = LR * r + LG * g + LB * b;
  const kd = smoothstep(T.hiDesatStart, T.hiDesatEnd, hl) * T.hiDesatAmount;
  r += (hl - r) * kd; g += (hl - g) * kd; b += (hl - b) * kd;

  const curve = (x: number): number => {
    const c = Math.max(x, 0) * T.curvePreScale;
    const n = c * (c + 0.0245786) - 0.000090537;
    const d = c * (0.983729 * c + 0.4329510) + 0.238081;
    return Math.pow(Math.min(1, Math.max(0, n / d)), T.toeGamma);
  };
  r = curve(r); g = curve(g); b = curve(b);

  const pv = T.contrastPivot;
  r = pv * Math.pow(Math.max(r, 1e-5) / pv, T.contrast);
  g = pv * Math.pow(Math.max(g, 1e-5) / pv, T.contrast);
  b = pv * Math.pow(Math.max(b, 1e-5) / pv, T.contrast);

  let l = LR * r + LG * g + LB * b;
  const sa = (1 - smoothstep(0, T.shadowMaskEnd, l)) * T.shadowTintAmount;
  const ha = smoothstep(T.highMaskStart, 1, l) * T.highTintAmount;
  r *= (1 + (T.shadowTint[0] - 1) * sa) * (1 + (T.highTint[0] - 1) * ha);
  g *= (1 + (T.shadowTint[1] - 1) * sa) * (1 + (T.highTint[1] - 1) * ha);
  b *= (1 + (T.shadowTint[2] - 1) * sa) * (1 + (T.highTint[2] - 1) * ha);

  l = LR * r + LG * g + LB * b;
  const chroma = Math.hypot(r - l, g - l, b - l) * 1.35;
  const sat = T.satNeutral + (T.satChromatic - T.satNeutral) * smoothstep(0, 0.40, chroma);
  r = l + (r - l) * sat; g = l + (g - l) * sat; b = l + (b - l) * sat;

  const or_ = Math.max(T.mixR[0] * r + T.mixR[1] * g + T.mixR[2] * b, 0);
  const og = Math.max(T.mixG[0] * r + T.mixG[1] * g + T.mixG[2] * b, 0);
  const ob = Math.max(T.mixB[0] * r + T.mixB[1] * g + T.mixB[2] * b, 0);
  // Left where the caller can read it, so `gradePedestal` below does not need a second
  // copy of this transform. Module scope, written in place: this runs on the build path
  // and on one frame in FRAME_PERIOD_S, and the bus is explicit about allocation.
  _gradeRGB[0] = or_; _gradeRGB[1] = og; _gradeRGB[2] = ob;

  const lab = linearToOklab(or_, og, ob);
  let h = Math.atan2(lab[2], lab[1]) * 180 / Math.PI;
  if (h < 0) h += 360;
  return { L: lab[0], a: lab[1], b: lab[2], C: Math.hypot(lab[1], lab[2]), h };
}

/**
 * The scene levels the neutral axis is sampled at — the palette's own table, so the two
 * files' numbers are directly comparable. 0.004 is a canopy shadow, 0.25 the pivot, 1.50
 * a sunlit highlight.
 */
const NEUTRAL_LEVELS = [0.004, 0.02, 0.08, 0.25, 0.70, 1.50];

/** `gradeNeutral`'s display-linear output, left for `gradePedestal`. */
const _gradeRGB = [0, 0, 0];
/** The pedestal in 8-bit codes, rewritten on each call to `gradePedestal`. */
const _pedestal: [number, number, number] = [0, 0, 0];

/**
 * WHAT THE GRADE PUTS UNDER A SCENE-BLACK PIXEL, in the codes a frame is measured in.
 *
 * The disambiguator for `postfx.frameBlackPoint`, and it exists because the first
 * capture with that invariant in it produced a number that could have been read two
 * opposite ways. On `fullrun`, 28 of 48 frames came back with a 1st-percentile luma above
 * the authored band — worst 0.106 — which reads as "the grade is milky". It is not: the
 * per-channel floor on that frame is 28/27/27 and the grade's own pedestal is 8/11/15, so
 * the frame simply contains nothing dark. An open sunlit traverse HAS no black in it.
 *
 * Both readings matter and they route to different owners, so both numbers are put in the
 * label rather than one being inferred from the other:
 *
 *   floor ≈ pedestal  ->  the frame reaches the grade's floor. p1 is then a statement
 *                         about the GRADE, and it is postfx's.
 *   floor >> pedestal ->  the frame never gets near the floor. p1 is a statement about
 *                         what was in shot and about the light in it, and it is not.
 *
 * Returns the encoded luma of the pedestal; `_pedestal` holds its three codes.
 */
function gradePedestal(): number {
  gradeNeutral(0);
  let lum = 0;
  const w = [0.2126, 0.7152, 0.0722];
  for (let i = 0; i < 3; i++) {
    const v = Math.min(1, Math.max(0, _gradeRGB[i]));
    const e = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    _pedestal[i] = Math.round(e * 255);
    lum += w[i] * e;
  }
  return lum;
}

/**
 * The tier the look is authored at, and the one every beauty frame in this project has
 * ever been graded on. Declared here as a literal rather than as `TIER_SPEC.length - 1`
 * because `TIER_SPEC` is a `const` further down the module and this block runs first.
 */
const TIER_TARGET = 3;

const INV_NEUTRAL = declareInvariant({
  id: 'postfx.neutralAxisChroma',
  owner: 'postfx',
  what: 'colour the grade puts on a scene neutral, worst level on the axis',
  unit: 'oklabC',
  budget: GradeTargets.neutralAxisChromaMax,
  compare: 'max',
  severity: 'error',
  phase: 'build',
  consequence: 'a cast on every pixel in the frame instead of cool shadows and a warm key',
  source: 'src/render/palette.ts GradeTargets.neutralAxisChromaMax',
});

const INV_BLACK_POINT = declareInvariant({
  id: 'postfx.blackPointChroma',
  owner: 'postfx',
  what: 'colour of the veiling flare, the one grade term with no luminance mask',
  unit: 'oklabC',
  budget: GradeTargets.blackPointChromaMax,
  compare: 'max',
  severity: 'error',
  phase: 'build',
  consequence: 'a blue pedestal under the darkest quarter of every frame',
  source: 'src/render/palette.ts GradeTargets.blackPointChromaMax',
});

const INV_GRADE_DRIFT = declareInvariant({
  id: 'postfx.gradeDrift',
  owner: 'postfx',
  what: 'largest difference between a shipping grade constant and the palette authority',
  unit: 'frac',
  budget: 1e-9,
  compare: 'max',
  severity: 'error',
  phase: 'run',
  consequence: 'the frames being graded were shot through a look no author signed off',
  source: 'src/render/palette.ts Grade',
});

/**
 * THE COST INVARIANT, and the reason it is stated in fill rather than in milliseconds.
 *
 * BAR gives this piece ~2 ms at 1080p and names 60 fps at 1080p **on integrated graphics**
 * as the floor. No integrated GPU is reachable from this machine, so a millisecond budget
 * declared here would be a budget against hardware nobody in this loop has — which is
 * exactly the shape of the five measurement failures this wave exists to stop.
 *
 * What IS measurable here, exactly and for free, is the quantity that decides the cost on
 * a fill-bound device: how many full-resolution passes' worth of pixels the chain writes.
 * Measured on this GPU (`captures/w5post-bench.mjs`, resolution sweep at a stocked stand),
 * the chain is per-render-pass-constant bound — 1.48x the time for 2.25x the pixels — so
 * milliseconds here are a statement about Metal's encode cost and do not transfer. Fill
 * does: an integrated GPU pays the same pixel count at whatever its own fill rate is.
 *
 * The budget is this file's own architectural claim, made checkable: "there is exactly one
 * full-resolution read/write in the whole chain besides the scene draw itself, which is
 * what makes the budget reachable" (header). One full-res composite plus the half- and
 * quarter-res ladder sums to 1.87 at tier 3; 2.10 leaves room for the ladder to be retuned
 * and none for a second full-resolution pass, which is the thing that must not appear
 * without someone arguing for it.
 */
const INV_FILL = declareInvariant({
  id: 'postfx.fillPasses',
  owner: 'postfx',
  what: 'pixels the chain writes per frame, in units of one full-resolution pass',
  unit: 'passes',
  budget: 2.10,
  compare: 'max',
  severity: 'error',
  phase: 'run',
  consequence: 'the frame cost on the integrated GPU BAR names as the floor, where this term is what is paid',
  source: 'src/render/post.ts INV_FILL',
});

const INV_TIER = declareInvariant({
  id: 'postfx.renderTier',
  owner: 'postfx',
  what: 'post-processing tier the frame actually rendered at',
  unit: 'tier',
  budget: TIER_TARGET,
  compare: 'min',
  severity: 'warn',
  phase: 'run',
  consequence: 'occlusion, bloom and blur are silently absent from the frames being graded',
  source: 'src/render/post.ts TIER_SPEC',
});

/**
 * THE SECOND HALF OF THE SAME QUESTION, AND THE REASON IT IS A SEPARATE INVARIANT.
 *
 * `postfx.renderTier` says the frame rendered below the authored tier. It CANNOT say why,
 * and the two whys want opposite responses:
 *
 *   - a probe pinned tier 1 to price the ladder, or the device genuinely cannot run tier 3
 *     (no float render target). Working as designed; nothing to fix.
 *   - the frame-time governor took it away on its own. That is the defect that removed the
 *     look from 99.1% of the gate run's 5154 frames, and the ONE number that separates it
 *     from the case above is "how many tiers has the governor taken and not given back".
 *
 * Budget 0: on any machine that is keeping up, the governor should be taking nothing. When
 * it fires, `where` names the median frame time it acted on and whether the step down
 * survived its own verification, so the next reader starts from the measurement rather
 * than from a screenshot that looks flat.
 */
const INV_GOVERNOR = declareInvariant({
  id: 'postfx.governorTierLoss',
  owner: 'postfx',
  what: 'tiers the frame-time governor has taken away and not given back',
  unit: 'tier',
  budget: 0,
  compare: 'max',
  severity: 'warn',
  phase: 'run',
  consequence: 'the authored chain was removed by a controller, not by an author, and every frame graded after it is a different chain',
  source: 'src/render/post.ts PostTuning.degradeMs',
});

// ---------------------------------------------------------------------------
// THE FRAME INSTRUMENT — the one that reads a pixel.
//
// ## Why this exists, stated as the gap it closes
//
// Every invariant above certifies the chain's CONFIGURATION analytically. `renderTier` and
// `governorTierLoss` read `this.tier`; `fillPasses` reads a pixel counter; `gradeDrift`
// compares two constant tables; `neutralAxisChroma` and `blackPointChroma` push a SYNTHETIC
// neutral through a CPU mirror of the shader. Not one of them samples the framebuffer. So
// this piece — which owns the last pass over every pixel, and which BAR says "carries more
// weight than usual" for whether the image reads as authored — reported six green lines on
// captures whose frames could not have supported the claim. That is the whole gap, and it
// is the same failure class as `overTravel` (structurally blind to the bike sinking) and
// the camera's depthless green-pixel count: a metric that cannot see its own subject.
//
// `terrainMaterial.ts`'s `nearClassSeparationL` already reads the finished frame in this
// codebase, so the capability was here; postfx just was not using it. This block is the
// same shape — read the canvas post owns, reduce it to a distribution, publish.
//
// ## What it measures, and where each budget comes from
//
// All four budgets are `palette.ts`'s `GradeTargets`, which is explicit that "a parameter
// block is not evidence that a picture looks right" and names the frame statistics the
// grade must produce. Until now nothing measured them.
//
//   postfx.frameBlackPoint      display luma at the 1st percentile.  Band [0.030, 0.070].
//                               Carries the grade's own pedestal beside it, because the
//                               two readings of a high p1 have different owners — see
//                               `gradePedestal`.
//   postfx.frameWhitePoint      display luma at the 99th.            Max 0.94.
//   postfx.frameClipped         share of the frame at or past 0.98.  Max 0.005.
//   postfx.frameToneOccupancy   how much of the frame's OWN tonal range carries pixels.
//   postfx.frameAuditMs         this instrument's own bill, on the bus with everything else.
//
// ## The fourth one is the answer to "green on two flat colour fields"
//
// The first three are properties of the grade. `frameToneOccupancy` is a property of the
// EVIDENCE: it is the fraction of 32 bins spanning the frame's own [p1, p99] that carry at
// least 0.5% of the pixels. Normalising by the frame's own range rather than by [0, 1] is
// the whole design — a legitimately low-key canopy frame spans 0.031 to 0.235 and still
// fills 0.72 of its own range, while a frame that is two flat colour fields scores 2/32 =
// 0.06 however far apart the two fields are. Measured across `vista`, `todvista` and
// `camtimber` (19 frames, r3 baseline) the real range is **0.53 to 1.00**, so a floor of
// 0.35 is below every frame this build actually produces and an order of magnitude above a
// degenerate one. It is a `warn` and it is owned here on purpose: postfx cannot fix a
// frame with nothing in it, but postfx is the piece whose evidence it invalidates, and a
// critic reading SUMMARY.md needs to know that before reading the three lines above it.
//
// ## What it deliberately does NOT measure, because it would be confounded
//
// The obvious fifth invariant is the split tone ON REAL PIXELS: shadows cooler than the
// key, measured off the frame rather than off a synthetic neutral. It is not here. The
// brightest thing in almost every frame is the sky, the sky is blue, and this instrument
// has the canvas and no depth — so a "highlights are warm" test would read the sky's
// albedo and report the split tone inverted on a correctly graded frame. Excluding sky
// needs the depth buffer at readback time, which is a second full-resolution read. The
// synthetic-neutral pair above (`neutralAxisChroma`, `blackPointChroma`) is the right
// instrument for that question and it already exists. Saying so here rather than shipping
// a confounded fifth number is the point of the wave.
// ---------------------------------------------------------------------------

/** Display luma at or above which a pixel counts as clipped. `GradeTargets`'s own wording:
 *  "fraction of pixels allowed above 0.98". */
const FRAME_CLIP_L = 0.98;
/** Bins across the frame's own [p1, p99] for the occupancy metric. */
const FRAME_OCC_BINS = 32;
/** Share of the frame a bin must carry to count as occupied. */
const FRAME_OCC_SHARE = 0.005;
/**
 * Occupancy floor. Measured 0.531-1.000 over the 19 beauty frames of `vista`, `todvista`
 * and `camtimber` on the round-3 baseline; two flat colour fields score 0.06.
 */
const FRAME_OCC_MIN = 0.35;
/** Below this p99-p1 span the frame has no tonal range at all and occupancy is reported 0
 *  rather than the 1.0 that normalising by an empty range would produce. */
const FRAME_SPAN_MIN = 0.05;
/** Seconds between frame measurements. */
const FRAME_PERIOD_S = 4.0;
/** Sample every Nth pixel in both axes once the frame is in main memory. */
const FRAME_STRIDE = 2;
/**
 * THIS INSTRUMENT'S OWN BUDGET, on the same bus as everything it measures.
 *
 * An instrument that is not itself audited is how `camera.pixelProbeMs` came to be running
 * at 112 ms a sample inside the frame budget every other piece is graded on.
 *
 * WHAT THE NUMBER IS. Like `terrainshading.sepAuditMs`, the cost reported is the MARGINAL
 * cost past the driver flush: a one-texel read forces the queue and the clock starts after
 * it, so the frame's own rasterisation — 48 ms of it on a headless capture, measured — is
 * reported separately and never billed here. What remains is one full-canvas `readPixels`
 * memcpy plus a strided reduction over a 256-bin histogram.
 *
 * MEASURED: **5.4 ms at 1600x900** (capture harness) and **7.6 ms at 1920x1080** (minimum
 * of five, canopy station) — about 3.7 ms per megapixel, which is a memcpy's slope. It runs
 * one frame in `FRAME_PERIOD_S`: one in 240 at 60 fps, and never on two consecutive frames.
 *
 * IT IS A HITCH, AND THAT IS THE PART WORTH SAYING OUT LOUD. 7.6 ms is 0.03% of wall clock
 * and 45% of one 60 Hz frame, and BAR is explicit that "hitches are worse than a lower
 * average". `terrain.sepaudit` reads the same canvas on a 6 s period; `resetFrameAudit`
 * offsets this one's phase so the two cannot land on the same frame at a scenario start.
 * The honest reading is that this is a development-time instrument whose price is a
 * dropped frame every four seconds on the machines we can measure, and the lever if that
 * ever bites is `FRAME_PERIOD_S`, not the measurement.
 *
 * 25 ms rather than the measured 8: the cost is per-megapixel, so this leaves room to 1440p
 * and correctly complains at 4K, where a 31 ms readback would be a visible stall.
 */
const FRAME_AUDIT_MS_MAX = 25;

/** Midpoint and half-width of `GradeTargets.blackPointP1`, so the two-sided band can be
 *  one `abs` invariant rather than two half-invariants nobody reads together. */
const FRAME_BP_MID = (GradeTargets.blackPointP1[0] + GradeTargets.blackPointP1[1]) / 2;
const FRAME_BP_HALF = (GradeTargets.blackPointP1[1] - GradeTargets.blackPointP1[0]) / 2;

const INV_FRAME_BLACK = declareInvariant({
  id: 'postfx.frameBlackPoint',
  owner: 'postfx',
  what: 'how far the finished frame\'s 1st-percentile display luma sits outside the authored black-point band',
  unit: 'frac',
  budget: FRAME_BP_HALF,
  compare: 'abs',
  severity: 'error',
  phase: 'run',
  consequence: 'below the band the shadows are a hole with no detail in them; above it nothing in the frame reads as black — read the `floor vs pedestal` pair in `where` before routing, it says which of the two this is',
  source: 'src/render/palette.ts GradeTargets.blackPointP1',
});

const INV_FRAME_WHITE = declareInvariant({
  id: 'postfx.frameWhitePoint',
  owner: 'postfx',
  what: '99th-percentile display luma of the finished frame, over the pixels that are not already clipped',
  unit: 'frac',
  budget: GradeTargets.whitePointP99Max,
  compare: 'max',
  severity: 'error',
  phase: 'run',
  consequence: 'the top of the frame is pinned against white, so highlights arrive as paper instead of rolling off — the video-gamey failure BAR names first for this piece',
  source: 'src/render/palette.ts GradeTargets.whitePointP99Max',
});

const INV_FRAME_CLIP = declareInvariant({
  id: 'postfx.frameClipped',
  owner: 'postfx',
  what: 'share of the finished frame at or past display luma 0.98',
  unit: 'frac',
  budget: GradeTargets.clippedFractionMax,
  compare: 'max',
  severity: 'error',
  phase: 'run',
  consequence: 'a flat white region with no structure in it, where a filmic highlight should still be carrying cloud',
  source: 'src/render/palette.ts GradeTargets.clippedFractionMax',
});

const INV_FRAME_OCC = declareInvariant({
  id: 'postfx.frameToneOccupancy',
  owner: 'postfx',
  what: 'fraction of the frame\'s own p1-p99 luma range that carries a readable share of its pixels',
  unit: 'frac',
  budget: FRAME_OCC_MIN,
  compare: 'min',
  severity: 'warn',
  phase: 'run',
  consequence: 'the frame the grade is being judged on has almost no tonal structure, so nothing measured on this capture is evidence that the grade works',
  source: 'src/render/post.ts FRAME_OCC_MIN',
});

const INV_FRAME_MS = declareInvariant({
  id: 'postfx.frameAuditMs',
  owner: 'postfx',
  what: 'marginal CPU cost of one frame measurement, past the driver flush it forces',
  unit: 'ms',
  budget: FRAME_AUDIT_MS_MAX,
  compare: 'max',
  severity: 'warn',
  phase: 'run',
  consequence: 'a measuring hitch the player feels, inside a frame budget shared with nine other pieces',
  source: 'src/render/post.ts FRAME_AUDIT_MS_MAX',
});

/** One frame measurement, as the hook and the invariants both see it. */
export interface FrameStats {
  ok: boolean;
  reason?: string;
  width: number;
  height: number;
  pixels: number;
  tier: number;
  /** Display luma percentiles, 0..1, on the sRGB-encoded values the panel is driven with.
   *  `p99` is taken over the UNCLIPPED population — see the note in `measureFrame`. */
  p1: number;
  p50: number;
  p99: number;
  /** Share of sampled pixels at or past `FRAME_CLIP_L`. */
  clipped: number;
  /** See `INV_FRAME_OCC`. */
  occupancy: number;
  /** The frame's darkest 1% per channel, as 8-bit codes, and the OKLab chroma/hue of that
   *  colour. Reported rather than budgeted — see the note on `auditFrame`. */
  floorRGB: [number, number, number];
  floorC: number;
  floorHue: number;
  /** What the grade puts under a scene-BLACK pixel, in the same codes. Compare with
   *  `floorRGB` to tell "the grade is milky" from "the frame has no black in it" —
   *  see `gradePedestal`. */
  pedestalRGB: [number, number, number];
  pedestalLuma: number;
  /** Marginal cost, and the flush it deliberately excludes. */
  costMs: number;
  flushMs: number;
}

/**
 * Check every grade constant against the authority and record the worst deviation.
 *
 * Relative where the constant is not near zero, absolute where it is — a tint of 1.048
 * and a flare of 0.024 cannot share an epsilon. Called once per frame from
 * `syncUniforms`, which is ~30 compares against ~20 uniform writes that were already
 * happening; it does not show up in a frame time.
 */
const _drift = { worst: 0, field: '' };

function gradeDrift(): { worst: number; field: string } {
  const T = PostTuning as unknown as Record<string, number | number[]>;
  const G = Grade as unknown as Record<string, number | readonly number[]>;
  let worst = 0, field = '';
  for (const k of Object.keys(G)) {
    const a = T[k], b = G[k];
    if (typeof b === 'number') {
      if (typeof a !== 'number') { worst = 1; field = `${k} (missing)`; continue; }
      const d = Math.abs(a - b) / Math.max(1e-3, Math.abs(b));
      if (d > worst) { worst = d; field = k; }
    } else {
      const av = a as number[];
      if (!Array.isArray(av)) { worst = 1; field = `${k} (missing)`; continue; }
      for (let i = 0; i < b.length; i++) {
        const d = Math.abs(av[i] - b[i]) / Math.max(1e-3, Math.abs(b[i]));
        if (d > worst) { worst = d; field = `${k}[${i}]`; }
      }
    }
  }
  // One module-scope object, written in place: this runs on every frame and the audit bus
  // is explicit that the hot paths are allocation free.
  _drift.worst = worst; _drift.field = field;
  return _drift;
}

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const QUAD_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** Shared helpers: depth linearisation, view-space reconstruction, cheap hash. */
const DEPTH_LIB = /* glsl */ `
uniform vec4 uProj; // x: tan(fov/2)*aspect, y: tan(fov/2), z: near, w: far

float linZ(float d) {
  float z = d * 2.0 - 1.0;
  return (2.0 * uProj.z * uProj.w) / (uProj.w + uProj.z - z * (uProj.w - uProj.z));
}
vec3 viewPos(vec2 uv, float z) {
  vec2 ndc = uv * 2.0 - 1.0;
  return vec3(ndc.x * uProj.x * z, ndc.y * uProj.y * z, -z);
}
// Interleaved gradient noise. Static (no time term) on purpose: with no temporal
// filter in the chain, an animated dither pattern reads as crawling grain.
float ign(vec2 p) {
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}
// Ordered pattern for the *radial* stratum offset, where IGN is used for the angular one:
// the two are uncorrelated, so a block of pixels covers many distinct (angle, radius)
// strata and a 3x3 denoise sees several times the sample count it paid for. That is the
// whole reason the denoise can be small enough to leave a contact line standing.
//
// **Period three, not four.** This was a 4x4 Bayer pattern, and the wave-3 whole-game
// review found what a 4x4 pattern does behind a 3x3 denoise: nine taps cannot average
// sixteen strata, so the residual is a screen-locked 4x4 lattice — visible in the raw
// occlusion buffer across the whole near-field ground, which is exactly where a screen-
// locked artefact is least forgivable because it does not move with the world. Nine
// strata under a nine-tap filter cancel. The 5x scramble (5 is coprime with 9, so the map
// is a bijection) keeps neighbouring pixels from sampling neighbouring radii, which is
// what the Bayer ordering was there for in the first place.
float ord3(vec2 a) {
  vec2 m = floor(mod(a, 3.0));
  return mod((m.x + 3.0 * m.y) * 5.0, 9.0) * (1.0 / 9.0);
}
`;

/**
 * Shared geometry reconstruction for both occlusion passes.
 *
 * No normal buffer and no second scene pass: the view-space normal is rebuilt from four
 * depth neighbours, taking whichever side of each axis has the smaller depth step so a
 * silhouette does not produce a normal that faces nowhere. `planarity` falls to zero
 * where the depth buffer at *this pass's* resolution cannot represent the surface — a fir
 * seen through its own needles — which is the guard that stops a stronger occlusion term
 * turning a canopy into speckle.
 *
 * Factored out when the wide ring moved to its own resolution: the two passes need the
 * identical reconstruction over different texel sizes and different planarity thresholds,
 * and two copies of a normal estimator is exactly the kind of thing that drifts apart and
 * then produces occlusion that disagrees with itself across a silhouette.
 *
 * Requires DEPTH_LIB and a `float aoZ(vec2 uv)` accessor to be declared first. The two
 * passes disagree about where depth comes from — the contact ring reads the full-res depth
 * buffer, the wide ring reads its own median-prefiltered quarter-res one — and that
 * difference is the whole of "stop feeding the wide ring quarter-res foliage". Everything
 * downstream of the accessor stays shared.
 */
const AO_KEY_LIB = /* glsl */ `
uniform float uInvFar;

/** View depth -> two bytes. sqrt keeps the resolution where the geometry is: ~4 mm at
 *  5 m, ~12 mm at 50 m, against 49 mm flat if this were linear. */
vec2 packKey(float z) {
  float k = sqrt(clamp(z * uInvFar, 0.0, 1.0)) * 255.0;
  return vec2(floor(k) * (1.0 / 255.0), fract(k));
}
float unpackKey(vec2 k) {
  float s = k.x + k.y * (1.0 / 255.0);
  return s * s / max(uInvFar, 1e-9);
}
`;

const AO_GEOM_LIB = /* glsl */ `
struct AoGeom { vec3 p; vec3 n; float planarity; };

AoGeom aoGeom(vec2 uv, float z0, vec2 texel, vec2 planarFade) {
  AoGeom g;
  g.p = viewPos(uv, z0);

  float zr = aoZ(uv + vec2(texel.x, 0.0));
  float zl = aoZ(uv - vec2(texel.x, 0.0));
  float zu = aoZ(uv + vec2(0.0, texel.y));
  float zd = aoZ(uv - vec2(0.0, texel.y));

  bool useR = abs(zr - z0) < abs(zl - z0);
  bool useU = abs(zu - z0) < abs(zd - z0);
  vec3 dx = useR
    ? viewPos(uv + vec2(texel.x, 0.0), zr) - g.p
    : g.p - viewPos(uv - vec2(texel.x, 0.0), zl);
  vec3 dy = useU
    ? viewPos(uv + vec2(0.0, texel.y), zu) - g.p
    : g.p - viewPos(uv - vec2(0.0, texel.y), zd);
  // Not normalize(cross(...)): on a silhouette where both neighbours land on the same
  // surface the cross product is the zero vector, normalize() of which is a NaN that
  // survives every max() and clamp() downstream and comes out of the composite as a hard
  // black pixel. Cheap to guard, invisible until it is not — it painted a few dozen
  // black dashes along the far treeline of every frame at tier 2 and above.
  vec3 nc = cross(dx, dy);
  float nl2 = dot(nc, nc);
  g.n = nl2 > 1e-20 ? nc * inversesqrt(nl2) : vec3(0.0, 0.0, 1.0);

  // The same two steps the normal was built from: small on ground beside a trunk (the
  // near side of each axis is still ground), large only where the surface genuinely is
  // not resolvable at this resolution.
  float dstep = abs(useR ? zr - z0 : zl - z0) + abs(useU ? zu - z0 : zd - z0);
  g.planarity = 1.0 - smoothstep(planarFade.x, planarFade.y, dstep / max(z0, 0.25));
  return g;
}
`;

/**
 * The **contact** ring: a short horizon march at half resolution.
 *
 * Output is `vec4(1, contact, keyHi, keyLo)`. R is left white here and the denoise pass
 * fills it with the upsampled wide ring — see `AO_WIDE_FRAG` for why that term now lives
 * at its own resolution. The key is this texel's own view depth, sqrt-encoded across two
 * bytes, and exists so the composite can bilaterally upsample the buffer without sampling
 * full-res depth four more times.
 */
const AO_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tDepth;
uniform vec2 uTexel;   // 1 / AO resolution
uniform vec4 uAoN;     // x contact radius, y intensity, z min px, w max px
uniform vec3 uPlanar;  // resolvability fade start, end (dz/z), horizon bias (sine)
uniform vec2 uLod;     // x feature frequency (cycles/m), y hard cull distance (m)
uniform float uPxu;    // px per world unit at z = 1, at this pass's resolution
${DEPTH_LIB}
${reliefGLSL('fragment', false)}
${AO_KEY_LIB}
float aoZ(vec2 uv) { return linZ(texture2D(tDepth, uv).x); }
${AO_GEOM_LIB}

void main() {
  float z0 = aoZ(vUv);
  vec2 key = packKey(z0);
  if (z0 > uLod.y) { gl_FragColor = vec4(1.0, 1.0, key); return; }

  AoGeom g = aoGeom(vUv, z0, uTexel, uPlanar.xy);
  vec3 p = g.p;
  vec3 n = g.n;

  float pxu = uPxu;
  float ang0 = ign(gl_FragCoord.xy) * 6.2831853;
  float rad0 = ord3(gl_FragCoord.xy);

  // --- contact ring: a horizon search, not a point estimate ---------------------
  //
  // The first build of this ring was the same scattered-point estimator as the wide one,
  // and it failed for a reason worth writing down: near a rock's base only about a
  // quarter of a disc of directions is blocked, so with seven taps the answer swings
  // between "nothing" and "fully occluded" depending on whether a tap happened to land on
  // the rock. Measured, the raw buffer reached 0.51 occlusion at a boulder's base and the
  // resolve delivered 0.32, because a signal that is black on one texel and white on its
  // neighbour is *noise*, and every denoise there is will treat it as such.
  //
  // Marching a few directions and keeping the highest horizon each one reaches is the
  // same tap budget spent on a far lower-variance estimator: the samples along a ray are
  // correlated, so three of them answer "how high does the occluder rise in this
  // direction" almost exactly, and averaging four directions is smooth in the per-pixel
  // rotation rather than random in it. It is also the estimator that matches what a
  // contact shadow *is* — the elevation of the nearest thing standing on the ground.
  float rpxN = clamp(pxu * uAoN.x / z0, uAoN.z, uAoN.w);
  float rN = max(rpxN * z0 / pxu, 0.015);
  float r2N = rN * rN;
  float occN = 0.0;
  for (int d = 0; d < AO_DIRS; d++) {
    float ang = ang0 + 1.19998 + float(d) * (6.2831853 / float(AO_DIRS));
    vec2 dir = vec2(cos(ang), sin(ang));
    float h = 0.0;
    for (int s = 1; s <= AO_STEPS; s++) {
      // At least one texel out, always: a jittered step that lands inside the receiver's
      // own texel returns the receiver's own depth, which is a tap spent proving that a
      // point does not occlude itself.
      float t = max(rpxN * (float(s) - rad0) / float(AO_STEPS), 1.0);
      vec2 suv = vUv + dir * t * uTexel;
      if (suv.x < 0.0 || suv.x > 1.0 || suv.y < 0.0 || suv.y > 1.0) break;
      float zs = linZ(texture2D(tDepth, suv).x);
      vec3 v = viewPos(suv, zs) - p;
      float vv = dot(v, v);
      // Elevation of this sample above the receiver's tangent plane, as a sine. An
      // angular bias is the scale-invariant form: the error it guards against is a
      // mis-estimated normal, which is an angle, not a distance.
      float sinH = dot(v, n) * inversesqrt(max(vv, 1e-8)) - uPlanar.z;
      // Falls off with distance *in view space*, so a tap that walked off a silhouette
      // and landed ten metres behind is rejected by the same term that softens reach.
      float att = 1.0 / (1.0 + vv / r2N);
      h = max(h, sinH * att);
    }
    occN += max(h, 0.0);
  }
  float aoN = 1.0 - occN * uAoN.y / float(AO_DIRS);

  // Backstop for the same failure as the normal guard: a negated >= is the one test a
  // NaN answers reliably, where max and clamp are free to return either operand. Failing
  // to 1.0 means "not occluded", which is the harmless direction.
  if (!(aoN >= 0.0)) aoN = 1.0;

  // relief.ts's LOD policy at this ring's own feature frequency, in place of the invented
  // 42-78 m window both rings used to share. rlfLodManual takes the footprint directly,
  // which is the honest form here: this is a deferred pass over a depth buffer, so there is
  // no world-space derivative to hand rlfLodAt, but z / pxu is exactly the metres-per-pixel
  // it would have computed. A 0.30 m contact shadow therefore holds to 20 m and is gone by
  // 65, which is where half-res depth stops being able to represent it.
  RlfLod L = rlfLodManual(z0, z0 / uPxu);
  // The library's fade, times a soft approach to the cost cull. The cull is a hard
  // early-out, and the library's Nyquist fade is happy to run this ring further than the
  // cull does, so without this there is a visible ring of "occlusion stops here" at the
  // cull distance — which is the artefact rule 5 exists to prevent, arriving from the
  // budget rather than from the policy.
  float keep = clamp(rlfAlbedoAmp(L, uLod.x), 0.0, 1.0)
    * (1.0 - smoothstep(uLod.y * 0.72, uLod.y, z0));
  float fade = max(1.0 - keep, 1.0 - g.planarity);
  aoN = mix(clamp(aoN, 0.0, 1.0), 1.0, fade);
  gl_FragColor = vec4(1.0, aoN, key);
}
`;

/**
 * The wide ring's **surface-coherence** prefilter, quarter res. One number per texel: how
 * far apart the surfaces inside that texel are, relative to how far away they are.
 *
 * This is what stops the wide ring spending its authority on foliage, and it is upstream of
 * the estimator on purpose. Measured on the stocked-stand frame, the wide ring's authority
 * (DEBUG_VIEW 9) had 5.5 code values of local contrast on the canopy against 0.6 on the
 * ground — the term's variation was mostly aliasing — while the ground, the actual subject,
 * sat inside a fifth of the model's range. Reshaping the transfer alone would have spread
 * the ground out *and* amplified the speckle.
 *
 * Nine taps on the block's own corners, edge midpoints and centre, so they cover the 4x4
 * full-res footprint of a quarter-res texel rather than clustering in the middle of it.
 * `(max - min) / median` is scale-free: a hillside is a few per cent however steep it is,
 * a fir seen through its own needles is over 1, and sky behind a canopy edge sits at the
 * far plane and pins it. Nine taps at quarter resolution is 9/16 of one full-res tap.
 *
 * ## What this pass deliberately does NOT do, and the measurement that decided it
 *
 * The first build of it also handed the wide ring a **median-filtered depth to march**, on
 * the argument that a median is the only defensible answer to "what surface does this texel
 * stand on". It is, and it cost the term half its signal on ground that was never the
 * problem: measured by A/B in one page session at this station, marching the median took
 * the bare forest floor's own wide deficit from 0.180 to 0.090 while the canopy's local
 * contrast went *up*, to 7.7. The reason is obvious in hindsight — a median pushes sparse
 * canopy back toward the sky behind it, and sparse canopy inside 6 m is a real occluder of
 * the floor even when its depth samples are noise. So the march reads the full-resolution
 * depth buffer exactly as it did before, and this pass's output is used for one thing only:
 * fading the term out where the *receiver* is not a surface. Same for discounting foliage
 * *occluders*, which was measured at another 45% of the floor's deficit for no visible gain
 * (`aoCoherenceFade` has the numbers).
 *
 * DEBUG_VIEW 10 is this buffer. It should read white on ground, trunks, deadfall and rock,
 * and black on canopy and fern.
 */
const AO_COHERENCE_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tDepth;
uniform vec2 uTexel;    // 1 / quarter-res size
uniform float uSpreadK; // scale on (max-min)/median before the 0..1 clamp
${DEPTH_LIB}

// Separable pseudo-median of nine: median each row of three, then median the three
// results. Twelve compares against the nineteen a full 3x3 median network costs, and on
// the only input that matters here — one surface plus a minority of outliers — it returns
// the same answer. Used for the denominator, so a texel straddling a silhouette is
// normalised against the surface it mostly is rather than against either extreme.
float med3(float a, float b, float c) {
  return max(min(a, b), min(max(a, b), c));
}

void main() {
  vec2 o = uTexel * 0.375;
  float z00 = linZ(texture2D(tDepth, vUv + vec2(-o.x, -o.y)).x);
  float z10 = linZ(texture2D(tDepth, vUv + vec2( 0.0, -o.y)).x);
  float z20 = linZ(texture2D(tDepth, vUv + vec2( o.x, -o.y)).x);
  float z01 = linZ(texture2D(tDepth, vUv + vec2(-o.x,  0.0)).x);
  float z11 = linZ(texture2D(tDepth, vUv).x);
  float z21 = linZ(texture2D(tDepth, vUv + vec2( o.x,  0.0)).x);
  float z02 = linZ(texture2D(tDepth, vUv + vec2(-o.x,  o.y)).x);
  float z12 = linZ(texture2D(tDepth, vUv + vec2( 0.0,  o.y)).x);
  float z22 = linZ(texture2D(tDepth, vUv + vec2( o.x,  o.y)).x);

  float m = med3(med3(z00, z10, z20), med3(z01, z11, z21), med3(z02, z12, z22));
  float lo = min(min(min(z00, z10), min(z20, z01)),
             min(min(z11, z21), min(min(z02, z12), z22)));
  float hi = max(max(max(z00, z10), max(z20, z01)),
             max(max(z11, z21), max(max(z02, z12), z22)));

  float spread = clamp((hi - lo) / max(m, 0.25) * uSpreadK, 0.0, 1.0);
  gl_FragColor = vec4(spread, spread, spread, 1.0);
}
`;

/**
 * The **wide** ring: a long horizon march at quarter resolution.
 *
 * ## Why this is a separate pass now
 *
 * The wide term used to be a scattered-point SAO estimate sharing the contact ring's
 * half-res pass and its 34-px screen clamp, and measured on the resolved buffer of a
 * forest frame it contributed a median of 0.4% darkening and exceeded 15% on 1.9% of
 * ground pixels. It was not weak, it was *small*: a 1 m disc, clamped to 0.26 m at 3 m
 * from the camera, placed on a forest floor. Nothing this term exists to find is that
 * size. A tree well is 2–4 m across, the crease where a cut bench meets the hillside is a
 * 3–6 m concavity, and a canopy edge is a hillside-scale event.
 *
 * Widening it in place was not an option worth taking. A 6 m radius at half res means a
 * ~190 full-res-pixel march on 25% of the frame's pixels, and the denoise it would then
 * need is the one thing the contact ring cannot survive — the whole reason the contact
 * line resolves at 0.87 is that its filter is one texel wide. So the wide term gets its
 * own grid:
 *
 *   - **Quarter resolution.** A large-scale visibility term is low-frequency by
 *     construction, so a quarter of the pixels is not a compromise, it is the correct
 *     sample rate. It buys twice the screen radius for a quarter of the tap cost, and the
 *     4x fewer pixels are what make a 30-tap march affordable at all.
 *   - **A horizon march, not a point estimate**, for the same reason the contact ring is
 *     one: samples along a ray are correlated, so a few of them answer "how high does the
 *     ground rise in this direction" with far lower variance than the same budget
 *     scattered over a disc — and over metres, a scattered estimate is *all* variance.
 *   - **Quadratically spaced steps.** Occlusion at this scale is dominated by what is
 *     near, and a linear march to 48 texels puts four of its five samples where nothing
 *     is. `t = r * f^2` lands the five steps at roughly 1, 4, 12, 24 and 39 quarter-res
 *     texels — a log-ish spread that resolves a trunk at 1 m and a bank at 6 m in the
 *     same march.
 *   - **A near-field fade.** The first ~14% of the radius is handed to the contact ring
 *     rather than counted twice. Without it every rock grows a soft quarter-res halo on
 *     top of the contact line it already has, which is the "grime" failure this term is
 *     supposed to avoid.
 *
 * What the estimator measures is elevation above the receiver's own tangent plane, so a
 * planar hillside — every sample in the tangent plane — returns zero however steep it is,
 * a convex roller returns zero, and a concavity returns the angle it subtends. That is
 * precisely the large-scale value structure the direction asks lighting to carry.
 *
 * Output is `vec4(wide, 1, keyHi, keyLo)` — same key encoding as the contact pass, so the
 * same joint bilateral upsample works on it.
 */
const AO_WIDE_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tDepth;
uniform sampler2D tCoh;  // the quarter-res surface-coherence prefilter
uniform vec2 uTexel;   // 1 / wide-AO resolution
uniform vec4 uWide;    // x radius m, y intensity, z min px, w max px
uniform vec4 uPlanar;  // x,y resolvability fade (dz/z), z horizon bias (sine), w near m
uniform vec2 uLod;     // x feature frequency (cycles/m), y hard cull distance (m)
uniform vec2 uCoh;     // receiver coherence fade: start, end, in clamped spread
uniform float uPxu;    // px per world unit at z = 1, at this pass's resolution
${DEPTH_LIB}
${reliefGLSL('fragment', false)}
${AO_KEY_LIB}
float aoZ(vec2 uv) { return linZ(texture2D(tDepth, uv).x); }
${AO_GEOM_LIB}

void main() {
  float z0 = aoZ(vUv);
  vec2 key = packKey(z0);
  if (z0 > uLod.y) { gl_FragColor = vec4(1.0, 1.0, key); return; }

  AoGeom g = aoGeom(vUv, z0, uTexel, uPlanar.xy);
  // Receiver coherence — one fetch, and the whole of "stop feeding it quarter-res foliage".
  // A texel that contains several surfaces has no tangent plane, so every elevation this
  // estimator measures *against* it is meaningless, and the numbers it returns there are
  // large: that is why the model's authority was ending up on canopy speckle. Faded rather
  // than cut, so a canopy edge does not get a hard line down it.
  float cohR = 1.0 - smoothstep(uCoh.x, uCoh.y, texture2D(tCoh, vUv).r);

  // Radius from the clamp, not from the tuning: both the falloff and the normalisation
  // derive from the radius actually sampled, so biting the screen clamp tightens the
  // term rather than silently discarding most of its integral.
  float rpx = clamp(uPxu * uWide.x / z0, uWide.z, uWide.w);
  float r = max(rpx * z0 / uPxu, 0.05);
  float r2 = r * r;
  // Near-field handover to the contact ring, in metres. A world constant, because the
  // thing it is handing over to has a world-constant reach: expressed as a fraction of
  // this ring's own clamped radius it drifted from 27 cm in the near field to 84 cm past
  // 17 m, leaving everything between 30 and 84 cm in the mid ground unseen by either
  // term. Capped at half the radius so a tightly clamped ring keeps something to march.
  float rn = min(uPlanar.w, r * 0.5);
  float rn2 = rn * rn;

  float ang0 = ign(gl_FragCoord.xy) * 6.2831853;
  float j0 = ord3(gl_FragCoord.xy);

  float occ = 0.0;
  for (int d = 0; d < AOW_DIRS; d++) {
    float ang = ang0 + float(d) * (6.2831853 / float(AOW_DIRS));
    vec2 dir = vec2(cos(ang), sin(ang));
    float h = 0.0;
    for (int s = 0; s < AOW_STEPS; s++) {
      float f = (float(s) + j0) / float(AOW_STEPS);
      // At least one texel out: a step that lands inside the receiver's own texel returns
      // the receiver's own depth, which is a tap spent proving a point does not occlude
      // itself.
      float t = max(rpx * f * f, 1.0);
      vec2 suv = vUv + dir * t * uTexel;
      // Off-screen taps end the march rather than clamping: a clamped tap reads the edge
      // pixel over and over, which paints a dark band down the sides of the frame.
      if (suv.x < 0.0 || suv.x > 1.0 || suv.y < 0.0 || suv.y > 1.0) break;
      float zs = aoZ(suv);
      vec3 v = viewPos(suv, zs) - g.p;
      float vv = dot(v, v);
      // Elevation above the receiver's tangent plane, as a sine. An angular bias is the
      // scale-invariant form: the error it guards against is a mis-estimated normal,
      // which is an angle, not a distance.
      float sinH = dot(v, g.n) * inversesqrt(max(vv, 1e-8)) - uPlanar.z;
      // Band-pass, in one divide: vv/(rn2+vv) hands the first fraction of the radius to
      // the contact ring, 1/(1+vv/r2) softens the reach and rejects a tap that walked off
      // a silhouette and landed thirty metres behind.
      float att = vv / ((rn2 + vv) * (1.0 + vv / r2));
      h = max(h, sinH * att);
    }
    occ += max(h, 0.0);
  }
  float ao = 1.0 - occ * uWide.y / float(AOW_DIRS);
  if (!(ao >= 0.0)) ao = 1.0;

  // relief.ts's LOD policy, at this ring's own feature frequency and this pass's own
  // resolution. A 6 m concavity is resolvable far past the 78 m the old shared window cut
  // it off at: full strength to ~200 m and gone by ~650, which is the band the standing
  // critique describes as carrying no shade at all.
  RlfLod L = rlfLodManual(z0, z0 / uPxu);
  // The library's fade, times a soft approach to the cost cull. The cull is a hard
  // early-out, and the library's Nyquist fade is happy to run this ring further than the
  // cull does, so without this there is a visible ring of "occlusion stops here" at the
  // cull distance — which is the artefact rule 5 exists to prevent, arriving from the
  // budget rather than from the policy.
  float keep = clamp(rlfAlbedoAmp(L, uLod.x), 0.0, 1.0)
    * (1.0 - smoothstep(uLod.y * 0.72, uLod.y, z0));
  float fade = max(1.0 - keep * cohR, 1.0 - g.planarity);
  ao = mix(clamp(ao, 0.0, 1.0), 1.0, fade);
  gl_FragColor = vec4(ao, 1.0, key);
}
`;

/**
 * Denoise of the wide occlusion buffer, quarter res.
 *
 * The opposite trade from the contact denoise: this term is low-frequency by
 * construction, so it *wants* reach. Nine inner taps plus a four-tap cross two texels out
 * is an eight-full-res-pixel support, which is what takes the six-direction march's
 * residual rotational structure off it without touching anything the term is supposed to
 * resolve.
 *
 * The bilateral weight comes from the AO texel's **own packed depth key** rather than
 * from a second depth fetch — the key is already there, it is the same depth the estimate
 * was computed at, and it halves the pass's bandwidth. Because the key is `sqrt(z/far)`,
 * `2*|dk|/k` is exactly the relative depth difference, so the tolerance is in the same
 * units as the upsamplers'.
 */
const AO_WIDE_BLUR_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tAo;
uniform vec2 uTexel;
uniform vec3 uBlur;   // x relative-depth tolerance, y outer-ring weight, z outer offset

float aoKey(vec4 s) { return s.b + s.a * (1.0 / 255.0); }

void main() {
  vec4 c0 = texture2D(tAo, vUv);
  float k0 = max(aoKey(c0), 1e-4);
  float acc = c0.r;
  float wsum = 1.0;

  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      if (i == 0 && j == 0) continue;
      vec4 s = texture2D(tAo, vUv + vec2(float(i), float(j)) * uTexel);
      float rel = abs(aoKey(s) - k0) * 2.0 / k0;
      float w = 1.0 / (1.0 + rel * uBlur.x);
      acc += s.r * w; wsum += w;
    }
  }
  for (int d = 0; d < 4; d++) {
    vec2 o = d == 0 ? vec2(1.0, 0.0)
           : d == 1 ? vec2(-1.0, 0.0)
           : d == 2 ? vec2(0.0, 1.0) : vec2(0.0, -1.0);
    vec4 s = texture2D(tAo, vUv + o * uBlur.z * uTexel);
    float rel = abs(aoKey(s) - k0) * 2.0 / k0;
    float w = uBlur.y / (1.0 + rel * uBlur.x);
    acc += s.r * w; wsum += w;
  }

  gl_FragColor = vec4(acc / wsum, c0.g, c0.ba);
}
`;

/**
 * 3x3 relative-depth denoise of the contact channel, half res — and the point at which
 * the two occlusion scales are brought onto one grid.
 *
 * One texel of reach, not two and a half, and the bilateral term is `|dz| / z` rather
 * than `|dz|` — the old absolute-metre form gave a trunk 0.3 m in front of the ground at
 * 20 m a weight of 0.4, which is not a bilateral filter, it is a blur with extra steps.
 * The contact channel carries its own centre weight so the term that draws the line is
 * diluted by ~2.2x rather than by ~7.6x. Measured on a boulder base: raw contact
 * occlusion 0.97, resolved 0.87.
 *
 * The R channel is *replaced* by a joint bilateral upsample of the quarter-res wide
 * buffer. Doing that here rather than in the composite is worth a paragraph: the
 * composite runs at full resolution, so four extra fetches there cost four per output
 * pixel, where the same four here cost one — and the term being upsampled is
 * low-frequency, so nothing is lost by resolving it on the half-res grid the composite is
 * going to bilaterally upsample from anyway. The whole two-scale model therefore reaches
 * the composite as the same `vec4(wide, contact, key)` it always did, and the composite
 * did not have to change.
 *
 * The depth key in B/A passes through untouched from the centre texel.
 */
const AO_BLUR_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tAo;
uniform sampler2D tDepth;
uniform vec2 uTexel;
uniform vec3 uBlur;   // x relative-depth tolerance, y contact centre weight, z diagonal
#ifdef USE_WIDE
uniform sampler2D tAoWide;
uniform vec4 uWideRes;  // xy quarter-res size, zw its texel
uniform float uWideTol;   // joint bilateral upsample tolerance, reciprocal relative depth
uniform float uWideTrust; // how fast collapsed bilinear weight reads as unoccluded
#endif
${DEPTH_LIB}

#ifdef USE_WIDE
/**
 * Joint bilateral upsample of the quarter-res wide buffer onto the half-res grid.
 *
 * Same form as the composite's aoResolve, with one difference that matters: it does
 * **not** fall back to nearest when every candidate disagrees. The contact channel can,
 * because a contact line half a texel out of place beats one that has crossed a
 * silhouette — but the wide channel has no value of its own at this pixel, only the four
 * candidates, so "collapse to nearest" there means *a sky pixel one quarter-res texel from
 * a trunk edge takes the trunk's occlusion*. That is a dark halo around every trunk
 * against the sky, which is a worse artefact than the one it exists to prevent.
 *
 * So the bilinear weights, which sum to 1 when the candidates agree with this pixel, are
 * read as a confidence: where they collapse the result blends back toward unoccluded.
 * "I cannot resolve this" and "there is nothing here" are the same answer for an
 * occlusion term, and it is the harmless one.
 */
float wideAt(float z0) {
  vec2 f = vUv * uWideRes.xy - 0.5;
  vec2 b = floor(f);
  vec2 t = f - b;
  vec2 base = (b + 0.5) * uWideRes.zw;
  float acc = 0.0;
  float wsum = 0.0;
  for (int j = 0; j < 2; j++) {
    for (int i = 0; i < 2; i++) {
      vec4 s = texture2D(tAoWide, base + vec2(float(i), float(j)) * uWideRes.zw);
      float k = s.b + s.a * (1.0 / 255.0);
      float zs = k * k * uProj.w;
      float bw = (i == 0 ? 1.0 - t.x : t.x) * (j == 0 ? 1.0 - t.y : t.y);
      float w = bw / (1.0 + abs(zs - z0) / max(z0, 0.5) * uWideTol);
      acc += s.r * w;
      wsum += w;
    }
  }
  float trust = clamp(wsum * uWideTrust, 0.0, 1.0);
  return mix(1.0, acc / max(wsum, 1e-6), trust);
}
#endif

void main() {
  float z0 = linZ(texture2D(tDepth, vUv).x);
  vec4 c0 = texture2D(tAo, vUv);
  float sn = c0.g * uBlur.y, wn = uBlur.y;

  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      if (i == 0 && j == 0) continue;
      vec2 su = vUv + vec2(float(i), float(j)) * uTexel;
      float zs = linZ(texture2D(tDepth, su).x);
      float dw = (i != 0 && j != 0) ? uBlur.z : 1.0;
      float w = exp(-abs(zs - z0) / max(z0, 0.5) * uBlur.x) * dw;
      sn += texture2D(tAo, su).g * w; wn += w;
    }
  }
#ifdef RAW_AO
  // Development view: pass the estimate through untouched, so a weak estimate can be told
  // from a smeared resolve. The wide channel's raw form is its own pre-denoise buffer,
  // bound by the CPU side; it still has to be upsampled to get here at all.
  sn = c0.g; wn = 1.0;
#endif

#ifdef USE_WIDE
  float wide = wideAt(z0);
#else
  float wide = c0.r;
#endif

  gl_FragColor = vec4(wide, sn / wn, c0.ba);
}
`;

/** Soft-knee bright extraction with a 4-tap box downsample folded in. */
const BRIGHT_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tScene;
uniform vec2 uTexel;   // source texel
uniform vec2 uThresh;  // threshold, knee

void main() {
  vec3 c = (
    texture2D(tScene, vUv + vec2(-1.0, -1.0) * uTexel).rgb +
    texture2D(tScene, vUv + vec2( 1.0, -1.0) * uTexel).rgb +
    texture2D(tScene, vUv + vec2(-1.0,  1.0) * uTexel).rgb +
    texture2D(tScene, vUv + vec2( 1.0,  1.0) * uTexel).rgb) * 0.25;
  float l = max(c.r, max(c.g, c.b));
  float knee = max(uThresh.y, 1e-4);
  float soft = clamp(l - uThresh.x + knee, 0.0, 2.0 * knee);
  soft = soft * soft / (4.0 * knee);
  float contrib = max(soft, l - uThresh.x) / max(l, 1e-4);
  gl_FragColor = vec4(min(c * contrib, vec3(24.0)), 1.0);
}
`;

/** Separable 9-tap Gaussian collapsed to 5 bilinear fetches. */
const BLUR_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tSrc;
uniform vec2 uDir; // texel-sized step along the blur axis

void main() {
  vec3 c = texture2D(tSrc, vUv).rgb * 0.2270270270;
  c += (texture2D(tSrc, vUv + uDir * 1.3846153846).rgb +
        texture2D(tSrc, vUv - uDir * 1.3846153846).rgb) * 0.3162162162;
  c += (texture2D(tSrc, vUv + uDir * 3.2307692308).rgb +
        texture2D(tSrc, vUv - uDir * 3.2307692308).rgb) * 0.0702702703;
  gl_FragColor = vec4(c, 1.0);
}
`;

/**
 * The one full-resolution pass. Motion blur, occlusion, bloom, exposure, tone curve,
 * grade, vignette, fringing, dither and sRGB encode, in that order.
 */
const COMPOSITE_FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;

uniform sampler2D tScene;
uniform sampler2D tDepth;

// The contact tint — declared OUTSIDE every feature #ifdef, deliberately.
//
// Two independent blocks in this shader multiply by it: the screen-space contact ring
// (USE_AO) and the analytic tyre discs (USE_WHEEL_AO). Those two blocks are gated by
// *different* tier fields — TIER_SPEC[t].dirs > 0 and t > 0 — and at tier 1 the second is
// on while the first is off. It used to live inside #ifdef USE_AO, so at tier 1 the
// fragment shader failed to compile on "uAoNearTint : undeclared identifier", three
// dropped the program, and THE ENTIRE FRAME RENDERED BLACK on exactly the machine
// docs/BAR.md names as the performance floor — integrated graphics, which is also every
// machine the frame-time governor steps down to tier 1 on. Nothing above tier 1 could see
// it, so three rounds of captures were clean.
//
// The rule that follows, and the reason this is a comment rather than a one-line move:
// ANYTHING MORE THAN ONE FEATURE BLOCK READS IS DECLARED HERE, UNCONDITIONALLY. A vec3
// uniform the compiler strips when unused costs nothing; a define combination that does
// not compile costs the whole picture on the lowest tier that ships. PostFX.warmNextTier
// is the belt to this braces — it links every tier's variant at boot so a repeat of this
// is a console error in every capture run rather than a black screen nobody renders.
uniform vec3 uAoNearTint;

#ifdef USE_AO
uniform sampler2D tAo;
// Bound only so DEBUG_VIEW 10 can show the wide ring's depth prefilter. Nothing in the
// shipping path reads it, and a sampler that is never fetched costs a uniform slot.
uniform sampler2D tAoDepth;
uniform vec4 uAoApply;   // x strength, y highlight relief, zw dark-relief ramp
uniform vec3 uAoTint;
uniform vec4 uAoNear;    // x strength, y highlight relief, z upsample tol, w unused
uniform vec4 uAoRes;     // xy half-res size, zw its texel
uniform vec2 uAoSun;     // scene luminance window over which a pixel counts as sunlit
uniform vec2 uAoWideShape; // x deficit floor, y soft-clip knee
uniform float uAoTintLight; // how far the occlusion hue follows the light rig, 0..1
#endif
#ifdef USE_BLOOM
uniform sampler2D tBloomTight;
uniform sampler2D tBloomWide;
uniform vec2 uBloom;     // intensity, wide weight
#endif

uniform vec2 uTexel;
uniform float uAspect;
uniform float uExposure;
uniform vec4 uMotion;    // x scale, y max px, z centre retain, w radial gain
uniform vec3 uRadial;    // x ramp start, y ramp end (aspect-corrected radius), z shape
uniform vec2 uCurve;     // scene pre-scale, display toe gamma
uniform vec3 uHiDesat;   // start, end, amount
uniform vec4 uGrade;     // contrast, pivot, satNeutral, satChromatic
uniform vec4 uShadow;    // rgb tint, a amount
uniform vec4 uHigh;      // rgb tint, a amount
uniform vec2 uMask;      // shadow mask end, highlight mask start
uniform vec3 uMixR;
uniform vec3 uMixG;
uniform vec3 uMixB;
uniform vec4 uFlare;     // rgb colour, a amount — scene-referred veiling flare
uniform vec2 uTrim;      // vignette amount, chromatic amount
#ifdef USE_WHEEL_AO
uniform vec4 uWheelF;    // front contact patch xyz, radius m
uniform vec4 uWheelR;    // rear  contact patch xyz, radius m
uniform vec2 uWheel;     // front, rear strength (0 when the wheel is off the ground)
#endif

uniform mat4 uInvViewProj;
uniform mat4 uPrevViewProj;

${DEPTH_LIB}
${reliefGLSL('fragment', false)}

const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

// ACES's RRT+ODT rational fit, per channel, *without* the ACES inset/outset matrices.
// Same response on a neutral as three's ACESFilmicToneMapping — so nobody's exposure
// calibration moves — but with none of its hue skew.
vec3 toneCurve(vec3 c) {
  c = max(c, vec3(0.0)) * uCurve.x;
  vec3 a = c * (c + 0.0245786) - 0.000090537;
  vec3 b = c * (0.983729 * c + 0.4329510) + 0.238081;
  return pow(clamp(a / b, 0.0, 1.0), vec3(uCurve.y));
}

vec3 srgb(vec3 c) {
  c = clamp(c, 0.0, 1.0);
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), c));
}

#ifdef USE_AO
/**
 * Joint bilateral upsample of the half-res occlusion buffer.
 *
 * The four candidate texels each carry their own view depth (packed into B/A by the AO
 * pass), so the weighting costs four fetches and no extra depth sampling. Weight is the
 * bilinear weight divided by a relative depth difference: candidates on this pixel's own
 * surface keep theirs, candidates that belong to the trunk in front of it lose it, and
 * where every candidate disagrees the filter collapses to nearest. Nearest is the correct
 * failure mode — half a texel of aliasing on a contact line is invisible, occlusion
 * bleeding two pixels across the silhouette is the artefact this piece exists to remove.
 */
vec2 aoResolve(float z0) {
  vec2 f = vUv * uAoRes.xy - 0.5;
  vec2 b = floor(f);
  vec2 t = f - b;
  vec2 base = (b + 0.5) * uAoRes.zw;
  vec2 acc = vec2(0.0);
  float wsum = 0.0;
  for (int j = 0; j < 2; j++) {
    for (int i = 0; i < 2; i++) {
      vec2 su = base + vec2(float(i), float(j)) * uAoRes.zw;
      vec4 s = texture2D(tAo, su);
      float k = s.b + s.a * (1.0 / 255.0);
      float zs = k * k * uProj.w;
      float bw = (i == 0 ? 1.0 - t.x : t.x) * (j == 0 ? 1.0 - t.y : t.y);
      float w = bw / (1.0 + abs(zs - z0) / max(z0, 0.5) * uAoNear.z) + 1e-5;
      acc += s.rg * w;
      wsum += w;
    }
  }
  return acc / wsum;
}

/**
 * The multiplier the occlusion model puts on this pixel.
 *
 * Split out of main() so the debug view can transform the frame twice — once with it and
 * once without — and report what the occlusion is worth *in display code values*, which
 * is the only number that answers "does the term survive the tone curve". The shipping
 * path calls it exactly once and the compiler inlines it.
 *
 * sceneLum is the exposure-scaled, pre-veil scene luminance: the same quantity
 * DEBUG_VIEW 6 reports, which is what both masks below were calibrated against.
 */
vec2 aoApplied(vec2 ao, float sceneLum) {
  // Direct sunlight is not ambient: spare what the sun is hitting. The contact term
  // gets far less of this relief — a contact shadow is the one occlusion that survives
  // full sun, which is exactly why it is what tells you a rock is sitting on the dirt.
  //
  // Both of these windows are handed in by the CPU each frame, positioned against the
  // light rig rather than against an absolute luminance. See PostTuning.aoSunMask.
  float sun = smoothstep(uAoSun.x, uAoSun.y, sceneLum);
  // And spare what has no light on it at all. Occlusion scales the *ambient* term, so
  // multiplying it into a pixel that is already unlit is counting the same occlusion
  // twice.
  float dark = smoothstep(uAoApply.z, uAoApply.w, sceneLum);
  // The wide ring's transfer. Subtract the floor the estimator returns on a plane, then a
  // SOFT CLIP whose knee sits at the forest floor's own p5 deficit — not an exponential
  // whose 90% point sits at its median, which is what turned this term into a black-point
  // shift on the one surface the brief is about. See PostTuning.aoWideShape.
  float d = clamp(1.0 - ao.x, 0.0, 1.0);
  float x = max(d - uAoWideShape.x, 0.0) / max(uAoWideShape.y, 1e-3);
  float wide = x * inversesqrt(1.0 + x * x);
  float occW = clamp(wide * uAoApply.x * (1.0 - uAoApply.y * sun) * dark, 0.0, 1.0);
  float occN = clamp((1.0 - ao.y) * uAoNear.x * (1.0 - uAoNear.y * sun) * dark, 0.0, 1.0);
  return vec2(occW, occN);
}

/**
 * The occlusion tint's HUE, taken from relief.ts's shared light block.
 *
 * Wide occlusion is loss of sky. What is left on a fully occluded pixel is the ground
 * bounce, so the colour it should be multiplied toward is the bounce's chromaticity
 * *relative to the ambient the pixel lost* — and both of those live in the one block every
 * other piece in this build shades against. The two authored tints stay: they set how dark
 * and how far from neutral the occlusion goes, which is grade, not physics. Only the
 * direction of the departure now tracks the hour.
 *
 * Chromaticities are normalised to luma 1 before the ratio is taken, so this rotates hue
 * and cannot change how dark the tint is — a bug that would otherwise show up as the whole
 * forest floor changing exposure at dawn.
 */
vec3 aoTintLit(vec3 authored) {
  RlfEnv E = rlfEnv();
  float gl = dot(E.gnd, LUMA);
  float sl = dot(E.sky, LUMA);
  if (gl < 1e-5 || sl < 1e-5) return authored;
  vec3 gc = E.gnd / gl;
  vec3 amb = mix(E.sky / sl, gc, 0.5);
  // Ratio of what survives to what the pixel is standing in. Clamped: a strongly coloured
  // dawn can otherwise push a channel far enough to read as a cast rather than as shade.
  vec3 shift = clamp(gc / max(amb, vec3(1e-3)), vec3(0.72), vec3(1.38));
  shift /= max(dot(shift, LUMA), 1e-4);
  return authored * mix(vec3(1.0), shift, uAoTintLight);
}

vec3 aoFactor(vec2 ao, float sceneLum) {
  vec2 o = aoApplied(ao, sceneLum);
  // Two independent occluders, not two passes of the same one.
  float occ = 1.0 - (1.0 - o.x) * (1.0 - o.y);
  vec3 tint = mix(aoTintLit(uAoTint), uAoNearTint, o.y / max(o.x + o.y, 1e-4));
  return mix(vec3(1.0), tint, occ);
}
#endif

/**
 * Everything between scene-referred linear and display-referred linear: highlight
 * desaturation, the tone curve, and the grade.
 *
 * A function rather than a run of statements because the debug view has to be able to put
 * two different scene values through the *identical* transform and subtract the results.
 * "The occlusion is correct in the buffer and absent on the screen" was the finding this
 * round exists to close, and it is not answerable from any quantity measured before this
 * transform runs.
 */
vec3 displayTransform(vec3 c) {
  // Highlight desaturation before the curve keeps cloud structure in a bright sky.
  float hl = dot(c, LUMA);
  c = mix(c, vec3(hl), smoothstep(uHiDesat.x, uHiDesat.y, hl) * uHiDesat.z);

  c = toneCurve(c);

  // Contrast about the pivot as a power, not as a slope. The slope form subtracts
  // pivot*(k-1) from every pixel — 0.048 of display at these settings — and everything
  // under that lands on zero, which is most of a canopy frame. See PostTuning.contrast.
  // The 1e-5 floor only exists because pow(0, k) is not required to be defined by the
  // spec; it sits about 24 stops under black.
  c = uGrade.y * pow(max(c, vec3(1e-5)) / uGrade.y, vec3(uGrade.x));

  float l = dot(c, LUMA);
  c *= mix(vec3(1.0), uShadow.rgb, (1.0 - smoothstep(0.0, uMask.x, l)) * uShadow.a);
  c *= mix(vec3(1.0), uHigh.rgb, smoothstep(uMask.y, 1.0, l) * uHigh.a);

  l = dot(c, LUMA);
  float chroma = length(c - vec3(l)) * 1.35;
  c = mix(vec3(l), c, mix(uGrade.z, uGrade.w, smoothstep(0.0, 0.40, chroma)));

  c = vec3(dot(c, uMixR), dot(c, uMixG), dot(c, uMixB));
  return max(c, vec3(0.0));
}

vec4 worldAt(vec2 uv, float rawDepth) {
  vec4 ndc = vec4(uv * 2.0 - 1.0, rawDepth * 2.0 - 1.0, 1.0);
  vec4 wp = uInvViewProj * ndc;
  return wp / wp.w;
}

vec2 velocityAt(vec2 uv, float rawDepth) {
  vec4 wp = worldAt(uv, rawDepth);
  vec4 pc = uPrevViewProj * wp;
  if (pc.w <= 1e-5) return vec2(0.0);
  return uv - ((pc.xy / pc.w) * 0.5 + 0.5);
}

#ifdef USE_WHEEL_AO
/**
 * The two tyre contact patches, as relief.ts's analytic disc occlusion — operation 3 of
 * the shared library, used for precisely the case its own header names: "a wheel...
 * anything sitting on the ground that the shadow cascade is too coarse to resolve".
 *
 * A screen-space horizon march cannot draw this one. The contact ring's radius is 0.30 m
 * and its grid is half resolution, and a tyre is 60 mm wide: at 3 m from the camera the
 * whole contact patch is a couple of half-res texels, so the estimator has nothing to
 * march over and the denoise removes what little it finds. That is why the wave-3
 * coherence pass could find three systems darkening the ground under the bike and still
 * read the result as a sticker — none of the three is drawing the tyre's own contact, they
 * are drawing body-sized blobs.
 *
 * rlfContactAt is exact for a disc on the ground (R^2 / (R^2 + y^2) on axis) and
 * light-independent, which is the property that matters: a contact shadow that is really a
 * shadow disappears in flat overcast, which is exactly when a floating object is most
 * obvious. Sized at the tyre's footprint rather than the bike's, so it does not overlap the
 * body-scale darkenings the model and the sky already draw.
 */
float wheelContact(vec3 wp) {
  return min(rlfContactAt(wp, uWheelF.xyz, uWheelF.w, uWheel.x),
             rlfContactAt(wp, uWheelR.xyz, uWheelR.w, uWheel.y));
}
#endif

void main() {
  float rawD = texture2D(tDepth, vUv).x;
  vec3 color = texture2D(tScene, vUv).rgb;

#if MB_TAPS > 0
  vec2 rel = (vUv - 0.5) * vec2(uAspect, 1.0);
  vec2 vel = velocityAt(vUv, rawD) * uMotion.x;
  // Re-anchor the streak's DIRECTION to the frame, and add no smear of its own.
  //
  // Reprojection already produces radial flow when riding forward, but its focus of
  // expansion is where the camera is *going*, which on a trail is rarely the middle of the
  // frame — so through a corner the streaks swing off-axis exactly when the bike is working
  // hardest, and the MAVRIX chase reference's blur is unmistakably outward. The round
  // before this one bought that with an ADDITIVE term, vel += rel * k, and additive is
  // the wrong operator: it does not care what the pixel is, so it smears the sky. Shot at
  // 50 km/h with the term at 0.024 and 0.030 (captures/w4post-r3-mb3), the clouds in the
  // top-left corner come back streaked — a "speed lines" filter, which is the exact failure
  // the tuning note claimed to be avoiding.
  //
  // Rotating instead of adding keeps the whole benefit and cannot produce that: a pixel
  // with no velocity has nothing to rotate, so the sky and the far ridge stay sharp at any
  // strength, and a fast near-ground pixel keeps its own smear length and gets it pointed
  // outward. Faded in over the middle 25% of the radius because the outward direction is
  // ill-conditioned at the focus and the mix would otherwise swing wildly there.
  float rl = length(rel);
  if (rl > 1e-4) {
    float vlen = length(vel);
    vel = mix(vel, rel * (vlen / rl), uMotion.w * smoothstep(0.0, 0.25, rl));
  }
  // The middle of the frame is the line you are about to ride. Keep it legible; let
  // the edges carry the speed. This is the difference between motion blur and mud — and
  // with the edge ceiling now 6x wave 3's, the shape of this ramp is the ONLY thing
  // protecting the subject. A pow above 1 holds the sharp island out to a third of the
  // frame width; the centre's own smear is 2.2 px at 900, which is where it has been for
  // three rounds. Everything that moved, moved outside r = 0.16.
  float rr = smoothstep(uRadial.x, uRadial.y, length(rel));
  vel *= mix(uMotion.z, 1.0, pow(rr, uRadial.z));
  // uMotion.y is the smaller of the authored edge ceiling and what this tier's tap ladder
  // can carry — a long streak sampled too sparsely is a row of ghosts, not a streak.
  float lenPx = length(vel / uTexel);
  if (lenPx > uMotion.y) vel *= uMotion.y / lenPx;

  if (lenPx > 0.4) {
    float centreZ = linZ(rawD);
    float wsum = 1.0;
    // Jitter the tap ladder by up to half a step, per pixel, decorrelated from the AO
    // hash. At 19 px of smear a fixed ladder of five taps is 2 px apart and reads as
    // discrete ghosts of the trunk you just passed; jittered, the same taps read as a
    // streak, because neighbouring pixels sample the trail between each other's taps.
    float mbJ = ign(gl_FragCoord.xy + 17.0) - 0.5;
    for (int i = 1; i <= MB_TAPS; i++) {
      vec2 o = vel * ((float(i) + mbJ) / float(MB_TAPS + 1)) * 0.5;
      vec2 u1 = clamp(vUv + o, vec2(0.0015), vec2(0.9985));
      vec2 u2 = clamp(vUv - o, vec2(0.0015), vec2(0.9985));
      #ifdef MB_REJECT
        // A tap that sits on something much nearer than us belongs to a foreground
        // object (the bike, a trunk) — smearing it over the ground is the artefact
        // that makes cheap motion blur look broken.
        float w1 = linZ(texture2D(tDepth, u1).x) < centreZ * 0.82 ? 0.12 : 1.0;
        float w2 = linZ(texture2D(tDepth, u2).x) < centreZ * 0.82 ? 0.12 : 1.0;
      #else
        float w1 = 1.0, w2 = 1.0;
      #endif
      color += texture2D(tScene, u1).rgb * w1 + texture2D(tScene, u2).rgb * w2;
      wsum += w1 + w2;
    }
    color /= wsum;

    #ifdef USE_CA
      // Fringing only at speed, only away from centre, and only a pixel or two. Mixed
      // rather than replaced so a sharp R/B never sits on top of a blurred G.
      vec2 caV = rel * (uTrim.y * dot(rel, rel));
      color.r = mix(color.r,
        texture2D(tScene, clamp(vUv + caV, vec2(0.0015), vec2(0.9985))).r, 0.6);
      color.b = mix(color.b,
        texture2D(tScene, clamp(vUv - caV, vec2(0.0015), vec2(0.9985))).b, 0.6);
    #endif
  }
#endif

  // --- display transform ------------------------------------------------------
  color *= uExposure;

  // The quantity DEBUG_VIEW 6 reports, and the one every occlusion mask is calibrated
  // against: exposure applied, veil not yet. Measured here rather than one line down
  // because the veil is 57% of the forest floor's own scene luminance, so a mask read
  // after it is a mask reading mostly itself.
  float sceneLum = dot(color, LUMA);

  // Veiling flare, scene-referred. This is the toe: adding it here rather than after the
  // curve is what makes it slope-preserving, because the curve's toe is convex and the
  // shadows land on a steeper part of it than they would have. See PostTuning.flare.
  color += uFlare.rgb * uFlare.a;

  // --- occlusion, *after* the veil ---------------------------------------------
  //
  // This ordering is the whole of round 2. The occlusion multiply used to sit above the
  // exposure line, so the veil went on afterwards as a flat pedestal — 0.0114 of scene
  // luminance against a forest floor that measures 0.020 — and immediately refilled about
  // 36% of every occlusion the wide ring had just computed. Measured on the A/B the
  // builder shipped: a p90 17% linear darkening arrived on the screen as 2.2 code values
  // against a region mean of 23. The buffer was right and the image did not have it.
  //
  // It is also the physically coherent order, not just the one that survives. The veil is
  // the sky's own contribution to the black point and occlusion *is* sky visibility, so a
  // pixel that cannot see the sky should not be handed the sky's veil. Multiplying the sum
  // says exactly that, in one instruction.
  //
  // Bloom stays outside it: bloom is light scattered in the lens off something bright
  // somewhere else in the frame, and geometry near this pixel does not occlude that.
#ifdef USE_AO
  #if DEBUG_VIEW == 7
    vec3 unocc = color;
  #endif
  color *= aoFactor(aoResolve(linZ(rawD)), sceneLum);
#endif

#ifdef USE_WHEEL_AO
  // The tyre contacts. Applied in the same place and in the same direction as the rest of
  // the occlusion — after the veil, before the curve — and tinted with the contact ring's
  // own tint, because the millimetres under a tyre get neither sky nor much bounce.
  // Sky pixels are excluded by depth rather than by the falloff: a cloud directly above
  // the bike is at the same xz and the disc form factor has no opinion about 3 km.
  if (rawD < 0.99999) {
    float wc = wheelContact(worldAt(vUv, rawD).xyz);
    // unocc deliberately does not get this factor, so DEBUG_VIEW 7 reports the tyre
    // contact as part of what the occlusion model is worth on the screen.
    color *= mix(vec3(1.0), uAoNearTint, 1.0 - wc);
  }
#endif

#ifdef USE_BLOOM
  {
    // Was added before the exposure multiply and is now after it, so it carries the
    // exposure explicitly: the contribution on the image is unchanged to the bit.
    vec3 bl = (texture2D(tBloomTight, vUv).rgb +
               texture2D(tBloomWide, vUv).rgb * uBloom.y) * (uBloom.x * uExposure);
    color += bl;
    #if defined(USE_AO) && DEBUG_VIEW == 7
      unocc += bl;
    #endif
  }
#endif

#if DEBUG_VIEW == 7
  vec3 occLin = color;
#endif

  color = displayTransform(color);

  // --- trimmings ---------------------------------------------------------------
  float r = length((vUv - 0.5) * vec2(uAspect, 1.0)) / 0.72;
  color *= 1.0 - uTrim.x * smoothstep(0.34, 1.18, r);

  vec3 outc = srgb(color);
  // 8-bit dither: the sky is a smooth gradient and banding after a grade is real.
  outc += (ign(gl_FragCoord.xy) - 0.5) * (1.0 / 255.0);

#if DEBUG_VIEW == 1
  #ifdef USE_AO
    vec2 dao = aoResolve(linZ(rawD));
    outc = vec3(min(dao.x, dao.y));
  #endif
#elif DEBUG_VIEW == 4
  #ifdef USE_AO
    outc = vec3(aoResolve(linZ(rawD)).y);   // contact ring alone
  #endif
#elif DEBUG_VIEW == 5
  #ifdef USE_AO
    outc = vec3(aoResolve(linZ(rawD)).x);   // wide ring alone
  #endif
#elif DEBUG_VIEW == 2
  vec2 dv = velocityAt(vUv, rawD) * uMotion.x / uTexel;
  outc = vec3(abs(dv) / uMotion.y, 0.0);
#elif DEBUG_VIEW == 3
  #ifdef USE_BLOOM
    outc = srgb(texture2D(tBloomTight, vUv).rgb * uBloom.x * 12.0);
  #endif
#elif DEBUG_VIEW == 6
  // Scene-referred luminance, linear, clipped at 1. Exists so the occlusion masks can be
  // calibrated against the numbers the scene actually produces rather than against a
  // guess: every one of them is a smoothstep over this quantity, and two of them shipped
  // with windows that no surface in this world ever entered.
  outc = vec3(clamp(dot(texture2D(tScene, vUv).rgb * uExposure, LUMA), 0.0, 1.0));
#elif DEBUG_VIEW == 8
  // The same quantity as view 6, **in stops**: code = (log2(L) + 14) / 14 * 255.
  //
  // View 6 is linear and 8-bit, so everything below 0.004 is code 0 or 1 — and the two
  // masks this piece was sent back for live at 0.0008 and 0.005, i.e. entirely inside
  // that first code. A round was spent calibrating a window nothing could measure. This
  // one resolves 0.055 of a stop per code across fourteen stops, so the forest floor at a
  // low sun and the same floor at noon are two readable, comparable numbers.
  //
  // L = 2^(code / 255 * 14 - 14). Code 67 = 0.0008, 116 = 0.005, 152 = 0.020, 201 = 0.13.
  outc = vec3(clamp(
    (log2(max(dot(texture2D(tScene, vUv).rgb * uExposure, LUMA), 1e-6)) + 14.0) / 14.0,
    0.0, 1.0));
#elif DEBUG_VIEW == 9
  // **The wide ring's authority after the masks**, 0..1, as a flat grey.
  //
  // View 7 answers "what is the occlusion worth on the screen" and folds in the tone
  // curve, which moves with the light; view 5 answers "what does the buffer say" and is
  // time-of-day invariant by construction. Neither isolates the thing this round was sent
  // back for, which is the *masks* — how much of a fixed geometric occlusion survives
  // aoSunMask and aoDarkRelief. Park the camera, sweep the clock, and this view should
  // not move. Before the windows were referred to the light rig it moved by 1.6x.
  #ifdef USE_AO
    outc = vec3(aoApplied(aoResolve(linZ(rawD)), sceneLum).x);
  #else
    outc = vec3(0.0);
  #endif
#elif DEBUG_VIEW == 10
  // **The wide ring's depth prefilter**, as the thing that decides whether a texel is one
  // surface or several: white where the quarter-res texel contains a single coherent
  // surface, black through foliage.
  //
  // Added because the first build of the prefilter took the ground's own occlusion down by
  // 3x and the buffer views could not say whether that was the receiver gate, the occluder
  // discount or the median itself. It is the only view here that reports a quantity
  // upstream of the estimator, which is where this round's fault was.
  #ifdef USE_AO
    outc = vec3(1.0 - texture2D(tAoDepth, vUv).r);
  #else
    outc = vec3(0.0);
  #endif
#elif DEBUG_VIEW == 7
  // **What the occlusion is worth on the screen**, in display code values x 8.
  //
  // The instrument this round was missing. Every other AO view here reports the *buffer*,
  // and the buffer was never the problem: round 1 shipped a wide ring measuring a median
  // 9.8% occlusion that arrived on the image as 1.2 code values, because a flat pedestal
  // was refilling it and the toe was compressing what survived. This view puts the same
  // pixel through the identical display transform twice, with the occlusion and without,
  // and shows the difference — so "the term is inert" and "the term is fine but the
  // pipeline eats it" stop being the same picture. Mid grey here is ~16 codes of
  // darkening; anything that reads as black is a place the model does nothing.
  #ifdef USE_AO
    outc = vec3(abs(srgb(displayTransform(unocc)) - srgb(displayTransform(occLin))) * 8.0);
  #else
    outc = vec3(0.0);
  #endif
#endif

  gl_FragColor = vec4(outc, 1.0);
}
`;

// ---------------------------------------------------------------------------
// Plumbing
// ---------------------------------------------------------------------------

/**
 * Tier 0 is grade only; 3 is everything.
 *
 * `dirs`/`steps` are the contact ring's horizon march at half res, costing dirs*steps
 * taps per half-res pixel. `wdirs`/`wsteps` are the wide ring's at quarter res, so its
 * tap count is divided by four again before it reaches the frame: tier 3's thirty taps
 * cost less per frame than the contact ring's twelve.
 *
 * The contact ring is the last thing given up, not the first: its screen radius is small
 * so its fetches stay in cache, and it is the one carrying the artefact the brief calls
 * the most damaging. The wide ring goes with it rather than before it — half an occlusion
 * model is what this round was sent back to fix, and a tier that ships the contact line
 * with nothing behind it is that failure with extra steps.
 */
const TIER_SPEC = [
  { dirs: 0, steps: 0, wdirs: 0, wsteps: 0, bloom: 0, mb: 0, reject: false, ca: false, samples: 0 },
  { dirs: 0, steps: 0, wdirs: 0, wsteps: 0, bloom: 1, mb: 3, reject: false, ca: false, samples: 2 },
  { dirs: 3, steps: 2, wdirs: 4, wsteps: 4, bloom: 1, mb: 4, reject: false, ca: true, samples: 4 },
  { dirs: 4, steps: 3, wdirs: 6, wsteps: 6, bloom: 2, mb: 8, reject: true, ca: true, samples: 4 },
] as const;

function v3(a: readonly [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(a[0], a[1], a[2]);
}

class Quad {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly mesh: THREE.Mesh;
  /**
   * Pixels written by every full-screen pass since the last reset — the chain's FILL.
   *
   * Counted here, in the one function every pass in this file goes through, rather than
   * summed from a table of target sizes somewhere else. This project's standing failure
   * mode is a mirror that drifts from the thing it mirrors, and a pass added at a new tier
   * or a target resized on a governor step would not appear in a table. It appears here
   * because it cannot be drawn without coming through this line.
   *
   * This is the number that transfers to a fill-bound GPU. See `postfx.fillPasses`.
   */
  fillPx = 0;

  constructor() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(
      new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    g.setAttribute('uv', new THREE.BufferAttribute(
      new Float32Array([0, 0, 2, 0, 0, 2]), 2));
    this.mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial());
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);
  }

  draw(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget | null,
       material: THREE.Material): void {
    this.fillPx += target
      ? target.width * target.height
      : renderer.domElement.width * renderer.domElement.height;
    this.mesh.material = material;
    renderer.setRenderTarget(target);
    renderer.render(this.scene, this.camera);
  }

  /**
   * Link a material's program *and prove it linked*, by drawing one triangle into a 4x4
   * target — not by `renderer.compile()`.
   *
   * This distinction is the whole value of the warm pass, so it is worth the paragraph.
   * three r152+ **defers its link check**: `WebGLProgram` calls `gl.linkProgram` in its
   * constructor but only reads `LINK_STATUS` from `onFirstUse`, which fires the first time
   * anything asks the program for its uniforms — i.e. from `setProgram` during a draw.
   * `renderer.compile()` builds and links and never asks, so a variant warmed that way
   * comes back silent whether it linked or not. Verified against the r171 source in
   * `node_modules/three/build/three.module.js` (`onFirstUse`, `this.compile`) rather than
   * assumed, because assuming it is what the round before this one would have done.
   *
   * A draw asks. So a variant that does not link produces three's `Shader Error ...
   * VALIDATE_STATUS false` on the console at boot, the capture harness records console
   * errors on every run, and the tier matrix is regression tested by every scenario any
   * agent ever captures instead of by a probe someone has to remember to run.
   *
   * The target is 4x4 rather than the screen for the obvious reason, and is disposed as
   * soon as the last variant is warm.
   */
  compileByDraw(renderer: THREE.WebGLRenderer, material: THREE.Material,
                target: THREE.WebGLRenderTarget): void {
    const prev = this.mesh.material;
    this.mesh.material = material;
    renderer.setRenderTarget(target);
    renderer.render(this.scene, this.camera);
    renderer.setRenderTarget(null);
    this.mesh.material = prev;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
  }
}

/**
 * GPU stopwatch for the post passes only.
 *
 * The budget in docs/BAR.md is a GPU budget, and CPU frame time cannot see it — the
 * driver returns from a draw call long before the work happens, so timing `render()`
 * with performance.now() measures the submission, not the shading. Measured that way,
 * the bypass path looked *slower* than the full chain. EXT_disjoint_timer_query is the
 * only thing here that answers the actual question, so when it exists it both reports
 * the cost and drives the tier adaptation. When it does not, the adaptation falls back
 * to frame time, which is blunt but never wrong in the dangerous direction.
 */
class GpuTimer {
  private gl: WebGL2RenderingContext | null = null;
  private ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null = null;
  private inflight: { q: WebGLQuery; frame: number }[] = [];
  private free: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  private frame = 0;
  /** Rolling average of the post chain, ms. -1 until the first result lands. */
  ms = -1;
  lastMs = -1;
  /** Increments once per resolved sample, so a reader can tell new from repeated. */
  count = 0;

  attach(renderer: THREE.WebGLRenderer): void {
    const gl = renderer.getContext() as WebGL2RenderingContext;
    if (typeof gl.createQuery !== 'function') return;
    // Deliberately not renderer.extensions.get(): three warns loudly for extensions it
    // does not know about, and this one is optional instrumentation.
    const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2');
    if (!ext) return;
    this.gl = gl;
    this.ext = ext as unknown as { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number };
  }

  get available(): boolean { return this.gl !== null; }

  begin(): void {
    if (!this.gl || !this.ext || this.active) return;
    const q = this.free.pop() ?? this.gl.createQuery();
    if (!q) return;
    this.active = q;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.stats[0]++;
  }

  end(): void {
    if (!this.gl || !this.ext || !this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.stats[1]++;
    this.inflight.push({ q: this.active, frame: this.frame });
    this.active = null;
    this.frame++;
    this.poll();
  }

  /** Diagnostics: [begun, ended, resolved, dropped-disjoint, dropped-stale]. */
  readonly stats = [0, 0, 0, 0, 0];

  private poll(): void {
    const gl = this.gl!, ext = this.ext!;
    // Never block: only read back queries that are old enough to have landed, and drop
    // a sample if the driver reports a disjoint (a context switch mid-measurement).
    const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT);
    while (this.inflight.length && this.frame - this.inflight[0].frame > 2) {
      const { q } = this.inflight[0];
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) break;
      this.inflight.shift();
      if (disjoint) {
        this.stats[3]++;
      } else {
        const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
        this.lastMs = ns / 1e6;
        this.ms = this.ms < 0 ? this.lastMs : this.ms + (this.lastMs - this.ms) * 0.08;
        this.count++;
        this.stats[2]++;
      }
      this.free.push(q);
    }
    // A stalled driver must not leak query objects.
    while (this.inflight.length > 8) { this.free.push(this.inflight.shift()!.q); this.stats[4]++; }
  }

  reset(): void { this.ms = -1; this.lastMs = -1; this.count = 0; }
}

/**
 * The frame clock the tier governor is steered by — a rolling MEDIAN of the MEASURED
 * wall-clock interval between consecutive frames.
 *
 * ## Why this class exists at all
 *
 * The governor used to be called as `this.adapt(dt * 1000)`, where `dt` is the timestep
 * the frame was *asked* to advance by. Those are two different quantities and only one of
 * them is a measurement:
 *
 *   - `dt` is how far the WORLD MOVED. Motion blur is right to use it (`dtScale` in the
 *     composite is a shutter angle over the displayed interval), and it is right there.
 *   - the frame PERIOD is how long the machine TOOK. Only that can say whether the
 *     machine is keeping up.
 *
 * Under `requestAnimationFrame` the two happen to coincide, because `GameLoop.frame`
 * passes the measured wall interval straight through — which is why this survived four
 * waves. Under the capture harness they do not: `__grip.stepFrames(n, dt)` calls
 * `stepManual` with a CONSTANT dt (1/30 on `fullrun`), so the governor was handed 33.3 ms
 * on every frame, `degradeMs` is 20.5, and the chain ratcheted to tier 0 in ~3.6 s on
 * hardware that was rendering the frame in 1.6 ms. Measured, on the round that carried
 * this gap: **99.1% of the gate run's 5154 frames rendered with no AO, no bloom, no motion
 * blur and no tyre-contact occlusion.** Every beauty frame this project has graded itself
 * on since was a different chain from the one being authored.
 *
 * ## Why a median and not the exponential average it replaces
 *
 * docs/AGENTS.md is explicit about this environment: `medianFrameMs` is the frame's real
 * cost, and roughly one frame in ten blocks for tens of milliseconds when the driver's
 * queue flushes. Measured on the gate run itself, the distribution is median 1.6 ms, p95
 * 3.9, p99 25.3, max 92.1. A mean or an EMA of that is a statement about the driver's
 * queue; the median is a statement about the frame. The harness adds its own outliers on
 * top — one CDP round trip per `stepFrames` batch and a ~100 ms readback on every
 * screenshot — and a median over 64 frames does not move for either.
 *
 * Zero allocation: two preallocated `Float64Array`s, an insertion sort over at most 64
 * elements, and it only runs when the median is asked for (every `RECOMPUTE` samples).
 */
class FrameClock {
  private buf: Float64Array;
  private scratch: Float64Array;
  private n = 0;
  private last = -1;
  private medianCache = -1;
  private computedAt = -1;
  /** Samples thrown away as not-a-frame. Reported; a large count is itself a finding. */
  rejected = 0;
  /** Last accepted interval, ms. Diagnostics only — never the control signal. */
  lastMs = -1;

  /** Recompute the median at most this often, in samples. The controller's own time
   *  constants are >= 1500 ms, so per-frame resolution would be spent for nothing. */
  private static readonly RECOMPUTE = 8;

  constructor(window: number) {
    const w = Math.max(8, Math.min(256, window | 0));
    this.buf = new Float64Array(w);
    this.scratch = new Float64Array(w);
  }

  /** Resize the window if the tuning changed. Drops the history, which is correct — a
   *  window of a different length is a different estimator. */
  setWindow(window: number): void {
    const w = Math.max(8, Math.min(256, window | 0));
    if (w === this.buf.length) return;
    this.buf = new Float64Array(w);
    this.scratch = new Float64Array(w);
    this.clear();
  }

  /**
   * Record one frame boundary. `nowMs` must be taken at the SAME point in every frame.
   * Returns the accepted interval in ms, or -1 if there was nothing to measure or the
   * sample was rejected as not-a-frame.
   */
  tick(nowMs: number, rejectMs: number): number {
    const prev = this.last;
    this.last = nowMs;
    if (prev < 0) return -1;
    const d = nowMs - prev;
    // A non-finite or non-positive delta means the clock moved backwards (a page
    // suspend/resume). Not a frame either.
    if (!(d > 0) || d > rejectMs) { this.rejected++; return -1; }
    this.buf[this.n % this.buf.length] = d;
    this.n++;
    this.lastMs = d;
    return d;
  }

  get count(): number { return this.n; }
  get filled(): number { return Math.min(this.n, this.buf.length); }

  /** Rolling median, or -1 while the window holds fewer than `minSamples`. */
  median(minSamples: number): number {
    const n = this.filled;
    if (n < Math.max(3, minSamples)) return -1;
    if (this.computedAt >= 0 && this.n - this.computedAt < FrameClock.RECOMPUTE) {
      return this.medianCache;
    }
    const s = this.scratch;
    for (let i = 0; i < n; i++) s[i] = this.buf[i];
    // Insertion sort. n <= 64, and this runs once every 8 frames.
    for (let i = 1; i < n; i++) {
      const v = s[i];
      let j = i - 1;
      while (j >= 0 && s[j] > v) { s[j + 1] = s[j]; j--; }
      s[j + 1] = v;
    }
    this.medianCache = (n & 1) ? s[n >> 1] : 0.5 * (s[(n >> 1) - 1] + s[n >> 1]);
    this.computedAt = this.n;
    return this.medianCache;
  }

  /** Fastest frame in the window. Diagnostics: a machine waiting on its display has a
   *  minimum equal to its median; a machine waiting on work does not. */
  fastest(): number {
    const n = this.filled;
    if (n === 0) return -1;
    let m = this.buf[0];
    for (let i = 1; i < n; i++) if (this.buf[i] < m) m = this.buf[i];
    return m;
  }

  /** Drop the history but keep the clock running. Used on a tier change, so the median
   *  being judged afterwards contains no frames from the tier before it. */
  clear(): void {
    this.n = 0;
    this.medianCache = -1;
    this.computedAt = -1;
  }

  /** Full reset, including the clock itself — the next tick measures nothing. */
  reset(): void {
    this.clear();
    this.last = -1;
    this.rejected = 0;
    this.lastMs = -1;
  }
}

function makeMaterial(frag: string, uniforms: Record<string, THREE.IUniform>,
                      defines?: Record<string, string | number>): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: QUAD_VERT,
    fragmentShader: frag,
    uniforms,
    defines: defines ?? {},
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
}

class PostFX {
  private renderer!: THREE.WebGLRenderer;
  private quad = new Quad();

  private sceneRT: THREE.WebGLRenderTarget | null = null;
  private aoRT: THREE.WebGLRenderTarget | null = null;
  private aoBlurRT: THREE.WebGLRenderTarget | null = null;
  private aoWideRT: THREE.WebGLRenderTarget | null = null;
  private aoWideBlurRT: THREE.WebGLRenderTarget | null = null;
  private aoCohRT: THREE.WebGLRenderTarget | null = null;
  private bloomA: THREE.WebGLRenderTarget | null = null;
  private bloomB: THREE.WebGLRenderTarget | null = null;
  private bloomC: THREE.WebGLRenderTarget | null = null;
  private bloomD: THREE.WebGLRenderTarget | null = null;

  private aoMat!: THREE.ShaderMaterial;
  private aoCohMat!: THREE.ShaderMaterial;
  private aoWideMat!: THREE.ShaderMaterial;
  private aoWideBlurMat!: THREE.ShaderMaterial;
  private aoBlurMat!: THREE.ShaderMaterial;
  private brightMat!: THREE.ShaderMaterial;
  private blurMat!: THREE.ShaderMaterial;
  private compMat!: THREE.ShaderMaterial;

  private width = 0;
  private height = 0;
  private hdr = true;
  /** Set once if the chain cannot run at all; the game then draws itself. */
  failed = false;

  tier = 3;
  /** 0 = normal, 1 = AO (both rings), 2 = velocity, 3 = bloom, 4 = contact ring alone,
   *  5 = wide ring alone, 6 = scene-referred luminance, 7 = the occlusion's worth in
   *  display code values, 8 = scene luminance in stops, 9 = the wide ring's authority
   *  after the masks. Development only. */
  debugView = 0;
  /** Show the pre-denoise occlusion buffer instead of the resolved one. Development
   *  only, and the one view that tells a weak estimate from a smeared resolve. */
  debugRawAo = false;
  /** Development only: lets the wide ring's two passes be dropped for an A/B. */
  private wideEnabled = true;
  /**
   * The highest tier this DEVICE can run at all, decided once in `init` from capability.
   * A capability cap is permanent (a device without a float target cannot grow one); a
   * governor cap is not. Keeping them in two fields is what makes `unlockTier` able to put
   * the chain back to its shipping state, which is what the harness's per-scenario reset
   * needs and did not get.
   */
  private deviceCeiling = 3;
  /** The highest tier the GOVERNOR currently allows. Probationary, not permanent — see
   *  `adapt`. It used to only ever decrease, so one bad second cost the look for the rest
   *  of the page session, and across every later scenario in a capture run. */
  private tierCeiling = 3;
  /** Composite variants already linked on this device, and the 4x4 target they link
   *  against. See `warmNextTier`; the target is released once the last one is warm. */
  private readonly warmed = new Set<number>();
  private warmRT: THREE.WebGLRenderTarget | null = null;
  private locked = false;
  private slowFor = 0;
  private fastFor = 0;
  /** The MEASURED frame clock. See `FrameClock` for why this replaced `dt * 1000`. */
  private readonly clock = new FrameClock(PostTuning.governorWindow);
  /** 'watch' — normal. 'verify' — a step down is on trial. 'backoff' — a step down was
   *  tried, did nothing, and is suppressed until the timer runs out. */
  private govMode: 0 | 1 | 2 = 0;
  private verifyForMs = 0;
  private verifyFromMs = -1;
  private verifyFromTier = -1;
  private backoffLeftMs = 0;
  private backoffNextMs = PostTuning.tierBackoffMs;
  /** Tiers the governor has taken away and not given back. The audit invariant. */
  private tierLoss = 0;
  /** Step downs kept, and step downs the verification threw out. Diagnostics that say
   *  WHY the tier is where it is, which is the thing nobody could see before. */
  private govDemotions = 0;
  private govRejectedDemotions = 0;
  private govUpgrades = 0;
  /** Last median the governor acted on, ms. -1 until the window fills. */
  private govMedianMs = -1;
  /** So the audit label is only rebuilt when the state actually changes. */
  private lastNotedLoss = 0;

  private readonly prevViewProj = new THREE.Matrix4();
  private readonly curViewProj = new THREE.Matrix4();
  private readonly invViewProj = new THREE.Matrix4();
  private readonly size = new THREE.Vector2();
  private readonly proj = new THREE.Vector4();
  private hasPrev = false;
  /**
   * Where each ring's Nyquist fade reaches zero, metres — solved on the CPU from
   * `relief.ts`'s own mirror of the shader's LOD policy, and re-solved whenever the
   * viewport or the FOV changes.
   *
   * These exist because the shader's early-out and the shader's fade have to be the same
   * number. If the cull is shorter than the fade there is a hard edge in the occlusion at
   * the cull distance; if it is longer the pass marches thirty taps for a term that has
   * already faded to nothing. Deriving both from `reliefNyquist` / `reliefFootprint` is
   * what the library's CPU mirror is for — its own header names "tile dissolves, imposter
   * switches and particle sizing", and a deferred pass's early-out is the same problem.
   */
  private cullContact = 78;
  private cullWide = 78;
  private lodFov = -1;
  private lodHeight = -1;
  /** Smoothed 0..1 presence of each tyre's analytic contact term. */
  private wheelF = 0;
  private wheelR = 0;
  /** Development amplifier for the GPU stopwatch. 1 in every shipping path. */
  private chainRepeat = 1;
  /** Camera far as of the last frame, so `probeAo` can decode the buffer's depth key. */
  private probeFar = 3200;
  readonly timer = new GpuTimer();

  // --- the frame instrument (see the block above `INV_FRAME_BLACK`) -----------------
  /** Canvas readback, allocated once per resolution. */
  private frameBuf: Uint8Array | null = null;
  private frameBufW = 0;
  private frameBufH = 0;
  /** One texel, read first so the clock starts after the driver's queue has flushed. */
  private readonly frameTexel = new Uint8Array(4);
  /** Luma and per-channel histograms in 8-bit code space. Allocated once; the reduction
   *  is zero-allocation, which is what lets this be a shipping path and not a probe. */
  private readonly frameHistL = new Int32Array(256);
  private readonly frameHistR = new Int32Array(256);
  private readonly frameHistG = new Int32Array(256);
  private readonly frameHistB = new Int32Array(256);
  private frameTimer = 2.0;
  /** Consecutive failures; three and the instrument retires rather than logging per frame. */
  private frameFails = 0;
  private lastFrame: FrameStats | null = null;

  init(renderer: THREE.WebGLRenderer): boolean {
    this.renderer = renderer;
    const ext = renderer.extensions;
    this.hdr = ext.has('EXT_color_buffer_half_float') || ext.has('EXT_color_buffer_float');

    // Cap the opening tier by what the device looks like. A soft rasteriser or a small
    // max-texture budget is not going to enjoy a half-res AO pass.
    const caps = renderer.capabilities;
    if (caps.maxTextureSize < 4096) this.deviceCeiling = 1;
    if (!this.hdr) this.deviceCeiling = Math.min(this.deviceCeiling, 2);
    this.tierCeiling = this.deviceCeiling;
    this.tier = this.deviceCeiling;

    try {
      this.buildMaterials();
      renderer.getDrawingBufferSize(this.size);
      this.resize(Math.max(2, this.size.x | 0), Math.max(2, this.size.y | 0));
    } catch (e) {
      console.warn('[post] chain unavailable, falling back to direct render', e);
      this.failed = true;
      return false;
    }
    // Draw calls are read by the capture harness after the frame. three resets
    // renderer.info at the start of every render() call, so with a multi-pass chain the
    // telemetry would report only the last pass. Take ownership of the reset instead.
    renderer.info.autoReset = false;
    this.timer.attach(renderer);
    this.auditGrade();
    return true;
  }

  /**
   * The build-phase half of the audit: push the neutral axis through this file's own
   * display transform and publish what the grade puts on it.
   *
   * Build phase because it is a property of the parameter block, not of a frame — it is
   * true before anything renders and the harness must not reset it at a scenario
   * boundary. It is also the only invariant in this piece that could have caught the
   * defect the palette authority found by hand, which is the reason it exists.
   */
  private auditGrade(): void {
    for (const s of NEUTRAL_LEVELS) {
      const n = gradeNeutral(s);
      INV_NEUTRAL.observeWhere(n.C, `scene ${s} -> C ${n.C.toFixed(4)} h ${n.h.toFixed(0)}`);
    }
    // The veiling flare's own colour, separately, because it is the term with no mask and
    // the authority puts a tighter ceiling on it than on the axis as a whole.
    const f = PostTuning.flare;
    const lab = linearToOklab(
      PostTuning.flareColor[0] * f, PostTuning.flareColor[1] * f, PostTuning.flareColor[2] * f);
    let h = Math.atan2(lab[2], lab[1]) * 180 / Math.PI;
    if (h < 0) h += 360;
    INV_BLACK_POINT.observeWhere(Math.hypot(lab[1], lab[2]), `flare hue ${h.toFixed(0)}`);
  }

  private buildMaterials(): void {
    const T = PostTuning;
    // Every material that carries relief.ts's GLSL also carries its shared uniform block,
    // by reference — one Float32Array for the whole scene, written once a frame by the
    // system `installReliefSystem()` registers. A material that forgets it does not break,
    // it falls back to the library's documented midday light, which is a silent wrong
    // answer at dawn; spreading `reliefUniforms()` is how that cannot happen.
    this.aoMat = makeMaterial(AO_FRAG, {
      ...reliefUniforms(),
      tDepth: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uProj: { value: new THREE.Vector4() },
      uAoN: { value: new THREE.Vector4() },
      uPlanar: { value: new THREE.Vector3(
        T.aoPlanarFade[0], T.aoPlanarFade[1], T.aoHorizonBias) },
      uLod: { value: new THREE.Vector2(1 / T.aoContactFeatureM, T.aoCullM) },
      uPxu: { value: 1 },
      uInvFar: { value: 1 / 3200 },
    }, { AO_DIRS: 4, AO_STEPS: 3 });

    this.aoCohMat = makeMaterial(AO_COHERENCE_FRAG, {
      tDepth: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uProj: { value: new THREE.Vector4() },
      uSpreadK: { value: T.aoCoherenceK },
    });

    this.aoWideMat = makeMaterial(AO_WIDE_FRAG, {
      ...reliefUniforms(),
      tDepth: { value: null },
      tCoh: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uProj: { value: new THREE.Vector4() },
      uWide: { value: new THREE.Vector4() },
      uPlanar: { value: new THREE.Vector4(
        T.aoWidePlanar[0], T.aoWidePlanar[1], T.aoWideBias, T.aoWideNearM) },
      uLod: { value: new THREE.Vector2(1 / T.aoWideFeatureM, T.aoCullM) },
      uCoh: { value: new THREE.Vector2(T.aoCoherenceFade[0], T.aoCoherenceFade[1]) },
      uPxu: { value: 1 },
      uInvFar: { value: 1 / 3200 },
    }, { AOW_DIRS: 6, AOW_STEPS: 5 });

    this.aoWideBlurMat = makeMaterial(AO_WIDE_BLUR_FRAG, {
      tAo: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uBlur: { value: new THREE.Vector3(
        T.aoWideBlurTol, T.aoWideBlurFar, T.aoWideBlurSpread) },
    });

    this.aoBlurMat = makeMaterial(AO_BLUR_FRAG, {
      tAo: { value: null },
      tAoWide: { value: null },
      tDepth: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uProj: { value: new THREE.Vector4() },
      uBlur: { value: new THREE.Vector3(T.aoBlurTol, T.aoBlurCentre, T.aoBlurDiag) },
      uWideRes: { value: new THREE.Vector4(1, 1, 1, 1) },
      uWideTol: { value: T.aoWideUpsampleTol },
      uWideTrust: { value: T.aoWideUpsampleTrust },
    });

    this.brightMat = makeMaterial(BRIGHT_FRAG, {
      tScene: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uThresh: { value: new THREE.Vector2(T.bloomThreshold, T.bloomKnee) },
    });

    this.blurMat = makeMaterial(BLUR_FRAG, {
      tSrc: { value: null },
      uDir: { value: new THREE.Vector2() },
    });

    this.compMat = makeMaterial(COMPOSITE_FRAG, {
      ...reliefUniforms(),
      tScene: { value: null },
      tDepth: { value: null },
      tAo: { value: null },
      tAoDepth: { value: null },
      tBloomTight: { value: null },
      tBloomWide: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uAspect: { value: 1 },
      uExposure: { value: 1 },
      uProj: { value: new THREE.Vector4() },
      uMotion: { value: new THREE.Vector4() },
      uCurve: { value: new THREE.Vector2(T.curvePreScale, T.toeGamma) },
      uHiDesat: { value: new THREE.Vector3(T.hiDesatStart, T.hiDesatEnd, T.hiDesatAmount) },
      uGrade: { value: new THREE.Vector4(T.contrast, T.contrastPivot, T.satNeutral, T.satChromatic) },
      uShadow: { value: new THREE.Vector4(T.shadowTint[0], T.shadowTint[1], T.shadowTint[2], T.shadowTintAmount) },
      uHigh: { value: new THREE.Vector4(T.highTint[0], T.highTint[1], T.highTint[2], T.highTintAmount) },
      uMask: { value: new THREE.Vector2(T.shadowMaskEnd, T.highMaskStart) },
      uMixR: { value: v3(T.mixR) },
      uMixG: { value: v3(T.mixG) },
      uMixB: { value: v3(T.mixB) },
      uFlare: { value: new THREE.Vector4(T.flareColor[0], T.flareColor[1], T.flareColor[2], T.flare) },
      uTrim: { value: new THREE.Vector2(T.vignette, 0) },
      uAoApply: { value: new THREE.Vector4(
        T.aoStrength, T.aoHighlightRelief, T.aoDarkRelief[0], T.aoDarkRelief[1]) },
      uAoTint: { value: v3(T.aoTint) },
      uAoNear: { value: new THREE.Vector4(
        T.aoContactStrength, T.aoContactHighlightRelief, T.aoUpsampleTol, 0) },
      uAoNearTint: { value: v3(T.aoContactTint) },
      uAoSun: { value: new THREE.Vector2(0.035, 0.13) },
      uAoWideShape: { value: new THREE.Vector2(T.aoWideShape[0], T.aoWideShape[1]) },
      uAoTintLight: { value: T.aoTintLight },
      uWheelF: { value: new THREE.Vector4(0, -1e6, 0, T.aoWheelRadius) },
      uWheelR: { value: new THREE.Vector4(0, -1e6, 0, T.aoWheelRadius) },
      uWheel: { value: new THREE.Vector2(0, 0) },
      uRadial: { value: new THREE.Vector3(
        T.blurRadialRamp[0], T.blurRadialRamp[1], T.blurRadialShape) },
      uAoRes: { value: new THREE.Vector4(1, 1, 1, 1) },
      uBloom: { value: new THREE.Vector2(T.bloomIntensity, T.bloomWide) },
      uInvViewProj: { value: new THREE.Matrix4() },
      uPrevViewProj: { value: new THREE.Matrix4() },
    });
    this.applyTierDefines();
  }

  /**
   * The two occlusion mask windows, positioned against **the light rig** rather than
   * against an absolute scene luminance.
   *
   * This is the whole of round 3 in nine lines. Both windows used to be constants read off
   * the histogram of one frame at 07:54, and the sun moves: measured with DEBUG_VIEW 8 on
   * a parked camera, the forest ground's median scene luminance is 0.014 at 06:24, 0.022
   * at 07:54, 0.075 at 12:00 and 0.027 at 17:36. A fixed window against a distribution
   * that travels two and a half stops is a mask that means something different every hour,
   * which is the opposite of docs/BAR.md's "a grade with an actual point of view, applied
   * consistently across every time of day".
   *
   * What it reads is `relief.ts`'s shared light block — sun colour x intensity in 4..6,
   * the sky probe's own irradiance in 8..10, the sun's visibility in 3. That block is the
   * one copy of the sun in this build and it is what every other piece shades against, so
   * a mask keyed off it cannot drift away from the image it is masking. Nothing here looks
   * at a pixel or at the frame: this is not an auto-exposure, and pointing the camera at
   * black timber or at a snowfield moves none of it. Only the sun moving moves it.
   *
   * `exposure` is folded in because the quantity being masked is post-exposure scene
   * luminance (the same quantity DEBUG_VIEW 6 and 8 report).
   */
  lightRef(): {
    keyLum: number; skyLum: number; gndLum: number; sunVis: number;
    ambRef: number; sunAdd: number;
    sunLo: number; sunHi: number; darkLo: number; darkHi: number;
    aoTint: [number, number, number];
  } {
    const T = PostTuning;
    const d = reliefUniforms().uRlf.value as Float32Array;
    const lum = (r: number, g: number, b: number) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const keyLum = Math.max(0, lum(d[4], d[5], d[6]));
    const skyLum = Math.max(0, lum(d[8], d[9], d[10]));
    const sunVis = Math.min(1, Math.max(0, d[3]));
    const exposure = this.renderer ? this.renderer.toneMappingExposure : 1;

    // A floor on the ambient reference so a night sky — or a build with no sky at all and
    // a zeroed block — cannot collapse both windows onto zero, where smoothstep(0,0,x) is
    // undefined and the occlusion would flicker between "all" and "none" on rounding.
    const ambRef = Math.max(1e-5, exposure * T.aoAmbientRefK * skyLum);
    const sunAdd = Math.max(0, exposure * T.aoSunRefK * keyLum * sunVis);

    const sunLo = ambRef + T.aoSunMask[0] * sunAdd;
    // Strictly ordered: at night sunAdd is 0 and the two ends would coincide.
    const sunHi = Math.max(sunLo * 1.02 + 1e-6, ambRef + T.aoSunMask[1] * sunAdd);
    const darkLo = ambRef * T.aoDarkRelief[0];
    const darkHi = Math.max(darkLo * 1.02 + 1e-7, ambRef * T.aoDarkRelief[1]);

    // CPU mirror of `aoTintLit` in COMPOSITE_FRAG. Reported rather than used, so the claim
    // "the occlusion's hue tracks the sun" is a number a critic can read out of the
    // shipping path instead of a colour someone has to believe they can see.
    const gndLum = Math.max(0, lum(d[12], d[13], d[14]));
    const tint: [number, number, number] = [T.aoTint[0], T.aoTint[1], T.aoTint[2]];
    if (gndLum > 1e-5 && skyLum > 1e-5) {
      const k = Math.min(1, Math.max(0, T.aoTintLight));
      const sh: number[] = [];
      for (let i = 0; i < 3; i++) {
        const gc = d[12 + i] / gndLum;
        const amb = 0.5 * (d[8 + i] / skyLum) + 0.5 * gc;
        sh.push(Math.min(1.38, Math.max(0.72, gc / Math.max(amb, 1e-3))));
      }
      const shl = lum(sh[0], sh[1], sh[2]) || 1;
      for (let i = 0; i < 3; i++) tint[i] *= 1 + k * (sh[i] / shl - 1);
    }
    return {
      keyLum, skyLum, gndLum, sunVis, ambRef, sunAdd,
      sunLo, sunHi, darkLo, darkHi, aoTint: tint,
    };
  }

  /**
   * Push the grade constants into the uniforms. Called every frame rather than once at
   * build: it is ~20 scalar writes, it costs nothing measurable, and it means the whole
   * look is live-tunable from a console or a capture script without a rebuild — which is
   * the difference between converging a grade in one browser session and in ten.
   */
  private syncUniforms(): void {
    const T = PostTuning;
    const u = this.compMat.uniforms;
    // The two run-phase invariants, here rather than in `render` because this is the one
    // place that runs on every frame the chain is actually on, and both are statements
    // about the frame that is about to be composited.
    const drift = gradeDrift();
    if (drift.worst > 0) INV_GRADE_DRIFT.observeWhere(drift.worst, drift.field);
    else INV_GRADE_DRIFT.observe(0);
    INV_TIER.observe(this.tier);
    INV_GOVERNOR.observe(this.tierLoss);
    // Label the worst only when the state changes, not per frame: `observeWhere` allocates
    // in the caller and this runs 120+ times a second (docs/AGENTS.md). `noteWorst` labels
    // whatever the current worst is, which after a step down is this step down.
    if (this.tierLoss !== this.lastNotedLoss) {
      this.lastNotedLoss = this.tierLoss;
      if (this.tierLoss > 0) {
        const g = this.governorState();
        INV_GOVERNOR.noteWorst(
          `median ${g.medianFrameMs} ms vs degrade ${g.degradeMs}, fastest ${g.fastestFrameMs}, ` +
          `tier ${g.tier}/${g.deviceCeiling}, kept ${g.demotions} rejected ${g.rejectedDemotions}, ` +
          `${g.rejected} non-frames dropped`);
        INV_TIER.noteWorst(`governor, median ${g.medianFrameMs} ms`);
      }
    }
    (u.uCurve.value as THREE.Vector2).set(T.curvePreScale, T.toeGamma);
    (u.uHiDesat.value as THREE.Vector3).set(T.hiDesatStart, T.hiDesatEnd, T.hiDesatAmount);
    (u.uGrade.value as THREE.Vector4).set(T.contrast, T.contrastPivot, T.satNeutral, T.satChromatic);
    (u.uShadow.value as THREE.Vector4).set(T.shadowTint[0], T.shadowTint[1], T.shadowTint[2], T.shadowTintAmount);
    (u.uHigh.value as THREE.Vector4).set(T.highTint[0], T.highTint[1], T.highTint[2], T.highTintAmount);
    (u.uMask.value as THREE.Vector2).set(T.shadowMaskEnd, T.highMaskStart);
    (u.uMixR.value as THREE.Vector3).set(T.mixR[0], T.mixR[1], T.mixR[2]);
    (u.uMixG.value as THREE.Vector3).set(T.mixG[0], T.mixG[1], T.mixG[2]);
    (u.uMixB.value as THREE.Vector3).set(T.mixB[0], T.mixB[1], T.mixB[2]);
    (u.uFlare.value as THREE.Vector4).set(
      T.flareColor[0], T.flareColor[1], T.flareColor[2], T.flare);
    const L = this.lightRef();
    (u.uAoApply.value as THREE.Vector4).set(
      T.aoStrength, T.aoHighlightRelief, L.darkLo, L.darkHi);
    (u.uAoTint.value as THREE.Vector3).set(T.aoTint[0], T.aoTint[1], T.aoTint[2]);
    (u.uAoNear.value as THREE.Vector4).set(
      T.aoContactStrength, T.aoContactHighlightRelief, T.aoUpsampleTol, 0);
    (u.uAoNearTint.value as THREE.Vector3).set(
      T.aoContactTint[0], T.aoContactTint[1], T.aoContactTint[2]);
    (u.uAoSun.value as THREE.Vector2).set(L.sunLo, L.sunHi);
    (u.uAoWideShape.value as THREE.Vector2).set(
      Math.min(0.9, Math.max(0, T.aoWideShape[0])), Math.max(0.005, T.aoWideShape[1]));
    u.uAoTintLight.value = Math.min(1, Math.max(0, T.aoTintLight));
    (u.uRadial.value as THREE.Vector3).set(
      T.blurRadialRamp[0], Math.max(T.blurRadialRamp[0] + 1e-3, T.blurRadialRamp[1]),
      Math.max(0.05, T.blurRadialShape));
    (this.brightMat.uniforms.uThresh.value as THREE.Vector2).set(T.bloomThreshold, T.bloomKnee);
    const au = this.aoMat.uniforms;
    (au.uLod.value as THREE.Vector2).set(
      1 / Math.max(0.01, T.aoContactFeatureM), this.cullContact);
    (au.uPlanar.value as THREE.Vector3).set(
      T.aoPlanarFade[0], T.aoPlanarFade[1], T.aoHorizonBias);
    const wu = this.aoWideMat.uniforms;
    (wu.uLod.value as THREE.Vector2).set(
      1 / Math.max(0.05, T.aoWideFeatureM), this.cullWide);
    (wu.uCoh.value as THREE.Vector2).set(
      T.aoCoherenceFade[0],
      Math.max(T.aoCoherenceFade[0] + 1e-3, T.aoCoherenceFade[1]));
    (wu.uPlanar.value as THREE.Vector4).set(
      T.aoWidePlanar[0], T.aoWidePlanar[1], T.aoWideBias, T.aoWideNearM);
    this.aoCohMat.uniforms.uSpreadK.value = Math.max(0.1, T.aoCoherenceK);
    (this.aoWideBlurMat.uniforms.uBlur.value as THREE.Vector3).set(
      T.aoWideBlurTol, T.aoWideBlurFar, T.aoWideBlurSpread);
    (this.aoBlurMat.uniforms.uBlur.value as THREE.Vector3).set(
      T.aoBlurTol, T.aoBlurCentre, T.aoBlurDiag);
    this.aoBlurMat.uniforms.uWideTol.value = T.aoWideUpsampleTol;
    this.aoBlurMat.uniforms.uWideTrust.value = T.aoWideUpsampleTrust;
  }

  /**
   * Solve the two cull distances from the library's LOD policy.
   *
   * `rlfAlbedoAmp` is `rlfBandLod` times a far-fade floor, and `rlfBandLod` is
   * `clamp(nyqBias - foot * freq * nyqRate, 0, 1)` — so it reaches zero at a footprint of
   * `nyqBias / (freq * nyqRate)` metres per pixel, and a perspective camera turns that into
   * a distance. `reliefFootprint` is that conversion, so this walks it rather than
   * duplicating it: bisect on the library's own function instead of inverting it by hand,
   * which cannot drift if the policy changes.
   *
   * Called on resize and when the FOV changes, not per frame — it is ~40 iterations.
   */
  private solveCulls(fovY: number, pxHeightHalf: number, pxHeightQuarter: number): void {
    const T = PostTuning;
    const solve = (freq: number, pxHeight: number): number => {
      let lo = 1, hi = T.aoCullM;
      if (reliefNyquist(freq, reliefFootprint(hi, fovY, pxHeight)) > 0) return hi;
      for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) * 0.5;
        if (reliefNyquist(freq, reliefFootprint(mid, fovY, pxHeight)) > 0) lo = mid;
        else hi = mid;
      }
      return Math.min(T.aoCullM, hi);
    };
    this.cullContact = solve(1 / Math.max(0.01, T.aoContactFeatureM), pxHeightHalf);
    this.cullWide = solve(1 / Math.max(0.05, T.aoWideFeatureM), pxHeightQuarter);
  }

  /**
   * The composite's `#define` set for a tier — split out of `applyTierDefines` so the
   * tier-warm pass below can build a *different* tier's set without going through the
   * governor. See `warmNextTier` for why that matters.
   */
  private compositeDefines(tier: number): Record<string, string | number> {
    const s = TIER_SPEC[tier];
    // `mbTaps` overrides the tier's ladder without touching the tier. It exists because the
    // smear LENGTH and the smear SAMPLE COUNT are different costs and the round before this
    // one could only vary the first — which is why "this round doubled the smear, a
    // fill-rate change" was the wrong model of what had changed. Measured: it is not.
    const mb = s.mb > 0 && PostTuning.mbTaps > 0
      ? Math.min(16, PostTuning.mbTaps | 0)
      : s.mb;
    const d: Record<string, string | number> = { MB_TAPS: mb, DEBUG_VIEW: this.debugView };
    if (s.dirs > 0) d.USE_AO = 1;
    // The tyre contacts are ~10 ALU and no taps, so they survive down to tier 1 — a device
    // that cannot afford a horizon march can still afford the one artefact BAR calls the
    // most damaging. Tier 0 is grade-only by definition.
    //
    // Note that this is the line that makes tier 1's define set *unique*: it is the only
    // tier where USE_WHEEL_AO is on and USE_AO is off. Any uniform or function both blocks
    // share therefore has to be declared outside both — see `uAoNearTint` in
    // COMPOSITE_FRAG for what it cost when one was not.
    if (tier > 0 && PostTuning.aoWheelStrength > 0) d.USE_WHEEL_AO = 1;
    if (s.bloom > 0) d.USE_BLOOM = 1;
    if (s.reject) d.MB_REJECT = 1;
    if (s.ca) d.USE_CA = 1;
    return d;
  }

  /**
   * Compile the composite for **every tier this device can reach**, one tier per frame,
   * starting on the second frame.
   *
   * This exists because of a defect that shipped and survived three rounds of captures.
   * The tyre-contact block is enabled from tier 1 up while the occlusion block is enabled
   * from tier 2 up, so tier 1's define set is the only one in which the two disagree — and
   * it referenced a uniform declared under the other block. The program did not link, three
   * dropped it, and the whole frame went black. Nothing caught it, because every capture
   * this piece has ever run boots at tier 3 and a shader variant that is never compiled
   * cannot fail.
   *
   * So: compile them all, up front, on the machine that is about to run them. Two things
   * follow, and the second is why this is worth a few frames of boot.
   *
   *   1. **A broken variant is now a console error in every capture run.** The harness
   *      already fails a run on console errors, so the whole define matrix is regression
   *      tested by every scenario any of the ten agents ever captures, not by a dedicated
   *      tier probe someone has to remember to run.
   *   2. **A tier step-down no longer compiles a shader.** The governor drops a tier when
   *      the frame is *already* over 20.5 ms, and the old behaviour was to answer that by
   *      blocking the driver on a fresh program link — a hitch, at the exact moment the
   *      frame could least afford one. docs/BAR.md: "hitches are worse than a lower
   *      average."
   *
   * Deferred rather than done in `init()` so it cannot delay first paint, and spread one
   * variant per frame so the links cannot stack into one hitch. Each one is a single
   * triangle into a 4x4 target — see `Quad.compileByDraw` for why it has to be a draw and
   * not `renderer.compile()`.
   *
   * Priced, because "it is cheap" is the kind of claim this project keeps getting wrong:
   * timed frame by frame at boot (`captures/w4post-tier.mjs`, headless ANGLE), frames 1..5
   * are **5.5 / 5.1 / 5.1 / 4.8 / 4.8 ms** — the three warm frames are the three *fastest*
   * of the five and there is no spike anywhere. The driver already translated this shader's
   * source at `buildMaterials`; a define permutation of it re-links off that, and the link
   * is not what costs.
   *
   * Verified to actually catch the thing it exists to catch: with the tier-1 defect
   * deliberately reintroduced, the same probe reports three's `VALIDATE_STATUS false` in
   * **bootErrors** — before anything asks for tier 1 — where previously it appeared only if
   * someone thought to pin that tier.
   */
  private warmNextTier(): void {
    let t = -1;
    for (let i = 0; i <= this.tierCeiling; i++) {
      if (!this.warmed.has(i)) { t = i; break; }
    }
    if (t < 0) {
      if (this.warmRT) { this.warmRT.dispose(); this.warmRT = null; }
      return;
    }
    this.warmed.add(t);
    if (t === this.tier) return;   // already linked; it is the one on screen
    const keep = this.compMat.defines;
    try {
      if (!this.warmRT) {
        this.warmRT = new THREE.WebGLRenderTarget(4, 4, {
          depthBuffer: false, stencilBuffer: false, generateMipmaps: false,
        });
      }
      this.compMat.defines = this.compositeDefines(t);
      this.compMat.needsUpdate = true;
      this.quad.compileByDraw(this.renderer, this.compMat, this.warmRT);
    } catch (e) {
      console.warn('[post] tier', t, 'failed to compile', e);
    } finally {
      this.compMat.defines = keep;
      this.compMat.needsUpdate = true;
    }
  }

  private applyTierDefines(): void {
    const s = TIER_SPEC[this.tier];
    // The define set of every variant depends on `debugView` too, so a change here
    // invalidates what was warmed. Re-warming is a program-cache hit for any variant
    // already seen, so this is close to free after the first pass.
    this.warmed.clear();
    this.compMat.defines = this.compositeDefines(this.tier);
    this.compMat.needsUpdate = true;
    this.aoMat.defines = {
      AO_DIRS: Math.max(1, s.dirs),
      AO_STEPS: Math.max(1, s.steps),
    };
    this.aoMat.needsUpdate = true;
    this.aoWideMat.defines = {
      AOW_DIRS: Math.max(1, s.wdirs),
      AOW_STEPS: Math.max(1, s.wsteps),
    };
    this.aoWideMat.needsUpdate = true;
    const bd: Record<string, string | number> = {};
    if (s.wdirs > 0 && this.wideEnabled) bd.USE_WIDE = 1;
    if (this.debugRawAo) bd.RAW_AO = 1;
    this.aoBlurMat.defines = bd;
    this.aoBlurMat.needsUpdate = true;
  }

  /**
   * Development only: drop the wide ring's two passes without touching anything else.
   *
   * The only honest way to price a pass in this harness is an A/B inside one page
   * session — `medianFrameMs` between two capture runs cannot separate this chain from
   * eight other agents editing the same tree, and `p95SystemMs.post` is the whole world's
   * draw (see docs/AGENTS.md). This is that A/B's switch.
   */
  setWide(on: boolean): void {
    if (this.wideEnabled === on) return;
    this.wideEnabled = on;
    this.applyTierDefines();
  }

  /** Development only: show the estimate before either denoise. */
  setRawAo(on: boolean): void {
    if (this.debugRawAo === on) return;
    this.debugRawAo = on;
    this.applyTierDefines();
  }

  private makeRT(w: number, h: number, hdr: boolean, depth: boolean,
                 samples: number, nearest = false): THREE.WebGLRenderTarget {
    const filter = nearest ? THREE.NearestFilter : THREE.LinearFilter;
    const rt = new THREE.WebGLRenderTarget(w, h, {
      type: hdr && this.hdr ? THREE.HalfFloatType : THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      minFilter: filter,
      magFilter: filter,
      depthBuffer: depth,
      stencilBuffer: false,
      generateMipmaps: false,
      samples,
    });
    rt.texture.colorSpace = THREE.NoColorSpace;
    rt.texture.wrapS = THREE.ClampToEdgeWrapping;
    rt.texture.wrapT = THREE.ClampToEdgeWrapping;
    if (depth) {
      const dt = new THREE.DepthTexture(w, h, THREE.UnsignedIntType);
      dt.format = THREE.DepthFormat;
      dt.minFilter = THREE.NearestFilter;
      dt.magFilter = THREE.NearestFilter;
      rt.depthTexture = dt;
    }
    return rt;
  }

  private resize(w: number, h: number): void {
    this.width = w;
    this.height = h;
    const hw = Math.max(2, w >> 1), hh = Math.max(2, h >> 1);
    const qw = Math.max(2, w >> 2), qh = Math.max(2, h >> 2);
    const ew = Math.max(2, w >> 3), eh = Math.max(2, h >> 3);
    const samples = PostTuning.msaaOverride >= 0
      ? PostTuning.msaaOverride : TIER_SPEC[this.tier].samples;

    this.disposeTargets();
    this.sceneRT = this.makeRT(w, h, true, true, samples);
    // Nearest on both AO targets. Every read of them is now an explicit point fetch at a
    // texel centre — the denoise walks the 3x3 neighbourhood, the composite walks the
    // four bilinear candidates itself — and the depth key packed into B/A would be
    // meaningless under hardware filtering anyway: interpolating the high and low bytes
    // of a two-byte number independently produces a depth that is not between the two
    // it was interpolated from.
    this.aoRT = this.makeRT(hw, hh, false, false, 0, true);
    this.aoBlurRT = this.makeRT(hw, hh, false, false, 0, true);
    // Nearest here for the same reason, and the wide ring is read exactly twice: once by
    // its own denoise, which walks its neighbourhood explicitly, and once by the half-res
    // denoise, which walks the four bilinear candidates itself.
    this.aoWideRT = this.makeRT(qw, qh, false, false, 0, true);
    this.aoWideBlurRT = this.makeRT(qw, qh, false, false, 0, true);
    // The wide ring's surface-coherence prefilter. Nearest, because every read of it is a
    // point fetch at a texel centre and a filtered "how many surfaces are in this texel"
    // is not a meaningful quantity.
    this.aoCohRT = this.makeRT(qw, qh, false, false, 0, true);
    this.bloomA = this.makeRT(qw, qh, true, false, 0);
    this.bloomB = this.makeRT(qw, qh, true, false, 0);
    this.bloomC = this.makeRT(ew, eh, true, false, 0);
    this.bloomD = this.makeRT(ew, eh, true, false, 0);
  }

  private disposeTargets(): void {
    for (const rt of [this.sceneRT, this.aoRT, this.aoBlurRT,
                      this.aoWideRT, this.aoWideBlurRT, this.aoCohRT,
                      this.bloomA, this.bloomB, this.bloomC, this.bloomD]) {
      if (!rt) continue;
      rt.depthTexture?.dispose();
      rt.dispose();
    }
    this.sceneRT = this.aoRT = this.aoBlurRT = null;
    this.aoWideRT = this.aoWideBlurRT = this.aoCohRT = null;
    this.bloomA = this.bloomB = this.bloomC = this.bloomD = null;
  }

  /**
   * The next tier DOWN the degradation ladder, skipping the steps that are not steps.
   *
   * The ladder is 3 -> 1 -> 0, not 3 -> 2 -> 1 -> 0, and that is a measurement rather than
   * a preference. `postfx.fillPasses` counts what the chain writes, exactly, on any
   * device: tier 3 is 1.906 full-resolution passes and tier 2 is 1.875 — **1.6%**. Tier 2
   * differs from 3 by one pair of 1/8-res bloom blurs, and pays for that 1.6% with the
   * reject term, half the motion-blur tap ladder and both occlusion rings at 3/2 of their
   * directions. It is a LOOK step, not a cost step. Putting it on the degradation path
   * means the first thing a struggling machine gives up buys it nothing measurable, and
   * the verification below would (correctly) throw the step out every time — so the
   * controller would burn a whole backoff cycle discovering that 1.6% is 1.6%.
   *
   * Tier 2 stays reachable: it is the device cap for a machine with no float render
   * target, and `setTier(2)` pins it for a probe. It is just not somewhere the governor
   * steps down TO.
   */
  private nextTierDown(from: number): number {
    return from >= 2 ? 1 : 0;
  }

  /** Expected fill saving of a step from `a` to `b`, as a fraction of `a`. Structural —
   *  it is the same number on every device, which is why the governor can reason with it
   *  on hardware nobody in this loop has. */
  private fillRatio(a: number, b: number): number {
    const f = (t: number): number => {
      const s = TIER_SPEC[Math.max(0, Math.min(TIER_SPEC.length - 1, t))];
      // Composite is always 1. AO contact + resolve are half res (0.25 each), the wide
      // ring's three quarter-res passes are 1/16 each, bloom bright is 1/16 and each blur
      // pair is 2/16 tight + 2/64 wide. Mirrors `lastFillPasses().parts`.
      let v = 1;
      if (s.dirs > 0) v += 0.25 + 0.25;
      if (s.dirs > 0 && s.wdirs > 0) v += 3 / 16;
      if (s.bloom > 0) v += 1 / 16 + 2 / 16;
      if (s.bloom > 1) v += 2 / 64;
      return v;
    };
    const fa = f(a);
    return fa > 0 ? (fa - f(b)) / fa : 0;
  }

  /**
   * Tier adaptation, driven by the MEASURED frame period.
   *
   * ## What this is fed, and what it used to be fed
   *
   * `FrameClock` above carries the whole argument. In one line: this was `adapt(dt *
   * 1000)`, `dt` is the timestep the frame was asked to advance by rather than the time
   * the frame took, and under the capture harness that constant 33.3 ms exceeded
   * `degradeMs` on every frame — so the chain collapsed to tier 0 on hardware rendering
   * the frame in 1.6 ms, and 99.1% of the gate run's frames were graded without the look
   * on. It now takes a rolling median of the measured interval between frames.
   *
   * ## Why every step down is put on trial rather than trusted
   *
   * The frame period is a blunt signal by construction: it cannot separate "the GPU is
   * saturated" from "the display is 30 Hz and the machine is idle" from "another system
   * in this frame is slow". The chain's own GPU time could separate them and does not
   * work here — the header records TIME_ELAPSED_EXT ranking a 4x fill-rate change
   * backwards between two runs on this machine, and a controller fed a signal that can be
   * wrong by 20x is worse than one that does not adapt.
   *
   * So the controller states a hypothesis and then checks it. "Frames are over budget
   * BECAUSE of this chain" predicts that removing 38% of the chain's fill makes the frame
   * measurably faster. If it does, keep the step. If it does not, the chain was not the
   * bottleneck, the step bought nothing, and it has been paid for with the entire authored
   * look — so put the tier back and stop trying for a while. That check is the difference
   * between this and the code it replaces, and it is what makes the failure mode of a
   * blunt signal *conservative*: on a machine whose slowness is elsewhere, this piece now
   * refuses to shed its own quality for a saving it can prove is not there.
   *
   * ## Why the ceiling can rise again
   *
   * `tierCeiling` used to only ever decrease and `unlockTier` did not restore it, so one
   * over-budget second — a shader compile, a scene rebuild, an alt-tab — cost the look for
   * the rest of the page session. It is now probation: sustained fast frames give a tier
   * back, and the backoff doubles on each failed attempt so a genuinely slow machine
   * settles within a minute instead of flapping.
   */
  private adapt(nowMs: number): void {
    const T = PostTuning;
    // Tick the clock even when locked or bypassed: a probe that pins a tier for two
    // seconds should not leave the governor with a stale `last` that reads as one huge
    // frame the moment it is released.
    const dtMs = this.clock.tick(nowMs, T.frameRejectMs);
    if (this.locked || dtMs < 0) return;

    const m = this.clock.median(T.governorMinSamples);
    this.govMedianMs = m;
    if (m < 0) return;

    // --- a step down is on trial ------------------------------------------------------
    if (this.govMode === 1) {
      this.verifyForMs += dtMs;
      if (this.verifyForMs < T.tierVerifyMs) return;
      const before = this.verifyFromMs;
      const need = Math.max(T.tierVerifyMinMs, T.tierVerifyGain * before);
      if (before - m >= need) {
        // The chain WAS the bottleneck. Keep the step; the ceiling stays down on
        // probation and `upgradeHoldMs` of fast frames is what gives it back.
        this.govDemotions++;
        this.govMode = 0;
        this.slowFor = 0;
        this.fastFor = 0;
      } else {
        // It was not. Give the look back and stop guessing for a while.
        this.govRejectedDemotions++;
        const back = this.verifyFromTier;
        this.tierCeiling = Math.max(this.tierCeiling, Math.min(this.deviceCeiling, back));
        this.tierLoss = Math.max(0, this.deviceCeiling - this.tierCeiling);
        this.backoffLeftMs = this.backoffNextMs;
        this.backoffNextMs = Math.min(T.tierBackoffMaxMs, this.backoffNextMs * 2);
        this.govMode = 2;
        this.applyTier(back);
      }
      return;
    }

    // --- suppressed after a step down that did nothing --------------------------------
    if (this.govMode === 2) {
      this.backoffLeftMs -= dtMs;
      if (this.backoffLeftMs <= 0) { this.govMode = 0; this.backoffLeftMs = 0; }
      // Fall through: an upgrade is still allowed while suppressed. Suppression is about
      // not shedding quality, not about refusing to restore it.
    }

    const over = m > T.degradeMs;
    const under = m < T.upgradeMs;

    if (over) {
      this.slowFor += dtMs;
      this.fastFor = 0;
      if (this.govMode === 0 && this.slowFor > T.degradeHoldMs && this.tier > 0) {
        const to = this.nextTierDown(this.tier);
        this.verifyFromMs = m;
        this.verifyFromTier = this.tier;
        this.verifyForMs = 0;
        this.govMode = 1;
        this.tierCeiling = Math.min(this.tierCeiling, to);
        this.tierLoss = Math.max(0, this.deviceCeiling - this.tierCeiling);
        this.slowFor = 0;
        this.applyTier(to);
      }
    } else if (under) {
      this.fastFor += dtMs;
      this.slowFor = 0;
      if (this.fastFor > T.upgradeHoldMs) {
        if (this.tier < this.tierCeiling) {
          this.govUpgrades++;
          this.fastFor = 0;
          this.applyTier(this.tier + 1);
        } else if (this.tierCeiling < this.deviceCeiling) {
          // Probation expired. Give a tier back and let the machine prove itself again.
          this.tierCeiling++;
          this.tierLoss = Math.max(0, this.deviceCeiling - this.tierCeiling);
          this.govUpgrades++;
          this.fastFor = 0;
          this.applyTier(this.tier + 1);
        } else {
          this.fastFor = 0;
        }
      }
    } else {
      this.slowFor = 0;
      this.fastFor = 0;
    }
  }

  /**
   * The governor's own tier change: same work as `setTier` minus the lock/ceiling
   * bookkeeping, which the governor does itself and must not have overwritten.
   */
  private applyTier(t: number): void {
    const to = Math.max(0, Math.min(TIER_SPEC.length - 1, t | 0));
    if (to === this.tier) return;
    const oldSamples = TIER_SPEC[this.tier].samples;
    this.tier = to;
    this.applyTierDefines();
    if (TIER_SPEC[to].samples !== oldSamples && this.width > 0) {
      this.resize(this.width, this.height);
    }
    // The median the next decision is made on must contain no frames from the tier before
    // it, or a step down is judged against a window that is half its own predecessor.
    this.clock.clear();
    this.govMedianMs = -1;
    this.timer.reset();
  }

  /** The governor's state, for the audit report and for `__gripPost.governor()`. Nobody
   *  could see any of this when the chain was collapsing, which is most of why it took
   *  four waves to find. */
  governorState(): {
    tier: number; tierCeiling: number; deviceCeiling: number; locked: boolean;
    mode: string; medianFrameMs: number; fastestFrameMs: number; lastFrameMs: number;
    samples: number; rejected: number; demotions: number; rejectedDemotions: number;
    upgrades: number; backoffLeftMs: number; degradeMs: number; upgradeMs: number;
  } {
    return {
      tier: this.tier,
      tierCeiling: this.tierCeiling,
      deviceCeiling: this.deviceCeiling,
      locked: this.locked,
      mode: this.locked ? 'locked' : ['watch', 'verify', 'backoff'][this.govMode],
      medianFrameMs: +this.clock.median(PostTuning.governorMinSamples).toFixed(3),
      fastestFrameMs: +this.clock.fastest().toFixed(3),
      lastFrameMs: +this.clock.lastMs.toFixed(3),
      samples: this.clock.count,
      rejected: this.clock.rejected,
      demotions: this.govDemotions,
      rejectedDemotions: this.govRejectedDemotions,
      upgrades: this.govUpgrades,
      backoffLeftMs: Math.max(0, Math.round(this.backoffLeftMs)),
      degradeMs: PostTuning.degradeMs,
      upgradeMs: PostTuning.upgradeMs,
    };
  }

  /** Re-allocate the targets after a tuning change that affects their format. */
  rebuild(): void {
    if (this.width > 0) this.resize(this.width, this.height);
  }

  setDebugView(v: number): void {
    this.debugView = v | 0;
    this.applyTierDefines();
  }

  /**
   * Run the chain `n` times per frame. **Development only**, and the reason it exists is
   * that the obvious way to price this file does not work here.
   *
   * `EXT_disjoint_timer_query_webgl2` IS present under the harness's launch args — the
   * round before this one said it was not, and that was wrong. What it returns is not what
   * the bracket asks for. Measured on this machine (ANGLE Metal, M4 Max), the same pinned
   * frame with `blurMaxFrac` 0 read 24.5 ms in one page session and 31.7 ms in the next,
   * and the *ordering* of five variants reversed between the two runs: a query that
   * resolves 894/894 with zero disjoints and still cannot rank a 4x fill-rate change is
   * measuring the command buffer, not the passes inside `beginQuery`/`endQuery`.
   *
   * So the price here is a **slope**, not a difference: run the chain 1, 2, 4 and 8 times
   * on an otherwise identical frame, take the wall clock with a `gl.finish()` on each end,
   * and regress. Everything that is not the chain — the world's draw, the MSAA resolve, the
   * swap, the driver's own overhead — is a constant in `n` and drops out of the slope,
   * which is exactly the property none of the absolute numbers above has. The residual of
   * that fit is the instrument's own error bar and it gets reported with the number.
   *
   * Costs nothing at `n = 1`: one predictable branch per frame.
   */
  setRepeat(n: number): void {
    this.chainRepeat = Math.max(1, Math.min(32, n | 0));
  }

  /**
   * Release a pinned tier AND put the chain back to what this device can actually run.
   *
   * This is what the capture harness calls at every scenario boundary, and until this
   * round it only cleared `locked`. `tierCeiling` was left wherever the governor had
   * ratcheted it, and the upgrade branch required `tier < tierCeiling`, which after a
   * downgrade is never true — so a collapse in scenario one was still in force in
   * scenario nine of the same page session, and every screenshot after it was a different
   * chain. Restoring the DEVICE ceiling here (not a hard-coded 3: a machine with no float
   * target genuinely cannot run tier 3) is what makes the reset mean what its doc says.
   */
  unlockTier(): void {
    this.locked = false;
    this.slowFor = 0;
    this.fastFor = 0;
    this.tierCeiling = this.deviceCeiling;
    this.tierLoss = 0;
    this.govMode = 0;
    this.verifyForMs = 0;
    this.verifyFromMs = -1;
    this.verifyFromTier = -1;
    this.backoffLeftMs = 0;
    this.backoffNextMs = PostTuning.tierBackoffMs;
    this.govDemotions = 0;
    this.govRejectedDemotions = 0;
    this.govUpgrades = 0;
    this.clock.setWindow(PostTuning.governorWindow);
    this.clock.reset();
    this.applyTier(this.deviceCeiling);
  }

  setTier(tier: number, lock = false): void {
    const t = Math.max(0, Math.min(TIER_SPEC.length - 1, tier | 0));
    if (lock) {
      this.locked = true;
      this.tierCeiling = Math.max(this.tierCeiling, Math.min(this.deviceCeiling, t));
      this.tierLoss = Math.max(0, this.deviceCeiling - this.tierCeiling);
      this.govMode = 0;
      this.backoffLeftMs = 0;
    }
    this.slowFor = 0;
    this.fastFor = 0;
    this.applyTier(t);
  }

  render(ctx: SystemContext, dt: number): void {
    // The frame boundary, taken at the same point every frame. This — not `dt` — is what
    // the tier governor is steered by; see `FrameClock`. `dt` is how far the world moved
    // and is still exactly right for the motion-blur shutter below.
    const tFrame = performance.now();
    const renderer = this.renderer;
    renderer.getDrawingBufferSize(this.size);
    const w = Math.max(2, this.size.x | 0), h = Math.max(2, this.size.y | 0);
    if (w !== this.width || h !== this.height) this.resize(w, h);

    const sceneRT = this.sceneRT!;
    const camera = ctx.camera;
    this.syncUniforms();

    // --- 1. scene into HDR ------------------------------------------------------
    //
    // This system owns the only renderer.render(scene) call in the app, so the CPU cost
    // of the whole world's draw submission lands inside `post`'s update timing. Reported
    // undivided it reads as "the post chain costs 20 ms", which is what three separate
    // pieces concluded from p95SystemMs in wave 2. Split it here: `post.scene` is the
    // world draw (which happens with or without this system) and `post` minus it is the
    // chain. Both are CPU submission time, not GPU time — see `timer` for that.
    const tScene = performance.now();
    renderer.setRenderTarget(sceneRT);
    renderer.render(ctx.scene, camera);
    systemTimings['post.scene'] = performance.now() - tScene;

    // Everything from here to the composite is "the chain"; the scene draw above would
    // have happened either way, so it is deliberately outside the stopwatch.
    //
    // `chainRepeat` is 1 in every shipping path — it is the development amplifier the price
    // in the header was finally measured with. See `setRepeat`.
    this.timer.begin();
    for (let rep = this.chainRepeat; rep > 0; rep--) this.chainPasses(ctx, dt, w, h);
    this.timer.end();
    this.prevViewProj.copy(this.curViewProj);

    // One composite variant per frame until every reachable tier has linked. Deliberately
    // after the frame is on the screen and outside the stopwatch: it is boot-time work that
    // happens to be spread over the first frames, not part of the chain's cost.
    this.warmNextTier();

    this.adapt(tFrame);

    // Last, and after `adapt`, on purpose. The readback forces the driver's queue, so the
    // frame it runs on is genuinely slower — and the governor is steered by the interval
    // between `tFrame` marks, so that cost lands in the NEXT frame's period. It is one
    // sample in `FRAME_PERIOD_S * fps` (1 in 240 at 60 Hz) against a 120-frame rolling
    // MEDIAN, so it cannot move the controller; putting it before `adapt` would have let
    // it bill itself to the frame it measured.
    this.auditFrame(dt);
  }

  /**
   * Passes 2-5 — occlusion, bloom, composite: everything the chain adds over drawing the
   * scene straight to the canvas, and nothing else.
   *
   * Split out of `render` so it can be run more than once on one frame (`setRepeat`). It
   * reads no state it also writes, apart from `hasPrev`, so running it twice on a frame
   * produces the identical image twice — which is the property the slope measurement needs.
   */
  private chainPasses(ctx: SystemContext, dt: number, w: number, h: number): void {
    const renderer = this.renderer;
    this.quad.fillPx = 0;
    const sceneRT = this.sceneRT!;
    const camera = ctx.camera;
    const tier = TIER_SPEC[this.tier];
    const tanHalf = Math.tan((camera.fov * Math.PI) / 360);
    const near = camera.near, far = camera.far;
    // The LOD culls only move when the framing does, and solving them is two bisections.
    if (camera.fov !== this.lodFov || h !== this.lodHeight) {
      this.lodFov = camera.fov;
      this.lodHeight = h;
      this.solveCulls(camera.fov, Math.max(2, h >> 1), Math.max(2, h >> 2));
    }
    // Reused, not allocated: this runs 120+ times a second (docs/AGENTS.md).
    const proj = this.proj.set(tanHalf * camera.aspect, tanHalf, near, far);
    this.probeFar = far;

    // --- 2/3. occlusion ---------------------------------------------------------
    if (tier.dirs > 0) {
      const aoRT = this.aoRT!, aoBlurRT = this.aoBlurRT!;
      const T = PostTuning;
      const au = this.aoMat.uniforms;
      au.tDepth.value = sceneRT.depthTexture;
      (au.uTexel.value as THREE.Vector2).set(1 / aoRT.width, 1 / aoRT.height);
      (au.uProj.value as THREE.Vector4).copy(proj);
      // The horizon estimator is normalised to [0,1] per direction, so this is the
      // intensity itself.
      (au.uAoN.value as THREE.Vector4).set(
        T.aoContactRadius,
        T.aoContactIntensity,
        T.aoContactMinPx,
        T.aoContactMaxPx,
      );
      au.uPxu.value = (aoRT.height * 0.5) / tanHalf;
      au.uInvFar.value = 1 / far;
      this.quad.draw(renderer, aoRT, this.aoMat);

      const wideOn = tier.wdirs > 0 && this.wideEnabled;
      if (wideOn) {
        const wRT = this.aoWideRT!, wBlurRT = this.aoWideBlurRT!;
        const cRT = this.aoCohRT!;

        // 2a. Surface coherence. Nine taps on 1/16 of the pixels — 9/16 of one full-res
        //     tap — and it is what stops the wide ring spending its authority on foliage.
        const cu = this.aoCohMat.uniforms;
        cu.tDepth.value = sceneRT.depthTexture;
        (cu.uTexel.value as THREE.Vector2).set(1 / cRT.width, 1 / cRT.height);
        (cu.uProj.value as THREE.Vector4).copy(proj);
        this.quad.draw(renderer, cRT, this.aoCohMat);

        const wu = this.aoWideMat.uniforms;
        wu.tDepth.value = sceneRT.depthTexture;
        wu.tCoh.value = cRT.texture;
        (wu.uTexel.value as THREE.Vector2).set(1 / wRT.width, 1 / wRT.height);
        (wu.uProj.value as THREE.Vector4).copy(proj);
        (wu.uWide.value as THREE.Vector4).set(
          T.aoRadius,
          T.aoIntensity,
          T.aoMinRadiusPx,
          T.aoMaxRadiusPx,
        );
        // Quarter-res px per world unit at z = 1. Derived from the target's own height so
        // the clamp means the same thing at any resolution or FOV.
        wu.uPxu.value = (wRT.height * 0.5) / tanHalf;
        wu.uInvFar.value = 1 / far;
        this.quad.draw(renderer, wRT, this.aoWideMat);

        const wbu = this.aoWideBlurMat.uniforms;
        wbu.tAo.value = wRT.texture;
        (wbu.uTexel.value as THREE.Vector2).set(1 / wRT.width, 1 / wRT.height);
        this.quad.draw(renderer, wBlurRT, this.aoWideBlurMat);
      }

      const bu = this.aoBlurMat.uniforms;
      bu.tAo.value = aoRT.texture;
      bu.tDepth.value = sceneRT.depthTexture;
      (bu.uTexel.value as THREE.Vector2).set(1 / aoRT.width, 1 / aoRT.height);
      (bu.uProj.value as THREE.Vector4).copy(proj);
      if (wideOn) {
        const wb = this.debugRawAo ? this.aoWideRT! : this.aoWideBlurRT!;
        bu.tAoWide.value = wb.texture;
        (bu.uWideRes.value as THREE.Vector4).set(
          wb.width, wb.height, 1 / wb.width, 1 / wb.height);
      }
      this.quad.draw(renderer, aoBlurRT, this.aoBlurMat);
    }

    // --- 4. bloom ---------------------------------------------------------------
    if (tier.bloom > 0) {
      const a = this.bloomA!, b = this.bloomB!, c = this.bloomC!, d = this.bloomD!;
      const brU = this.brightMat.uniforms;
      brU.tScene.value = sceneRT.texture;
      (brU.uTexel.value as THREE.Vector2).set(1 / w, 1 / h);
      this.quad.draw(renderer, a, this.brightMat);

      const blU = this.blurMat.uniforms;
      blU.tSrc.value = a.texture;
      (blU.uDir.value as THREE.Vector2).set(1 / a.width, 0);
      this.quad.draw(renderer, b, this.blurMat);

      blU.tSrc.value = b.texture;
      (blU.uDir.value as THREE.Vector2).set(0, 1 / a.height);
      this.quad.draw(renderer, a, this.blurMat);

      if (tier.bloom > 1) {
        blU.tSrc.value = a.texture;
        (blU.uDir.value as THREE.Vector2).set(1 / c.width, 0);
        this.quad.draw(renderer, c, this.blurMat);

        blU.tSrc.value = c.texture;
        (blU.uDir.value as THREE.Vector2).set(0, 1 / c.height);
        this.quad.draw(renderer, d, this.blurMat);
      }
    }

    // --- 5. composite -----------------------------------------------------------
    const u = this.compMat.uniforms;
    u.tScene.value = sceneRT.texture;
    u.tDepth.value = sceneRT.depthTexture;
    // Always the resolved buffer: with the wide ring on its own grid, "raw" is a property
    // of what the resolve pass was fed and whether it smoothed, not of which target the
    // composite reads. See `setRawAo`.
    u.tAo.value = tier.dirs > 0 ? this.aoBlurRT!.texture : null;
    u.tAoDepth.value = this.aoCohRT ? this.aoCohRT.texture : null;
    if (tier.dirs > 0) {
      const a = this.aoBlurRT!;
      (u.uAoRes.value as THREE.Vector4).set(a.width, a.height, 1 / a.width, 1 / a.height);
    }
    u.tBloomTight.value = tier.bloom > 0 ? this.bloomA!.texture : null;
    u.tBloomWide.value = tier.bloom > 1 ? this.bloomD!.texture : this.bloomA?.texture ?? null;
    (u.uTexel.value as THREE.Vector2).set(1 / w, 1 / h);
    u.uAspect.value = w / h;
    u.uExposure.value = renderer.toneMappingExposure;
    (u.uProj.value as THREE.Vector4).copy(proj);
    (u.uBloom.value as THREE.Vector2).set(
      PostTuning.bloomIntensity, tier.bloom > 1 ? PostTuning.bloomWide : 0);

    // Motion: camera reprojection, gated by how fast the bike is actually going.
    camera.updateMatrixWorld();
    this.curViewProj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.invViewProj.copy(this.curViewProj).invert();
    if (!this.hasPrev) { this.prevViewProj.copy(this.curViewProj); this.hasPrev = true; }
    (u.uInvViewProj.value as THREE.Matrix4).copy(this.invViewProj);
    (u.uPrevViewProj.value as THREE.Matrix4).copy(this.prevViewProj);

    // Tyre contacts. Read-only on BikeState (docs/AGENTS.md): the sim owns it.
    // `cx/cy/cz` is the contact patch the wheel solve resolved this step, so this is the
    // same point the tyre force was applied at rather than a guess from the chassis pose.
    {
      const T = PostTuning;
      const k = 1 - Math.exp(-Math.max(dt, 1e-4) * T.aoWheelBlend);
      const fw = ctx.bike.front, rw = ctx.bike.rear;
      const ok = (v: number): boolean => Number.isFinite(v);
      const fOn = fw.contact && ok(fw.cx) && ok(fw.cy) && ok(fw.cz) ? 1 : 0;
      const rOn = rw.contact && ok(rw.cx) && ok(rw.cy) && ok(rw.cz) ? 1 : 0;
      this.wheelF += (fOn - this.wheelF) * k;
      this.wheelR += (rOn - this.wheelR) * k;
      // Hold the last good position while the term fades out, so a wheel leaving the
      // ground does not drag its contact patch to wherever the solver parked it.
      if (fOn) (u.uWheelF.value as THREE.Vector4).set(fw.cx, fw.cy, fw.cz, T.aoWheelRadius);
      else (u.uWheelF.value as THREE.Vector4).w = T.aoWheelRadius;
      if (rOn) (u.uWheelR.value as THREE.Vector4).set(rw.cx, rw.cy, rw.cz, T.aoWheelRadius);
      else (u.uWheelR.value as THREE.Vector4).w = T.aoWheelRadius;
      (u.uWheel.value as THREE.Vector2).set(
        this.wheelF * T.aoWheelStrength, this.wheelR * T.aoWheelStrength);
    }

    const speed = Number.isFinite(ctx.bike.speed) ? Math.abs(ctx.bike.speed) : 0;
    const gate = smoothstep(PostTuning.blurSpeedIn, PostTuning.blurSpeedFull, speed);
    const dtScale = Math.min(2.5, Math.max(0.35, REF_DT / Math.max(dt, 1e-4)));
    // Two ceilings on the smear, and the tighter one wins: the authored fraction of frame
    // height, and what this tier's tap ladder can actually carry without breaking into
    // ghosts. See PostTuning.blurMaxPxPerTap — the look number and the cost number belong
    // to different owners and this is where they have to meet.
    const maxSmearPx = Math.min(
      PostTuning.blurMaxFrac * h,
      PostTuning.blurMaxPxPerTap * (2 * Math.max(1, tier.mb) + 1),
    );
    (u.uMotion.value as THREE.Vector4).set(
      PostTuning.shutter * gate * dtScale,
      maxSmearPx,
      PostTuning.blurCentreRetain,
      // How far the streak is rotated onto the frame's outward axis, 0..1, faded in with
      // the same speed gate as the smear itself. Dimensionless now — it used to be a
      // fraction of frame height ADDED to the velocity, which smeared the sky. See
      // PostTuning.blurRadialBlend.
      Math.max(0, Math.min(1, PostTuning.blurRadialBlend)) * gate,
    );
    (u.uTrim.value as THREE.Vector2).set(
      PostTuning.vignette + PostTuning.vignetteAtSpeed * gate,
      PostTuning.chromatic * gate,
    );

    this.quad.draw(renderer, null, this.compMat);

    // The chain's fill for this frame, in full-resolution passes. Counted from the one
    // function every pass goes through, so a pass added later cannot escape it, and
    // divided by the drawing buffer so the number means the same thing at any resolution.
    INV_FILL.observe(this.quad.fillPx / Math.max(1, w * h));
  }

  /**
   * READ THE FINISHED FRAME AND PUBLISH WHAT THE GRADE DID TO IT.
   *
   * Called at the end of `render`, after the composite has drawn to the canvas, so what is
   * read here is the same bytes the harness's screenshot contains. `post` owns the app's
   * only `renderer.render(scene)` call and the HUD and menus are DOM, so nothing else has
   * written to this buffer this frame.
   *
   * FOUR THINGS THAT WOULD MAKE THIS INSTRUMENT LIE, AND WHAT IS DONE ABOUT EACH:
   *
   *   1. **Measuring a frame that is not a beauty frame.** `debugView` turns the composite
   *      into a false-colour dump of the occlusion buffer, and wave 3 filed exactly those
   *      as beauty frames. Any non-zero debug view is refused rather than measured, and so
   *      is `chainRepeat > 1` (the cost bench runs the chain n times onto the canvas). The
   *      visible consequence, so nobody reads it as a bug: a capture of `aodebug` alone
   *      leaves these five invariants unobserved, and SUMMARY.md prints that as "declared
   *      but never observed". On a false-colour dump that is the correct answer — the
   *      alternative is a black-point number taken off a picture of an occlusion buffer.
   *   2. **Billing the frame's own rasterisation to the instrument.** In the harness's
   *      headless path `renderer.render()` only queues GL work and the first read is what
   *      makes the driver do it — timed naively, `terrainshading.sepAuditMs` billed 1241 ms
   *      for work that would have happened anyway. A one-texel read forces the flush and
   *      the clock starts after it; the flush is reported separately, never hidden.
   *   3. **Moving the numbers other pieces share.** No render, no state change, no uniform
   *      write: this is a `readPixels` and arithmetic. `renderer.info` is untouched because
   *      nothing is drawn.
   *   4. **A silent failure reading as a clean sheet.** A context loss or a zero-size canvas
   *      makes `readPixels` return zeros, which would score as a perfectly black frame in
   *      band. The guard is `frameSpan`: a frame whose p99-p1 is under `FRAME_SPAN_MIN` is
   *      published as occupancy 0, which is a violation, not silence.
   *
   * WHAT IS REPORTED BUT NOT BUDGETED, and why. `floorRGB` / `floorC` is the colour of the
   * frame's darkest 1% per channel — the pixel-space cousin of `postfx.blackPointChroma`.
   * It is deliberately not an invariant: measured across the same 19 frames it runs 0.0016
   * to 0.0120 OKLab chroma, and the spread is frame CONTENT (how much genuinely-black
   * material is in shot), not the grade. A budget on it would fire on a frame with no
   * shadow in it and pass on a frame full of dark albedo, which is a metric that measures
   * the world and blames the grade. It is in the hook so a probe can A/B it against a
   * changed `flareColor` inside one page session, where content is held fixed and the
   * confound cancels — that is what it is good for and it is the only thing it is good for.
   */
  private auditFrame(dt: number): void {
    if (this.frameFails >= 3) return;
    this.frameTimer -= dt;
    if (this.frameTimer > 0) return;
    this.frameTimer = FRAME_PERIOD_S;
    if (this.debugView !== 0 || this.chainRepeat !== 1) return;
    this.measureFrame();
  }

  /**
   * The measurement itself. Split out of `auditFrame` so a probe can force one on a pinned
   * frame (`__gripPost.frameStats()`) instead of waiting for the timer — an A/B that has to
   * wait 4 s per variant is an A/B nobody runs.
   */
  measureFrame(): FrameStats {
    const fail = (reason: string): FrameStats => {
      const st: FrameStats = {
        ok: false, reason, width: 0, height: 0, pixels: 0, tier: this.tier,
        p1: 0, p50: 0, p99: 0, clipped: 0, occupancy: 0,
        floorRGB: [0, 0, 0], floorC: 0, floorHue: 0,
        pedestalRGB: [0, 0, 0], pedestalLuma: 0, costMs: 0, flushMs: 0,
      };
      this.lastFrame = st;
      return st;
    };
    if (!this.renderer) return fail('no renderer');
    const W = this.width | 0, H = this.height | 0;
    if (W < 32 || H < 32) return fail('canvas too small');

    let flushMs = 0, t0 = 0;
    try {
      if (!this.frameBuf || this.frameBufW !== W || this.frameBufH !== H) {
        this.frameBuf = new Uint8Array(W * H * 4);
        this.frameBufW = W; this.frameBufH = H;
      }
      // `warmNextTier` may have left a 4x4 warm target bound. Bind the canvas explicitly
      // rather than assuming the composite's own target is still current.
      this.renderer.setRenderTarget(null);
      const gl = this.renderer.getContext();
      const tf = performance.now();
      gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, this.frameTexel);
      flushMs = performance.now() - tf;
      t0 = performance.now();
      gl.readPixels(0, 0, W, H, gl.RGBA, gl.UNSIGNED_BYTE, this.frameBuf);
    } catch (e) {
      this.frameFails++;
      if (this.frameFails === 1) console.warn('[post] frame audit read failed', e);
      return fail('readPixels threw');
    }

    const buf = this.frameBuf;
    const hL = this.frameHistL, hR = this.frameHistR, hG = this.frameHistG, hB = this.frameHistB;
    hL.fill(0); hR.fill(0); hG.fill(0); hB.fill(0);
    const S = FRAME_STRIDE;
    let n = 0;
    for (let y = 0; y < H; y += S) {
      const row = y * W;
      for (let x = 0; x < W; x += S) {
        const o = (row + x) * 4;
        const r = buf[o], g = buf[o + 1], b = buf[o + 2];
        // Luma on the ENCODED values: "display-space luma" is the number the panel is
        // driven with, which is also what the offline mirror over the harness's PNG reads.
        let l = (0.2126 * r + 0.7152 * g + 0.0722 * b) | 0;
        if (l > 255) l = 255;
        hL[l]++; hR[r]++; hG[g]++; hB[b]++;
        n++;
      }
    }
    if (n < 256) { this.frameFails++; return fail('too few samples'); }

    const pct = (h: Int32Array, p: number): number => {
      const want = p * n;
      let c = 0;
      for (let i = 0; i < 256; i++) { c += h[i]; if (c >= want) return i; }
      return 255;
    };
    const c1 = pct(hL, 0.01), c50 = pct(hL, 0.50);
    const p1 = c1 / 255, p50 = c50 / 255;

    // Clipped: everything at or past FRAME_CLIP_L, counted off the same histogram.
    const clipCode = Math.ceil(FRAME_CLIP_L * 255);
    let clip = 0;
    for (let i = clipCode; i < 256; i++) clip += hL[i];
    const clipped = clip / n;

    // THE WHITE POINT IS TAKEN OVER THE UNCLIPPED POPULATION, and the reason is the
    // authority's own wording: `whitePointP99Max` is "99th percentile, EXCLUDING the sun
    // disc and specular highlights". Those are exactly the pixels `frameClipped` already
    // counts and already budgets. Taking p99 over the whole frame would double-count them
    // — on a frame that clips 1.3%, the 99th percentile lands INSIDE the sun and the
    // number stops being about the rest of the picture. Split this way the two invariants
    // are disjoint: `frameClipped` says how much of the frame may sit at the ceiling,
    // `frameWhitePoint` says where the top of everything else sits. (It does not rescue
    // this build: measured, the unclipped p99 is 0.9725 against the same 0.94.)
    const nUnclipped = n - clip;
    let c99 = 0;
    if (nUnclipped >= n * 0.5) {
      const want = 0.99 * nUnclipped;
      let c = 0;
      c99 = clipCode - 1;
      for (let i = 0; i < clipCode; i++) { c += hL[i]; if (c >= want) { c99 = i; break; } }
    } else {
      // More than half the frame past 0.98 is not a frame with a sun in it, it is a blown
      // frame, and excluding "the highlights" would exclude the subject. Report the raw
      // percentile so the number stays a statement about what was drawn.
      c99 = pct(hL, 0.99);
    }
    const p99 = c99 / 255;

    // Occupancy over the frame's OWN range. Walking the 256-bin histogram rather than
    // re-scanning the buffer keeps this O(256). The bin count is capped by the span in
    // CODE values so no bin is narrower than one code — at a 13-code span, 32 bins would
    // be mostly empty by construction and the metric would report degeneracy that is
    // really quantisation.
    let occupancy = 0;
    const cHi = pct(hL, 0.99);
    if ((cHi - c1) / 255 >= FRAME_SPAN_MIN) {
      const span = cHi - c1;
      const bins = Math.max(4, Math.min(FRAME_OCC_BINS, span));
      const need = FRAME_OCC_SHARE * n;
      let filled = 0;
      for (let k = 0; k < bins; k++) {
        const a = c1 + Math.floor((k * span) / bins);
        const bEnd = k === bins - 1 ? cHi : c1 + Math.floor(((k + 1) * span) / bins) - 1;
        let c = 0;
        for (let i = a; i <= bEnd; i++) c += hL[i];
        if (c >= need) filled++;
      }
      occupancy = filled / bins;
    }

    // The frame's darkest 1% per channel — reported, not budgeted. See the doc above.
    const fr = pct(hR, 0.01), fg = pct(hG, 0.01), fb = pct(hB, 0.01);
    const lab = linearToOklab(srgbToLinear(fr), srgbToLinear(fg), srgbToLinear(fb));
    let hue = Math.atan2(lab[2], lab[1]) * 180 / Math.PI;
    if (hue < 0) hue += 360;

    const pedestalLuma = gradePedestal();
    const costMs = performance.now() - t0;
    const st: FrameStats = {
      pedestalLuma: +pedestalLuma.toFixed(4),
      pedestalRGB: [_pedestal[0], _pedestal[1], _pedestal[2]],
      ok: true, width: W, height: H, pixels: n, tier: this.tier,
      p1: +p1.toFixed(4), p50: +p50.toFixed(4), p99: +p99.toFixed(4),
      clipped: +clipped.toFixed(5), occupancy: +occupancy.toFixed(4),
      floorRGB: [fr, fg, fb],
      floorC: +Math.hypot(lab[1], lab[2]).toFixed(4), floorHue: +hue.toFixed(0),
      costMs: +costMs.toFixed(2), flushMs: +flushMs.toFixed(2),
    };
    this.lastFrame = st;
    this.frameFails = 0;

    // Publish. `observeWhere` allocates in the caller, which is correct here and only here:
    // this runs one frame in FRAME_PERIOD_S, and a black point with no label attached is a
    // number a critic cannot act on.
    const label = `${W}x${H} tier ${this.tier} p1 ${p1.toFixed(3)} p50 ${p50.toFixed(3)} ` +
      `p99 ${p99.toFixed(3)} clip ${(clipped * 100).toFixed(2)}% occ ${occupancy.toFixed(2)} ` +
      `floor ${fr}/${fg}/${fb} vs pedestal ${_pedestal[0]}/${_pedestal[1]}/${_pedestal[2]}`;
    INV_FRAME_BLACK.observeWhere(p1 - FRAME_BP_MID,
      `p1 ${p1.toFixed(4)} vs band ${GradeTargets.blackPointP1[0]}-${GradeTargets.blackPointP1[1]}, ` +
      `${fr - _pedestal[0] > 6 ? 'FRAME has no black (floor is above the grade\'s pedestal)'
        : 'frame reaches the grade\'s own floor'} — ${label}`);
    INV_FRAME_WHITE.observeWhere(p99, label);
    INV_FRAME_CLIP.observeWhere(clipped, label);
    INV_FRAME_OCC.observeWhere(occupancy, label);
    INV_FRAME_MS.observeWhere(costMs, `${W}x${H}, flush ${flushMs.toFixed(1)} ms excluded`);
    return st;
  }

  /** The last frame measurement, for a probe. Null until one has run. */
  lastFrameStats(): FrameStats | null { return this.lastFrame; }

  /**
   * Re-arm the frame instrument at a scenario boundary.
   *
   * docs/AGENTS.md, wave-4 instrument failure 2: any state a debug path can leave behind
   * must be in `capture.mjs`'s per-scenario reset in the same edit. This instrument has
   * two such pieces of state — a retirement counter that a transient context loss could
   * latch for the rest of the page session, and a phase that would otherwise let a short
   * scenario go entirely unmeasured because the previous one happened to consume the
   * timer. The offset start is deliberate: `terrain.sepaudit` reads the same canvas on a
   * 6 s period, and two full-frame readbacks landing on one frame is a hitch neither
   * instrument would own.
   */
  resetFrameAudit(): void {
    this.frameFails = 0;
    this.frameTimer = 1.5;
    this.lastFrame = null;
  }

  /**
   * Read the resolved occlusion buffer back and reduce it to a distribution.
   *
   * "The wide ring is inert" is a claim about a distribution, and the fastest way to spend
   * a round is to argue it from a debug view that has been through an upsample and a tone
   * curve. This reads the half-res buffer the composite actually consumes — R wide, G
   * contact, B/A the texel's own depth — and reports, over pixels that hit geometry:
   * median, p90 and p99 occlusion, and the fraction darkened by more than 5% and 15%.
   * Those last two are exactly the numbers the wave-3 review quoted.
   *
   * Development only. A full GPU->CPU readback stalls the pipeline hard.
   */
  probeAo(): unknown {
    const rt = this.aoBlurRT;
    if (!rt || TIER_SPEC[this.tier].dirs === 0) return { ok: false, reason: 'ao off' };
    const n = rt.width * rt.height;
    const buf = new Uint8Array(n * 4);
    this.renderer.readRenderTargetPixels(rt, 0, 0, rt.width, rt.height, buf);
    const wide: number[] = [];
    const contact: number[] = [];
    for (let i = 0; i < n; i++) {
      const k = (buf[i * 4 + 2] + buf[i * 4 + 3] / 255) / 255;
      const z = k * k * this.probeFar;
      // Sky and the far fade carry no occlusion by construction; including them would
      // report the fraction of the frame that is sky, not the strength of the term.
      if (z >= this.cullWide * 0.999) continue;
      wide.push(1 - buf[i * 4] / 255);
      contact.push(1 - buf[i * 4 + 1] / 255);
    }
    const stats = (a: number[]) => {
      if (!a.length) return null;
      a.sort((x, y) => x - y);
      const q = (p: number) => +a[Math.min(a.length - 1, Math.floor(p * a.length))].toFixed(4);
      const frac = (t: number) => {
        let c = 0;
        for (const v of a) if (v > t) c++;
        return +(c / a.length).toFixed(4);
      };
      return { median: q(0.5), p90: q(0.9), p99: q(0.99), max: q(0.9999),
               over05: frac(0.05), over15: frac(0.15) };
    };
    return {
      ok: true, tier: this.tier, width: rt.width, height: rt.height,
      geomPixels: wide.length, geomFrac: +(wide.length / n).toFixed(3),
      wide: stats(wide), contact: stats(contact),
    };
  }

  /**
   * The chain's fill for the last frame it drew, in units of one full-resolution pass,
   * with the per-target breakdown a critic needs to see where it went.
   *
   * Structural, not timed: it is the same number on every device, which is the point.
   */
  lastFillPasses(): unknown {
    const w = Math.max(1, this.width), h = Math.max(1, this.height);
    const px = (rt: THREE.WebGLRenderTarget | null): number =>
      (rt ? (rt.width * rt.height) / (w * h) : 0);
    const tier = TIER_SPEC[this.tier];
    // `total` is MEASURED — it comes out of Quad.draw and cannot drift from what was
    // drawn. `parts` is a breakdown reconstructed from the tier flags, so it is a mirror
    // and is labelled as one: believe `total`, and use `parts` to see where it went.
    const ao = tier.dirs > 0, wide = ao && tier.wdirs > 0 && this.wideEnabled;
    return {
      tier: this.tier,
      // No `chainRepeat` divisor: `chainPasses` zeroes the counter on entry, so after the
      // repeat loop `fillPx` already holds ONE chain's worth. Dividing again would have
      // reported a tenth of the truth to any probe that had `setRepeat(10)` on, which is
      // every row of the cost bench.
      total: +(this.quad.fillPx / (w * h)).toFixed(4),
      budget: INV_FILL.budget,
      parts: {
        aoContact: ao ? +px(this.aoRT).toFixed(4) : 0,
        aoCoherence: wide ? +px(this.aoCohRT).toFixed(4) : 0,
        aoWide: wide ? +px(this.aoWideRT).toFixed(4) : 0,
        aoWideBlur: wide ? +px(this.aoWideBlurRT).toFixed(4) : 0,
        aoResolve: ao ? +px(this.aoBlurRT).toFixed(4) : 0,
        bloomBright: tier.bloom > 0 ? +px(this.bloomA).toFixed(4) : 0,
        bloomBlurTight: tier.bloom > 0 ? +(2 * px(this.bloomB)).toFixed(4) : 0,
        bloomBlurWide: tier.bloom > 1 ? +(2 * px(this.bloomC)).toFixed(4) : 0,
        composite: 1,
      },
      drawingBuffer: [w, h],
    };
  }

  dispose(): void {
    this.disposeTargets();
    this.warmRT?.dispose();
    this.warmRT = null;
    this.quad.dispose();
    for (const m of [this.aoMat, this.aoWideMat, this.aoWideBlurMat, this.aoBlurMat,
                     this.brightMat, this.blurMat, this.compMat]) {
      m?.dispose();
    }
    if (this.renderer) this.renderer.info.autoReset = true;
  }
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a || 1e-6)));
  return t * t * (3 - 2 * t);
}

/** 8-bit sRGB code to linear. The inverse of the composite's own `srgb()` encode, so a
 *  colour read back off the canvas can be taken into OKLab without going through the
 *  approximation `pow(x, 2.2)` that would put the frame's darkest codes in the wrong place
 *  — the linear segment below code 10 is exactly where this instrument works. */
function srgbToLinear(code: number): number {
  const c = code / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

// ---------------------------------------------------------------------------

const fx = new PostFX();

// The occlusion masks are positioned against the shared light block (see `lightRef`), so
// something has to keep that block in step with the sky. `installReliefSystem` is
// idempotent and exists for exactly this — if another piece has already called it, this
// is free; if none has, the block would otherwise sit on its documented midday fallback
// and the masks would stop tracking the clock, which is the failure this round closed.
installReliefSystem();

registerSystem({
  id: 'post',
  order: 110,
  init(ctx: SystemContext) {
    const ok = fx.init(ctx.renderer);
    renderHook.taken = ok;
    // Small debug surface: lets a capture script hold a tier steady while it measures,
    // which is the only honest way to price the chain.
    (window as unknown as Record<string, unknown>).__gripPost = {
      fx,
      tuning: PostTuning,
      setTier: (t: number) => {
        if (t < 0) { renderHook.taken = false; return; }
        renderHook.taken = !fx.failed;
        fx.setTier(t, true);
      },
      getTier: () => fx.tier,
      setDebug: (v: number) => fx.setDebugView(v),
      /** Lets a scenario latch a debug view without a per-frame shader rebuild, and
       *  without parking a latch on `window` that the harness's boundary reset does not
       *  know to clear. */
      getDebug: () => fx.debugView,
      setRawAo: (b: boolean) => fx.setRawAo(!!b),
      /** Set PostTuning.mbTaps and rebuild the composite's define set. The tap ladder is a
       *  #define, so a probe that only mutates `tuning` cannot move it. */
      setMbTaps: (n: number) => { PostTuning.mbTaps = n | 0; fx.setDebugView(fx.debugView); },
      /** Drop the wide occlusion ring's two passes. Development only — this is how the
       *  pass is priced by A/B inside one page session. */
      setWide: (b: boolean) => fx.setWide(!!b),
      /**
       * Read the resolved occlusion buffer back and reduce it to the numbers a critic
       * actually quotes: how much of the frame the wide ring darkens, and by how much.
       *
       * Development only, and slow (a full GPU->CPU readback). It exists because "the
       * wide ring is inert" is a claim about a distribution, and the only honest way to
       * settle it is to read the distribution rather than to squint at a debug view that
       * has already been through a tone curve.
       */
      probeAo: () => fx.probeAo(),
      /**
       * The light-referred mask windows this frame, and the rig they were derived from.
       *
       * The claim this round has to be able to settle is "the occlusion means the same
       * thing at every hour", and that is a statement about these four numbers moving with
       * the sun. Reading them straight out of the shipping path is the only way to check
       * it that cannot be argued with.
       */
      lightRef: () => fx.lightRef(),
      /**
       * The neutral axis, through this file's own display transform, at whatever
       * `PostTuning` currently holds.
       *
       * This is the grade's instrument and it is exposed so it can be FAILED rather than
       * believed: a probe sets `tuning.flareColor` back to the pre-adoption `[0.42, 0.52,
       * 0.76]`, calls this, and must get the old C 0.0153 back. An instrument that
       * returns the same answer for both grades cannot be watching the grade.
       */
      gradeNeutral: () => NEUTRAL_LEVELS.map((s) => {
        const n = gradeNeutral(s);
        return { scene: s, L: +n.L.toFixed(4), C: +n.C.toFixed(4), h: +n.h.toFixed(1) };
      }),
      /** Worst deviation of any shipping grade constant from the palette authority. */
      gradeDrift: () => ({ ...gradeDrift() }),
      /**
       * MEASURE THE FINISHED FRAME NOW, and return what the grade did to it.
       *
       * The instrument behind `postfx.frameBlackPoint` / `frameWhitePoint` / `frameClipped`
       * / `frameToneOccupancy`, forced rather than waited for. Exposed so it can be FAILED
       * rather than believed: a probe pins one frame, reads this, mutates `tuning` into a
       * grade known to be wrong (a flat black point, a doubled exposure, the pre-adoption
       * blue flare) and reads it again. An instrument that returns the same numbers for two
       * visibly different frames is not watching the frame.
       */
      frameStats: () => fx.measureFrame(),
      /** The last measurement the shipping path took, without forcing a new one. */
      lastFrameStats: () => fx.lastFrameStats(),
      /**
       * The chain's fill for the last frame, in full-resolution passes.
       *
       * The number the integrated-GPU question is actually about, and the only part of the
       * cost that is measurable from this machine without believing anything. Read
       * straight out of the shipping path — `Quad.draw` counts every pass in this file.
       */
      fillPasses: () => fx.lastFillPasses(),
      /**
       * The tier governor's own state — the measured median frame period it is steering
       * by, the ceiling, the mode, and how many step downs it kept versus threw out.
       *
       * Exposed because the defect this round closed was invisible: the chain was
       * collapsing to tier 0 in 3.6 s on every 30 Hz capture path and the only trace was
       * that the pictures looked flat. Nothing reported the signal, the ceiling, or the
       * fact that the ceiling could not go back up. `postfx.governorTierLoss` puts the
       * headline in SUMMARY.md; this is the rest of it, for a probe.
       */
      governor: () => fx.governorState(),
      /**
       * Put the chain back to its shipping state.
       *
       * The capture harness shares one page across every scenario, so a debug view or a
       * pinned tier set by one scenario is still set for the next one. It calls this at
       * each scenario boundary; without it, `aodebug` left DEBUG_VIEW=1 on and every
       * later scenario in an --all run screenshotted the raw occlusion buffer.
       */
      reset: () => {
        fx.setWide(true);
        fx.setRawAo(false);
        fx.setDebugView(0);
        fx.setRepeat(1);
        // The tap override is a #define, so a scenario that left it set would silently
        // change the composite for every scenario after it — the same class of leak that
        // left DEBUG_VIEW=1 on for a whole --all run.
        PostTuning.mbTaps = -1;
        fx.unlockTier();
        fx.resetFrameAudit();
        renderHook.taken = !fx.failed;
      },
      rebuild: () => fx.rebuild(),
      /** Rolling GPU ms for the post chain alone, or -1 if unmeasurable here.
       *
       *  Read the doc on `setRepeat` before quoting this: the extension is present in the
       *  harness and the query resolves, and the number it returns still could not rank a
       *  4x fill-rate change on this machine. `setRepeat` + a `gl.finish()` slope is the
       *  instrument that survived validation; this one is reported beside it as a control. */
      gpuMs: () => fx.timer.ms,
      timerStats: () => ({
        available: fx.timer.available, ms: fx.timer.ms, lastMs: fx.timer.lastMs,
        count: fx.timer.count, stats: fx.timer.stats.slice(),
      }),
      resetTimer: () => fx.timer.reset(),
      /** Run the chain n times a frame. Development only — the GPU price is a slope in n,
       *  because every constant cost in the frame drops out of a slope and does not drop
       *  out of a difference. See PostFX.setRepeat. */
      setRepeat: (n: number) => fx.setRepeat(n),
    };
  },
  update(dt: number, ctx: SystemContext) {
    // We own the reset now (see PostFX.init), so it has to happen even on a frame where
    // the chain is bypassed — otherwise renderer.info accumulates without bound.
    ctx.renderer.info.reset();
    if (!renderHook.taken || fx.failed) {
      // The world draw is main.ts's again, so it is no longer part of this system's cost.
      // Leaving the last measured value here would misreport a bypassed chain as expensive.
      systemTimings['post.scene'] = 0;
      return;
    }
    try {
      fx.render(ctx, dt);
    } catch (e) {
      console.warn('[post] chain failed mid-frame, releasing the render hook', e);
      fx.failed = true;
      renderHook.taken = false;
      ctx.renderer.setRenderTarget(null);
    }
  },
  dispose() {
    fx.dispose();
  },
});

export {};
